// 🔵 DEBUG MARKER: roomSyncService.ts loaded - VERSION 2025-01-25-12:33
console.log('🔵 [CODE VERSION] roomSyncService.ts LOADED - NEW CODE 2025-01-25-12:33');

import type { Room, DoorElement, WindowElement, RadiatorElement, Wall, UtilityElement, FloorPlanElement, BoilerElement, LightElement, Point } from '../types';
import type { SiteNotesData } from '../utils/fileUtils';
import { 
  findRoomAtPoint, 
  calculateRooms, 
  RoomCalculationContext,
  getRoomBounds,
  getRoomCenter,
  isPointInsideRoom,
  pointToLineDistance
} from '../utils/floorPlanUtils';
import { findRoomContainingRadiatorText } from '../utils/radiatorPlacement';
import { PIXELS_PER_METER } from '../constants/floorPlanConstants';
import { debounce } from 'lodash';

/**
 * Maps floor plan orientation format (N, E, S, W, NE, etc.) to site notes format (north, east, south, west)
 */
const mapFloorPlanOrientationToSiteNotes = (floorPlanOrientation: string): string => {
  const orientationMap: Record<string, string> = {
    'N': 'north',
    'NE': 'northeast',
    'E': 'east',
    'SE': 'southeast',
    'S': 'south',
    'SW': 'southwest',
    'W': 'west',
    'NW': 'northwest',
    // Also handle full word formats in case they're used
    'North': 'north',
    'Northeast': 'northeast',
    'East': 'east',
    'Southeast': 'southeast',
    'South': 'south',
    'Southwest': 'southwest',
    'West': 'west',
    'Northwest': 'northwest'
  };
  
  return orientationMap[floorPlanOrientation] || '';
};

// Global sync state trackers to prevent conflicts and duplicates
const lightSyncState = {
  isInProgress: false,
  lastSyncTimestamp: 0,
  processedLightIds: new Set<string>()
};

// Helper functions for enhanced wall matching
function extractWallCoordinates(wallId: string): { x1: number; y1: number; x2: number; y2: number } | null {
  if (!wallId) return null;
  
  // Extract coordinates from wall ID formats like "wall_-280.00_799.15_-147.73_799.15"
  const coordMatch = wallId.match(/wall_(?:\d+_)?([\-\d\.]+)_([\-\d\.]+)_([\-\d\.]+)_([\-\d\.]+)/);
  if (coordMatch) {
    const coords = coordMatch.slice(1, 5).map(Number);
    if (coords.every(c => !isNaN(c))) {
      return { x1: coords[0], y1: coords[1], x2: coords[2], y2: coords[3] };
    }
  }
  return null;
}

function areWallsOnSameLine(wall1: { x1: number; y1: number; x2: number; y2: number }, wall2: { x1: number; y1: number; x2: number; y2: number }): boolean {
  const tolerance = 5.0; // Pixel tolerance for alignment
  
  // Check if walls share the same Y coordinate (horizontal walls)
  if (Math.abs(wall1.y1 - wall1.y2) < tolerance && Math.abs(wall2.y1 - wall2.y2) < tolerance) {
    return Math.abs(wall1.y1 - wall2.y1) < tolerance;
  }
  
  // Check if walls share the same X coordinate (vertical walls)  
  if (Math.abs(wall1.x1 - wall1.x2) < tolerance && Math.abs(wall2.x1 - wall2.x2) < tolerance) {
    return Math.abs(wall1.x1 - wall2.x1) < tolerance;
  }
  
  return false;
}

// Global state for managing sync operations
let windowSyncState = {
  isInProgress: false,
  lastSyncTimestamp: 0,
  processedWindowIds: new Set<string>(),
  isManualSync: false
};

const radiatorSyncState = {
  isInProgress: false,
  lastSyncTimestamp: 0,
  processedRadiatorIds: new Set<string>(),
  isManualSync: false,
  // 🧹 DEDUP PROTECTION: Track synced rooms to prevent multiple sync runs
  syncedRooms: new Map<string, number>() // roomName -> timestamp
};

/**
 * Debug function to clear window sync state - useful when windows aren't syncing
 */
export const clearWindowSyncState = (): void => {
  windowSyncState.processedWindowIds.clear();
  windowSyncState.isInProgress = false;
  windowSyncState.isManualSync = false;
  console.log('🧹 Cleared window sync state - all windows can now be reprocessed');
};

/**
 * Debug function to get current sync state information
 */
export const getWindowSyncDebugInfo = (): {
  processedWindowCount: number;
  processedWindowIds: string[];
  isInProgress: boolean;
  lastSyncTimestamp: number;
} => {
  return {
    processedWindowCount: windowSyncState.processedWindowIds.size,
    processedWindowIds: Array.from(windowSyncState.processedWindowIds),
    isInProgress: windowSyncState.isInProgress,
    lastSyncTimestamp: windowSyncState.lastSyncTimestamp
  };
};

// PERFORMANCE OPTIMIZATION: Memoized room calculation for Room Sync Service
// This prevents expensive recalculation of the same rooms with identical parameters
const memoizedCalculateRoomsCache = new Map();

const memoizedCalculateRooms = (
  walls: Wall[], 
  roomCustomLabels: Record<string, string>, 
  context: RoomCalculationContext = 'labelPreservation',
  currentFloor?: number
): Room[] => {
  // Create cache key from walls and room labels
  const cacheKey = JSON.stringify({ 
    walls: walls.map(w => ({ 
      id: w.tempId || w.id, 
      start: w.start, 
      end: w.end, 
      type: w.type 
    })), 
    roomCustomLabels, 
    context,
    currentFloor 
  });
  
  if (memoizedCalculateRoomsCache.has(cacheKey)) {
    return memoizedCalculateRoomsCache.get(cacheKey);
  }
  
  const rooms = calculateRooms(walls, roomCustomLabels, context, currentFloor);
  memoizedCalculateRoomsCache.set(cacheKey, rooms);
  
  // Limit cache size to prevent memory issues
  if (memoizedCalculateRoomsCache.size > 50) {
    const firstKey = memoizedCalculateRoomsCache.keys().next().value;
    memoizedCalculateRoomsCache.delete(firstKey);
  }
  
  return rooms;
};

// Extract types from the existing SiteNotesData interface
export type SurveyorRoom = SiteNotesData['floors'][0]['rooms'][0];
export type SurveyorFloor = SiteNotesData['floors'][0];

/**
 * Converts floor plan pixels to meters
 * Uses the same scale factor as the main floor plan (100 pixels = 1 meter)
 */
const pixelsToMeters = (pixels: number): number => {
  return Math.round((pixels / PIXELS_PER_METER) * 100) / 100; // Round to 2 decimal places
};

/**
 * Calculates room dimensions from walls using actual wall segment lengths
 * This matches the canvas display method for accurate dimensions
 */
const calculateRoomDimensions = (room: Room): { length: number; width: number } => {
  if (!room.walls || room.walls.length === 0) {
    return { length: 0, width: 0 };
  }

  // Calculate actual wall lengths (matches canvas calculation)
  const wallLengths = room.walls.map(wall => {
    const dx = wall.end.x - wall.start.x;
    const dy = wall.end.y - wall.start.y;
    return Math.sqrt(dx * dx + dy * dy) / PIXELS_PER_METER;
  });

  // Sort to find longest and shortest walls
  wallLengths.sort((a, b) => b - a);

  // For rectangular rooms, we typically have pairs of walls
  // Length is the longest wall, width is typically the second or third longest
  // (depending on if it's a perfect rectangle or has some variation)
  const length = wallLengths[0] || 0;
  
  // Find the width by looking for walls that are significantly different from length
  // This handles both perfect rectangles and slightly irregular shapes
  let width = 0;
  for (let i = 1; i < wallLengths.length; i++) {
    // Look for a wall that's at least 10% different in length
    if (Math.abs(wallLengths[i] - length) / length > 0.1) {
      width = wallLengths[i];
      break;
    }
  }
  
  // If no significantly different wall found, use the second longest
  if (width === 0 && wallLengths.length > 1) {
    width = wallLengths[1];
  }

  return {
    length: Math.round(length * 100) / 100,   // Round to 2 decimal places
    width: Math.round(width * 100) / 100      // Round to 2 decimal places
  };
};

/**
 * No longer automatically sets room height - heights must be manually entered in site notes
 * This function is kept for backward compatibility but returns empty string
 */
const getDefaultRoomHeight = (floorLevel: string): string => {
  // Room heights must be manually entered in site notes dimensions page
  // No automatic height calculation
  return '';
};

/**
 * Finds or creates a room in the surveyor data that corresponds to a floor plan room
 */
const findOrCreateSurveyorRoom = (
  floorPlanRoom: Room,
  surveyorFloor: SurveyorFloor,
  roomNameMap: Record<string, number> // Maps room names to surveyor room IDs
): SurveyorRoom => {
  // First, try to find by room name map
  const mappedRoomId = roomNameMap[floorPlanRoom.name];
  if (mappedRoomId) {
    const existingRoom = surveyorFloor.rooms.find(r => r.id === mappedRoomId);
    if (existingRoom) {
      return existingRoom;
    }
  }

  // Second, try to find by room name directly (fallback)
  const existingRoomByName = surveyorFloor.rooms.find(r => r.name === floorPlanRoom.name);
  if (existingRoomByName) {
    // Update the room name map for future reference
    roomNameMap[floorPlanRoom.name] = existingRoomByName.id;
    
    // CRITICAL FIX: Calculate dimensions from bounding box and update existing room
    const PIXELS_PER_METER = 100;
    const roomWithPoints = floorPlanRoom as any;
    
    let calculatedLength = 0;
    let calculatedWidth = 0;
    
    if (roomWithPoints.points && Array.isArray(roomWithPoints.points) && roomWithPoints.points.length > 0) {
      const xs = roomWithPoints.points.map((p: any) => p.x);
      const ys = roomWithPoints.points.map((p: any) => p.y);
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);
      
      const widthPixels = maxX - minX;
      const heightPixels = maxY - minY;
      calculatedWidth = Math.round((widthPixels / PIXELS_PER_METER) * 100) / 100;
      calculatedLength = Math.round((heightPixels / PIXELS_PER_METER) * 100) / 100;
    } else if (roomWithPoints.walls && Array.isArray(roomWithPoints.walls) && roomWithPoints.walls.length > 0) {
      // Fallback: Calculate from walls using getRoomBounds
      const bounds = getRoomBounds(roomWithPoints.walls);
      const widthPixels = bounds.maxX - bounds.minX;
      const heightPixels = bounds.maxY - bounds.minY;
      calculatedWidth = Math.round((widthPixels / PIXELS_PER_METER) * 100) / 100;
      calculatedLength = Math.round((heightPixels / PIXELS_PER_METER) * 100) / 100;
    }
    
    // Always update with latest floor plan dimensions
    if (calculatedLength > 0 && calculatedWidth > 0) {
      const previousLength = existingRoomByName.length;
      const previousWidth = existingRoomByName.width;
      
      existingRoomByName.length = calculatedLength.toFixed(2);
      existingRoomByName.width = calculatedWidth.toFixed(2);
      
      console.log(`📐 [roomSyncService] Updated existing room "${floorPlanRoom.name}" with dimensions:`, {
        length: calculatedLength.toFixed(2),
        width: calculatedWidth.toFixed(2),
        area: roomWithPoints.area || 0,
        pointsCount: roomWithPoints.points?.length || 0,
        previousLength,
        previousWidth,
        changed: previousLength !== calculatedLength.toFixed(2) || previousWidth !== calculatedWidth.toFixed(2)
      });
    }
    
    return existingRoomByName;
  }

  // Create a new room if none found
  const newRoomId = Math.max(0, ...surveyorFloor.rooms.map(r => r.id)) + 1;
  
  // CRITICAL FIX: Calculate dimensions from bounding box of room points
  // Room objects from calculateRooms() have: id, name, points (array), area
  // Note: TypeScript Room type is incomplete, points exists at runtime
  const PIXELS_PER_METER = 100; // Match floor plan constant
  
  let calculatedLength = 0;
  let calculatedWidth = 0;
  
  const roomWithPoints = floorPlanRoom as any; // Access runtime points property
  
  if (roomWithPoints.points && Array.isArray(roomWithPoints.points) && roomWithPoints.points.length > 0) {
    // Calculate bounding box from points
    const xs = roomWithPoints.points.map((p: any) => p.x);
    const ys = roomWithPoints.points.map((p: any) => p.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    
    // Convert pixels to meters and round to 2 decimal places
    const widthPixels = maxX - minX;
    const heightPixels = maxY - minY;
    calculatedWidth = Math.round((widthPixels / PIXELS_PER_METER) * 100) / 100;
    calculatedLength = Math.round((heightPixels / PIXELS_PER_METER) * 100) / 100;
  } else if (roomWithPoints.walls && Array.isArray(roomWithPoints.walls) && roomWithPoints.walls.length > 0) {
    // Fallback: Calculate from walls using getRoomBounds
    const bounds = getRoomBounds(roomWithPoints.walls);
    const widthPixels = bounds.maxX - bounds.minX;
    const heightPixels = bounds.maxY - bounds.minY;
    calculatedWidth = Math.round((widthPixels / PIXELS_PER_METER) * 100) / 100;
    calculatedLength = Math.round((heightPixels / PIXELS_PER_METER) * 100) / 100;
  }
  
  console.log(`📐 [roomSyncService] Creating room "${floorPlanRoom.name}":`, {
    length: calculatedLength.toFixed(2),
    width: calculatedWidth.toFixed(2),
    area: (roomWithPoints.area || 0).toFixed(2)
  });
  
  const newRoom: SurveyorRoom = {
    id: newRoomId,
    name: floorPlanRoom.name, // Set the room name from floor plan
    length: calculatedLength > 0 ? calculatedLength.toFixed(2) : '',  // Use calculated dimension
    width: calculatedWidth > 0 ? calculatedWidth.toFixed(2) : '',     // Use calculated dimension
    height: '',
    lowEnergyLights: '0',
    nonLowEnergyLights: '0',
    radiatorWidth: '',
    radiatorHeight: '',
    radiatorType: '',
    windowWidth: '',
    windowHeight: '',
    
    windowTrickleVents: '',
    windows: []
  };

  surveyorFloor.rooms.push(newRoom);
  roomNameMap[floorPlanRoom.name] = newRoomId;
  
  return newRoom;
};

/**
 * Syncs a single room from floor plan to surveyor site details
 */
export const syncRoomToSurveyorData = (
  floorPlanRoom: Room,
  siteNotesData: SiteNotesData,
  floorNumber: number = 1,
  roomNameMap: Record<string, number> = {}
): SiteNotesData => {
  // Basic validation - rooms must have valid ID and name
  const hasValidId = floorPlanRoom.id && floorPlanRoom.id.trim() !== '';
  const hasValidName = floorPlanRoom.name && floorPlanRoom.name.trim() !== '';
  
  if (!hasValidId || !hasValidName) {
    console.warn(`🚫 Refusing to sync invalid room - ID: "${floorPlanRoom.id}", Name: "${floorPlanRoom.name}"`);
    return siteNotesData; // Return unchanged data
  }

  // Clone the site notes data to avoid mutations
  const updatedSiteNotesData = JSON.parse(JSON.stringify(siteNotesData));

  // Find or create the appropriate floor
  let targetFloor = updatedSiteNotesData.floors.find((f: SurveyorFloor) => f.id === floorNumber);
  
  if (!targetFloor) {
    // Create a new floor if it doesn't exist
    const floorLevels = ['basement', 'ground floor', 'first floor', 'second floor', 'third floor', 'fourth floor', 'fifth floor'];
    const floorLevel = floorLevels[Math.min(floorNumber, floorLevels.length - 1)] || 'ground floor';
    
    targetFloor = {
      id: floorNumber,
      floorLevel,
      buildingType: 'main building',
      height: '',
      floorArea: '',
      hlp: '',
      pw: '',
      rooms: []
    };
    updatedSiteNotesData.floors.push(targetFloor);
  }

  // Find or create the room in surveyor data
  const surveyorRoom = findOrCreateSurveyorRoom(floorPlanRoom, targetFloor, roomNameMap);

  // Calculate room dimensions
  const { length, width } = calculateRoomDimensions(floorPlanRoom);
  const height = getDefaultRoomHeight(targetFloor.floorLevel); // Returns empty string - no automatic height
  
  // Use the POLYGON AREA from the floor plan (accurate), not bounding box area
  const polygonAreaInMeters = floorPlanRoom.area / (PIXELS_PER_METER * PIXELS_PER_METER);

  // Update the surveyor room with floor plan data
  surveyorRoom.name = floorPlanRoom.name;
  surveyorRoom.length = length.toString();
  surveyorRoom.width = width.toString();
  surveyorRoom.area = Math.round(polygonAreaInMeters * 10000) / 10000; // Round to 4 decimal places
  
  // Heights must be manually entered in site notes - no automatic setting
  // Only set height if it's currently empty AND we have no default height (which is now always empty)
  if (!surveyorRoom.height || surveyorRoom.height === '') {
    surveyorRoom.height = height; // Will be empty string - user must enter manually in site notes
  }

  return updatedSiteNotesData;
};

/**
 * Syncs multiple rooms from floor plan to surveyor site details
 */
export const syncAllRoomsToSurveyorData = (
  floorPlanRooms: Room[],
  siteNotesData: SiteNotesData,
  floorNumber: number = 1,
  roomCustomLabels: Record<string, string> = {},
  skipCleanup: boolean = false
): SiteNotesData => {
  console.log(`🚀 [syncAllRoomsToSurveyorData] CALLED for floor ${floorNumber} with ${floorPlanRooms.length} rooms`);
  
  // Clone the site notes data to avoid mutations
  let updatedSiteNotesData = JSON.parse(JSON.stringify(siteNotesData));

  // Build room name map for existing rooms to prevent ID conflicts
  const roomNameMap: Record<string, number> = {};

  // Filter rooms to only include valid ones for syncing
  const validRooms = floorPlanRooms.filter(room => {
    // Check for valid ID (either id or stableId is sufficient)
    const hasValidId = (room.id && room.id.trim() !== '') || (room.stableId && room.stableId.trim() !== '');
    const hasValidName = room.name && room.name.trim() !== '';
    
    // Improved auto-generated name detection - be more specific about what's considered auto-generated
    const isAutoGeneratedName = /^(Room \d+|room \d+|Room\d+|Unnamed Room \d*|Untitled \d*)$/i.test(room.name || '');
    
    // Check if room has a custom label
    const hasCustomLabel = room.stableId && roomCustomLabels && roomCustomLabels[room.stableId] && roomCustomLabels[room.stableId].trim() !== '';
    
    // Enhanced validation: allow rooms with custom labels even if they have auto-generated display names
    // Also allow any room with a meaningful name (not auto-generated)
    const isValidForSync = hasValidId && hasValidName && (hasCustomLabel || !isAutoGeneratedName);
    
    if (!hasValidId || !hasValidName) {
      console.warn(`🚫 Skipping invalid room - ID: "${room.id}", Name: "${room.name}", StableID: "${room.stableId}"`);
      return false;
    }
    
    if (!isValidForSync) {
      console.warn(`🚫 Skipping auto-generated room - ID: "${room.id}", Name: "${room.name}", StableID: "${room.stableId}" (auto-generated names are not synced unless they have custom labels)`);
      return false;
    }
    
    return true;
  });
  
  console.log(`🔍 [ROOM FILTER] Result: ${validRooms.length}/${floorPlanRooms.length} rooms passed validation`);

  // Build existing room name map - ONLY for the current floor to prevent cross-floor duplication
  const targetFloor = updatedSiteNotesData.floors.find((f: SurveyorFloor) => f.id === floorNumber);
  if (targetFloor) {
    // Map existing rooms by name within this floor only
    // Use a floor-specific key to prevent cross-floor contamination
    targetFloor.rooms.forEach((room: SurveyorRoom) => {
      if (room.name) {
        const floorSpecificKey = `${room.name}_floor_${floorNumber}`;
        roomNameMap[floorSpecificKey] = room.id;
        // Also keep the original key for backward compatibility, but only for this floor
        roomNameMap[room.name] = room.id;
      }
    });
  }

  // Sync each valid room
  validRooms.forEach(room => {
    updatedSiteNotesData = syncRoomToSurveyorData(
      room,
      updatedSiteNotesData,
      floorNumber,
      roomNameMap
    );
  });

  // Clean up any duplicate rooms that might have been created during syncing (only if not skipped)
  if (!skipCleanup) {
    updatedSiteNotesData = cleanupDuplicateRooms(updatedSiteNotesData);
  }

  return updatedSiteNotesData;
};

/**
 * Syncs a room with custom height to surveyor site details
 */
export const syncRoomWithCustomHeight = (
  floorPlanRoom: Room,
  siteNotesData: SiteNotesData,
  customHeight: number,
  floorNumber: number = 1,
  roomNameMap: Record<string, number> = {}
): SiteNotesData => {
  // Clone the site notes data to avoid mutations
  const updatedSiteNotesData = JSON.parse(JSON.stringify(siteNotesData));

  // Find or create the appropriate floor
  let targetFloor = updatedSiteNotesData.floors.find((f: SurveyorFloor) => f.id === floorNumber);
  
  if (!targetFloor) {
    // Create a new floor if it doesn't exist
    const floorLevels = ['basement', 'ground floor', 'first floor', 'second floor', 'third floor', 'fourth floor', 'fifth floor'];
    const floorLevel = floorLevels[Math.min(floorNumber, floorLevels.length - 1)] || 'ground floor';
    
    targetFloor = {
      id: floorNumber,
      floorLevel,
      buildingType: 'main building',
      height: '',
      floorArea: '',
      hlp: '',
      pw: '',
      rooms: []
    };
    updatedSiteNotesData.floors.push(targetFloor);
  }

  // Find or create the room in surveyor data
  const surveyorRoom = findOrCreateSurveyorRoom(floorPlanRoom, targetFloor, roomNameMap);

  // Calculate room dimensions
  const { length, width } = calculateRoomDimensions(floorPlanRoom);
  
  // Use the POLYGON AREA from the floor plan (accurate), not bounding box area
  const polygonAreaInMeters = floorPlanRoom.area / (PIXELS_PER_METER * PIXELS_PER_METER);

  // Update the surveyor room with floor plan data and custom height
  surveyorRoom.name = floorPlanRoom.name; // Always update room name
  surveyorRoom.length = length.toString();
  surveyorRoom.width = width.toString();
  surveyorRoom.area = Math.round(polygonAreaInMeters * 10000) / 10000; // Round to 4 decimal places
  surveyorRoom.height = customHeight.toString();

  return updatedSiteNotesData;
};

/**
 * Clears all light counts for rooms on a specific floor
 */
export const clearFloorLightCounts = (
  siteNotesData: SiteNotesData,
  floorNumber: number = 1
): SiteNotesData => {
  // Clone the site notes data to avoid mutations
  const updatedSiteNotesData = JSON.parse(JSON.stringify(siteNotesData));

  // Find the target floor
  const targetFloor = updatedSiteNotesData.floors.find((f: SurveyorFloor) => f.id === floorNumber);
  if (!targetFloor) {
    console.warn(`Floor ${floorNumber} not found when clearing light counts`);
    return siteNotesData;
  }

  // Clear light counts for all rooms on this floor
  targetFloor.rooms.forEach((room: SurveyorRoom) => {
    room.lowEnergyLights = '0';
    room.nonLowEnergyLights = '0';
  });

  console.log(`🧹 Cleared light counts for all rooms on floor ${floorNumber}`);
  // Note: room_details clearing is handled by the subsequent sync in RetrofitFloorPlan
  return updatedSiteNotesData;
};

/**
 * Clears all light counts for all rooms across all floors
 */
export const clearAllLightCounts = (
  siteNotesData: SiteNotesData
): SiteNotesData => {
  // Clone the site notes data to avoid mutations
  const updatedSiteNotesData = JSON.parse(JSON.stringify(siteNotesData));

  // Clear light counts for all rooms on all floors
  updatedSiteNotesData.floors.forEach((floor: SurveyorFloor) => {
    floor.rooms.forEach((room: SurveyorRoom) => {
      room.lowEnergyLights = '0';
      room.nonLowEnergyLights = '0';
    });
  });

  // Also clear the totals in additional information
  updatedSiteNotesData.formData.totalLightsX = '0';
  updatedSiteNotesData.formData.totalLightsO = '0';

  console.log(`🧹 Cleared all light counts across all floors`);
  return updatedSiteNotesData;
};

/**
 * Updates light count for a specific room in surveyor data
 */
export const updateRoomLightCount = (
  roomName: string,
  lightType: 'low-energy' | 'non-low-energy',
  increment: number,
  siteNotesData: SiteNotesData,
  floorNumber: number = 1
): SiteNotesData => {
  // Clone the site notes data to avoid mutations
  const updatedSiteNotesData = JSON.parse(JSON.stringify(siteNotesData));

  // Find the target floor
  const targetFloor = updatedSiteNotesData.floors.find((f: SurveyorFloor) => f.id === floorNumber);
  if (!targetFloor) {
    console.warn(`Floor ${floorNumber} not found when updating light count for room "${roomName}"`);
    return siteNotesData;
  }

  // Debug available rooms in site notes data
  console.log(`💡 DEBUG: Floor ${floorNumber} has ${targetFloor.rooms.length} rooms:`, 
    targetFloor.rooms.map((r: SurveyorRoom) => `"${r.name}"`).join(', '));

  // Find the room by name
  const targetRoom = targetFloor.rooms.find((room: SurveyorRoom) => room.name === roomName);
  if (!targetRoom) {
    console.warn(`Room "${roomName}" not found when updating light count`);
    console.warn(`Available rooms on floor ${floorNumber}:`, targetFloor.rooms.map((r: SurveyorRoom) => r.name));
    return siteNotesData;
  }

  // Update the appropriate light count
  const currentCount = parseInt(
    lightType === 'low-energy' ? targetRoom.lowEnergyLights : targetRoom.nonLowEnergyLights
  ) || 0;
  
  const newCount = Math.max(0, currentCount + increment); // Ensure count doesn't go below 0

  if (lightType === 'low-energy') {
    targetRoom.lowEnergyLights = newCount.toString();
  } else {
    targetRoom.nonLowEnergyLights = newCount.toString();
  }

  console.log(`💡 Updated ${lightType} light count for room "${roomName}": ${currentCount} → ${newCount} (increment: ${increment})`);

  // Note: room_details sync is handled separately in RetrofitFloorPlan via onDataUpdate
  // because room_details is part of the retrofit survey data, not siteNotesData

  return updatedSiteNotesData;
};

/**
 * Smart light sync that prevents duplication and unnecessary clearing
 * This function tracks which lights have been processed to avoid double-counting
 */
/**
 * Ensures all floor plan rooms exist in site notes data before element sync
 */
const ensureRoomsExistInSiteNotes = (
  rooms: Room[],
  siteNotesData: SiteNotesData,
  floorNumber: number
): SiteNotesData => {
  let updatedData = JSON.parse(JSON.stringify(siteNotesData));
  
  // Find or create the target floor
  let targetFloor = updatedData.floors.find((f: SurveyorFloor) => f.id === floorNumber);
  if (!targetFloor) {
    targetFloor = {
      id: floorNumber,
      rooms: []
    };
    updatedData.floors.push(targetFloor);
  }

  const roomNameMap: Record<string, number> = {};
  
  // Build set of valid room names from current floor plan
  const validRoomNames = new Set<string>();
  rooms.forEach(room => {
    if (room.name && !room.name.match(/^Room \d+$/)) { // Skip auto-generated room names
      validRoomNames.add(room.name);
    }
  });
  
  // 🧹 CLEANUP: Remove rooms that exist in site notes but not in floor plan (orphaned/renamed)
  const roomsBeforeCleanup = targetFloor.rooms.length;
  targetFloor.rooms = targetFloor.rooms.filter((siteNotesRoom: SurveyorRoom) => {
    // Skip rooms without names
    if (!siteNotesRoom.name) {
      return false;
    }
    
    // Keep rooms that are in the current floor plan
    if (validRoomNames.has(siteNotesRoom.name)) {
      return true;
    }
    
    // Remove orphaned room
    console.log(`🧹 CLEANUP: Removing orphaned room "${siteNotesRoom.name}" from site notes (not in floor plan)`);
    return false;
  });
  
  const removedCount = roomsBeforeCleanup - targetFloor.rooms.length;
  if (removedCount > 0) {
    console.log(`🧹 CLEANUP: Removed ${removedCount} orphaned room(s) from floor ${floorNumber}`);
  }
  
  // Create all floor plan rooms in site notes data
  rooms.forEach(room => {
    if (room.name && !room.name.match(/^Room \d+$/)) { // Skip auto-generated room names
      const surveyorRoom = findOrCreateSurveyorRoom(room, targetFloor!, roomNameMap);
      console.log(`💡 ROOM_INIT: Ensured room "${room.name}" exists in site notes (ID: ${surveyorRoom.id})`);
    }
  });
  
  console.log(`💡 ROOM_INIT: Floor ${floorNumber} now has ${targetFloor.rooms.length} rooms in site notes data`);
  return updatedData;
};

export const smartSyncLightsToRooms = (
  lights: LightElement[],
  rooms: Room[],
  siteNotesData: SiteNotesData,
  floorNumber: number = 1,
  clearExisting: boolean = true
): SiteNotesData => {
  if (lights.length === 0) {
    console.log(`💡 No lights to sync on floor ${floorNumber}`);
    return siteNotesData;
  }

  // Check if another sync is in progress
  const currentTime = Date.now();
  if (lightSyncState.isInProgress && (currentTime - lightSyncState.lastSyncTimestamp) < 1000) {
    console.warn(`💡 Light sync already in progress, skipping duplicate sync for floor ${floorNumber}`);
    return siteNotesData;
  }

  // Mark sync as in progress
  lightSyncState.isInProgress = true;
  lightSyncState.lastSyncTimestamp = currentTime;

  // console.log(`💡 Smart syncing ${lights.length} lights on floor ${floorNumber} (clearExisting: ${clearExisting})`);
  
  // Ensure all floor plan rooms exist in site notes data before element sync
  let updatedSiteNotesData = ensureRoomsExistInSiteNotes(rooms, siteNotesData, floorNumber);
  
  // Always clear existing light counts to prevent inflation from multiple sync runs
  updatedSiteNotesData = clearFloorLightCounts(updatedSiteNotesData, floorNumber);
  console.log(`🧹 INFLATION FIX: Always clearing existing light counts for floor ${floorNumber} to prevent double-counting`);
  
  // Clear processed lights for this floor to enable fresh sync
  const floorLightIds = Array.from(lightSyncState.processedLightIds).filter(id => id.endsWith(`_${floorNumber}`));
  floorLightIds.forEach(id => lightSyncState.processedLightIds.delete(id));
  console.log(`🧹 INFLATION FIX: Cleared ${floorLightIds.length} processed light IDs for fresh sync`);
  
  // 🔧 FLOOR ISOLATION FIX: Filter rooms to only include rooms from the current floor
  // This prevents lights on floor 2 from being incorrectly matched to rooms on floor 1
  const currentFloorRooms = rooms.filter(room => {
    // Extract floor from stable ID (format: floor_X_coordinates...)
    if (room.stableId && room.stableId.startsWith('floor_')) {
      const floorMatch = room.stableId.match(/^floor_(\d+)_/);
      if (floorMatch) {
        const roomFloor = parseInt(floorMatch[1], 10);
        return roomFloor === floorNumber;
      }
    }
    // Fallback: if no stable ID or floor info, assume it belongs to the current floor
    return true;
  });
  
  console.log(`💡 Floor ${floorNumber} light sync: ${lights.length} lights, ${currentFloorRooms.length} rooms on this floor (filtered from ${rooms.length} total rooms)`);
  
  let syncedCount = 0;
  let skippedCount = 0;
  
  // Process each light
  for (const light of lights) {
    // Create a unique identifier for this light
    const lightId = `${light.id}_${light.position.x}_${light.position.y}_${light.floor}`;
    
    console.log(`💡 PROCESSING: Light ${lightId} - type: "${light.lightType}", pos: (${light.position.x}, ${light.position.y})`);
    
    // Skip if already processed globally
    if (lightSyncState.processedLightIds.has(lightId)) {
      console.warn(`💡 Skipping globally processed light ${lightId}`);
      skippedCount++;
      continue;
    }
    
    // Find the room this light belongs to
    const room = findElementRoomWithConsolidation(light.position, currentFloorRooms, 'light');
    
    console.log(`💡 ROOM_SEARCH: Light ${lightId} - found room: ${room ? `"${room.name}"` : 'null'}`);
    
    if (room && room.name) {
      // Check if this is an auto-generated room name
      const isAutoGenerated = /^Room \d+$/.test(room.name);
      
      console.log(`💡 ROOM_CHECK: Light ${lightId} - room: "${room.name}", isAutoGenerated: ${isAutoGenerated}`);
      
      if (!isAutoGenerated) {
        // Update light count for named rooms
        console.log(`💡 UPDATING: Light ${lightId} - updating ${light.lightType} count for room "${room.name}"`);
        updatedSiteNotesData = updateRoomLightCount(
          room.name, 
          light.lightType, 
          1, 
          updatedSiteNotesData, 
          floorNumber
        );
        
        lightSyncState.processedLightIds.add(lightId);
        syncedCount++;
        console.log(`  💡 ✅ AUTO-SYNCED: Light ${lightId} assigned to room "${room.name}" as ${light.lightType}`);
      } else {
        // For auto-generated rooms, just log but don't sync to surveyor data
        lightSyncState.processedLightIds.add(lightId);
        syncedCount++;
        console.log(`  💡 ⚠️ DETECTED: Light ${lightId} detected in auto-generated room "${room.name}" (not synced to surveyor data)`);
      }
    } else {
      console.warn(`  💡 Light ${lightId} at (${light.position.x}, ${light.position.y}) could not be assigned to a valid room`);
      console.warn(`  💡 Available rooms: ${currentFloorRooms.map(r => r.name).join(', ')}`);
      skippedCount++;
    }
  }
  
  console.log(`💡 Smart light sync complete for floor ${floorNumber}: ${syncedCount} synced, ${skippedCount} skipped`);
  
  // Mark sync as complete
  lightSyncState.isInProgress = false;

  return updatedSiteNotesData;
};

/**
 * Updates window dimensions for a specific room in surveyor data
 */
export const updateRoomWindowDimensions = (
  roomName: string,
  width: number,
  height: number,
  trickleVents: number | boolean,
  siteNotesData: SiteNotesData,
  floorNumber: number = 1,
  orientation?: string,
  isDraughtProofed?: boolean,
  windowType?: 'pvc' | 'wood' | 'metal',
  glazingGap?: string,
  glazingType?: string,
  windowCategory?: string,
  condition?: 'good' | 'average' | 'poor',
  displayId?: string
): SiteNotesData => {
  // 🔍 CRITICAL DEBUG: Check what values are actually being passed
  console.log(`🔍 DIMENSION DEBUG: updateRoomWindowDimensions called with:`, {
    roomName,
    width,
    height,
    widthType: typeof width,
    heightType: typeof height,
    widthGreaterThanZero: width > 0,
    heightGreaterThanZero: height > 0,
    glazingGap,
    glazingType,
    windowCategory
  });
  // Clone the site notes data to avoid mutations
  const updatedSiteNotesData = JSON.parse(JSON.stringify(siteNotesData));

  // Find the target floor
  const targetFloor = updatedSiteNotesData.floors.find((f: SurveyorFloor) => f.id === floorNumber);
  if (!targetFloor) {
    console.warn(`Floor ${floorNumber} not found when updating window dimensions for room "${roomName}"`);
    return siteNotesData;
  }

  // Find the room by name
  const targetRoom = targetFloor.rooms.find((room: SurveyorRoom) => room.name === roomName);
  if (!targetRoom) {
    console.warn(`Room "${roomName}" not found when updating window dimensions`);
    return siteNotesData;
  }

  // Update window dimensions (input is in mm from floor plan, convert to meters for site notes storage)
// 🛡️ PROTECTION: Preserve existing dimensions if new values are 0 or empty (prevent auto-sync overwrites)
const newWidth = width > 0 ? (width / 1000).toFixed(2) : '';
const newHeight = height > 0 ? (height / 1000).toFixed(2) : '';

// Only update dimensions if we have valid new values OR if current values are empty
if (newWidth || !targetRoom.windowWidth) {
  const oldWidth = targetRoom.windowWidth || 'empty';
  targetRoom.windowWidth = newWidth;
  console.log(`🛡️ DIMENSION PROTECTION: windowWidth updated to "${newWidth}" (was "${oldWidth}")`);
} else {
  console.log(`🛡️ DIMENSION PROTECTION: Preserving existing windowWidth "${targetRoom.windowWidth}" (would overwrite with empty)`);
}

if (newHeight || !targetRoom.windowHeight) {
  const oldHeight = targetRoom.windowHeight || 'empty';
  targetRoom.windowHeight = newHeight;
  console.log(`🛡️ DIMENSION PROTECTION: windowHeight updated to "${newHeight}" (was "${oldHeight}")`);
} else {
  console.log(`🛡️ DIMENSION PROTECTION: Preserving existing windowHeight "${targetRoom.windowHeight}" (would overwrite with empty)`);
}  targetRoom.windowTrickleVents = typeof trickleVents === 'number' ? String(trickleVents) : (trickleVents ? 'Yes' : 'No');
  
  // Update window orientation if provided (map to site notes format)
  if (orientation && orientation !== 'Unknown' && orientation !== '') {
    (targetRoom as any).windowOrientation = mapFloorPlanOrientationToSiteNotes(orientation);
  }
  
  // Update new window properties
  if (isDraughtProofed !== undefined) {
    (targetRoom as any).windowDraughtProofed = isDraughtProofed ? 'Yes' : 'No';
  }
  
  if (windowType) {
    (targetRoom as any).windowType = windowType;
  }
  
  if (condition) {
    (targetRoom as any).windowCondition = condition;
  }
  
  // Update glazing properties if provided
  if (glazingGap) {
    (targetRoom as any).windowGlazingGap = glazingGap;
    console.log(`🪟 DEBUG: Storing windowGlazingGap = "${glazingGap}" for room "${roomName}"`);
  }
  
  if (glazingType) {
    (targetRoom as any).windowGlazingType = glazingType;
    console.log(`🪟 DEBUG: Storing windowGlazingType = "${glazingType}" for room "${roomName}"`);
  }
  
  // Update window category if provided
  if (windowCategory) {
    (targetRoom as any).windowCategory = windowCategory;
    console.log(`🪟 DEBUG: Storing windowCategory = "${windowCategory}" for room "${roomName}"`);
  }
  
  // 🔧 MULTIPLE WINDOW HANDLING: Only clear windows array for single window rooms
  const hasMultipleWindows = targetRoom.windows && targetRoom.windows.length > 1;
  
  console.log(`🪟 DEBUG: Room "${roomName}" - hasMultipleWindows: ${hasMultipleWindows}, windows array length: ${targetRoom.windows?.length || 0}`);
  
  if (hasMultipleWindows) {
    console.log(`🪟 MULTIPLE WINDOW SAVE: Room "${roomName}" has ${targetRoom.windows.length} windows - updating individual window objects`);
    
    // For multiple windows, update ALL window objects with the same glazing data
    // This ensures window modal shows correct values for any window in the room
    targetRoom.windows.forEach((window: any, index: number) => {
      if (glazingGap && !window.glazingGap) window.glazingGap = glazingGap;
      if (glazingType && !window.glazingType) window.glazingType = glazingType;
      if (windowCategory && !window.windowCategory) window.windowCategory = windowCategory;
      if (windowType && !window.windowType) window.windowType = windowType;
      if (condition && !window.condition) window.condition = condition;
      if (isDraughtProofed !== undefined) window.isDraughtProofed = isDraughtProofed;
      if (orientation && orientation !== 'Unknown' && orientation !== '') {
        window.orientation = mapFloorPlanOrientationToSiteNotes(orientation);
      }
      
      console.log(`🪟 Updated window ${index + 1} with glazing data: Gap="${window.glazingGap}", Type="${window.glazingType}"`);
    });
    
    // Also update first window with dimensions if provided
    if (targetRoom.windows.length > 0 && (width > 0 || height > 0)) {
      const firstWindow = targetRoom.windows[0];
      if (width > 0) firstWindow.width = (width / 1000).toFixed(2);
      if (height > 0) firstWindow.height = (height / 1000).toFixed(2);
      firstWindow.trickleVents = trickleVents;
      console.log(`🪟 Updated first window dimensions: ${firstWindow.width}m × ${firstWindow.height}m`);
    }
    
    // 🔧 ROOMSURVEY COMPATIBILITY: Sync windowDetails for multiple window rooms
    (targetRoom as any).windowDetails = targetRoom.windows.map((window: any) => ({
      id: window.id,
      width: window.width || '',
      height: window.height || '',
      trickleVents: window.trickleVents !== undefined ? window.trickleVents : 0,
      isDraughtProofed: window.isDraughtProofed || false,
      windowType: window.windowType || '',
      condition: window.condition || '',
      orientation: window.orientation || '',
      glazingGap: window.glazingGap || '',
      glazingType: window.glazingType || '',
      photos: window.photos || []
    }));
    console.log(`🪟 ROOMSURVEY SYNC: Multiple windows - windowDetails created:`, {
      roomName,
      windowDetailsLength: (targetRoom as any).windowDetails.length,
      conditions: (targetRoom as any).windowDetails.map((w: any) => w.condition),
      windowTypes: (targetRoom as any).windowDetails.map((w: any) => w.windowType)
    });
  } else {
    // Single window room - clear windows array and use room-level fields
    console.log(`🪟 SINGLE WINDOW SAVE: Room "${roomName}" - using room-level fields`);
    console.log(`🪟 DEBUG: Storing single window data - windowCategory: "${windowCategory}", glazingType: "${glazingType}", glazingGap: "${glazingGap}"`);
    targetRoom.windows = [];
    
    // Store displayId at room level for single windows
    if (displayId) {
      (targetRoom as any).windowDisplayId = displayId;
      console.log(`🪟 Stored single window displayId: ${displayId} for room "${roomName}"`);
    }
    
    // Verify the data was stored
    console.log(`🪟 DEBUG: After storage - room.windowCategory: "${(targetRoom as any).windowCategory}", room.windowGlazingType: "${(targetRoom as any).windowGlazingType}", room.windowGlazingGap: "${(targetRoom as any).windowGlazingGap}", windowTrickleVents: "${targetRoom.windowTrickleVents}", windowOrientation: "${(targetRoom as any).windowOrientation}"`);
    
    // 🔧 ROOMSURVEY COMPATIBILITY: Sync windowDetails for single window rooms
    (targetRoom as any).windowDetails = [{
      id: 'window-1',
      width: newWidth,
      height: newHeight,
      trickleVents: typeof trickleVents === 'number' ? trickleVents : (trickleVents ? 1 : 0),
      isDraughtProofed: isDraughtProofed || false,
      windowType: windowType || '',
      condition: condition || '',
      orientation: orientation ? mapFloorPlanOrientationToSiteNotes(orientation) : '',
      glazingGap: glazingGap || '',
      glazingType: glazingType || '',
      photos: []
    }];
    console.log(`🪟 ROOMSURVEY SYNC: Single window - windowDetails created:`, {
      roomName,
      windowDetailsLength: (targetRoom as any).windowDetails.length,
      condition: (targetRoom as any).windowDetails[0].condition,
      windowType: (targetRoom as any).windowDetails[0].windowType,
      width: newWidth,
      height: newHeight
    });
  }

  console.log(`🪟 Window dimensions updated for room "${roomName}": ${width}mm → ${(width / 1000).toFixed(2)}m × ${height}mm → ${(height / 1000).toFixed(2)}m, Trickle vents: ${trickleVents ? 'Yes' : 'No'}${orientation ? `, Orientation: ${orientation}` : ''}${isDraughtProofed !== undefined ? `, Draught proofed: ${isDraughtProofed ? 'Yes' : 'No'}` : ''}${windowType ? `, Type: ${windowType}` : ''}${glazingGap ? `, Glazing gap: ${glazingGap}` : ''}${glazingType ? `, Glazing type: ${glazingType}` : ''}${windowCategory ? `, Category: ${windowCategory}` : ''}`);

  return updatedSiteNotesData;
};

/**
 * Ensures window placeholders exist in site notes for windows without dimensions
 * This preserves the window entries so users can input dimensions later
 */
const ensureWindowPlaceholdersExist = (
  roomName: string,
  windowsWithoutDimensions: WindowElement[],
  siteNotesData: SiteNotesData,
  floorNumber: number = 1
): SiteNotesData => {
  // Clone the site notes data to avoid mutations
  const updatedSiteNotesData = JSON.parse(JSON.stringify(siteNotesData));

  // Find the target floor
  const targetFloor = updatedSiteNotesData.floors.find((f: SurveyorFloor) => f.id === floorNumber);
  if (!targetFloor) {
    console.warn(`Floor ${floorNumber} not found when ensuring window placeholders for room "${roomName}"`);
    return siteNotesData;
  }

  // Find the room by name
  const targetRoom = targetFloor.rooms.find((room: SurveyorRoom) => room.name === roomName);
  if (!targetRoom) {
    console.warn(`Room "${roomName}" not found when ensuring window placeholders`);
    return siteNotesData;
  }

  // Ensure windows array exists
  if (!targetRoom.windows) {
    targetRoom.windows = [];
  }

  // Add placeholders for windows without dimensions if they don't already exist
  windowsWithoutDimensions.forEach((window, index) => {
    const existingWindowIndex = targetRoom.windows.findIndex((w: any) => 
      w.id === (index + 1) || 
      (w.width === '' && w.height === '' && !w.orientation)
    );

    if (existingWindowIndex === -1) {
      // Create new placeholder
      const mappedOrientation = (window.orientation && window.orientation !== 'Unknown') ? 
        mapFloorPlanOrientationToSiteNotes(window.orientation) : '';
      
      targetRoom.windows.push({
        id: targetRoom.windows.length + 1,
        displayId: window.displayId || '', // Include sequential window ID
        width: '',
        height: '',
        orientation: mappedOrientation,
        trickleVents: window.trickleVents || false,
        isDraughtProofed: window.isDraughtProofed || false,
        windowType: window.windowType || '',
        glazingGap: (window as any).glazingGap || '',
        glazingType: (window as any).glazingType || '',
        windowCategory: (window as any).windowCategory || ''
      });
      
      console.log(`🪟 Created placeholder for window ${index + 1} in room "${roomName}" (orientation: ${mappedOrientation || 'none'})`);
    } else {
      // Update existing placeholder with any available data
      const existingWindow = targetRoom.windows[existingWindowIndex];
      if (window.orientation && window.orientation !== 'Unknown' && !existingWindow.orientation) {
        existingWindow.orientation = mapFloorPlanOrientationToSiteNotes(window.orientation);
        console.log(`🧭 Updated placeholder orientation for window in room "${roomName}": ${existingWindow.orientation}`);
      }
      
      if (window.trickleVents !== undefined && !existingWindow.trickleVents) {
        existingWindow.trickleVents = window.trickleVents;
      }
      
      if (window.isDraughtProofed !== undefined && !existingWindow.isDraughtProofed) {
        existingWindow.isDraughtProofed = window.isDraughtProofed;
      }
      
      if (window.windowType && !existingWindow.windowType) {
        existingWindow.windowType = window.windowType;
      }
      
      // Update glazing properties if available and not already set
      if ((window as any).glazingGap && !existingWindow.glazingGap) {
        existingWindow.glazingGap = (window as any).glazingGap;
      }
      
      if ((window as any).glazingType && !existingWindow.glazingType) {
        existingWindow.glazingType = (window as any).glazingType;
      }
      
      if ((window as any).windowCategory && !existingWindow.windowCategory) {
        existingWindow.windowCategory = (window as any).windowCategory;
      }
    }
  });

  console.log(`🪟 Ensured ${windowsWithoutDimensions.length} window placeholders exist for room "${roomName}" on floor ${floorNumber}`);
  return updatedSiteNotesData;
};

/**
 * Updates multiple window dimensions for a specific room in surveyor data
 * This replaces the single window fields and populates the windows array
 */
export const updateRoomMultipleWindowDimensions = (
  roomName: string,
  windows: WindowElement[],
  siteNotesData: SiteNotesData,
  floorNumber: number = 1,
  isManualSync: boolean = false,
  isProtectedMode: boolean = false
): SiteNotesData => {
  // Clone the site notes data to avoid mutations
  const updatedSiteNotesData = JSON.parse(JSON.stringify(siteNotesData));

  // Find the target floor
  const targetFloor = updatedSiteNotesData.floors.find((f: SurveyorFloor) => f.id === floorNumber);
  if (!targetFloor) {
    console.warn(`Floor ${floorNumber} not found when updating multiple window dimensions for room "${roomName}"`);
    return siteNotesData;
  }

  // Find the room by name
  const targetRoom = targetFloor.rooms.find((room: SurveyorRoom) => room.name === roomName);
  if (!targetRoom) {
    console.warn(`Room "${roomName}" not found when updating multiple window dimensions`);
    return siteNotesData;
  }

  // Filter windows that belong to this room and this floor
  // Note: We don't filter by dimensions here to allow orientation sync for all windows
  const roomWindows = windows.filter(window => {
    const belongsToThisFloor = window.floor === floorNumber;
    
    if (!belongsToThisFloor) {
      console.log(`🪟 Filtering out window from floor ${window.floor} (target floor: ${floorNumber}) for room "${roomName}"`);
    }
    
    return belongsToThisFloor;
  });

  // Separate windows with and without dimensions
  const windowsWithDimensions = roomWindows.filter(w => w.width && w.height);
  const windowsWithoutDimensions = roomWindows.filter(w => !w.width || !w.height);

  // console.log(`🪟 Updating windows for room "${roomName}" on floor ${floorNumber}:`, {
  //   totalWindowsProvided: windows.length,
  //   windowsAfterFloorFilter: roomWindows.length,
  //   windowsWithDimensions: windowsWithDimensions.length,
  //   windowsWithoutDimensions: windowsWithoutDimensions.length,
  //   windowFloors: windows.map(w => w.floor),
  //   targetFloor: floorNumber
  // });

  if (roomWindows.length === 0) {
    // 🛡️ PROTECTION: Don't clear manually entered dimensions unless this is a manual sync
    if (isManualSync || (!targetRoom.windowWidth && !targetRoom.windowHeight)) {
      // Clear all window data if no windows (manual sync or no existing dimensions)
      targetRoom.windowWidth = '';
      targetRoom.windowHeight = '';
      targetRoom.windowTrickleVents = '';
      console.log(`🪟 Cleared all window data for room "${roomName}" on floor ${floorNumber} (manual: ${isManualSync})`);
    } else {
      // Preserve manually entered dimensions, only clear other fields
      targetRoom.windowTrickleVents = '';
      console.log(`🛡️ DIMENSION PROTECTION: Preserving manual dimensions for room "${roomName}" (${targetRoom.windowWidth}m × ${targetRoom.windowHeight}m) - only clearing trickle vents`);
    }
    
    // 🔧 PRESERVE GLAZING DATA: Only clear windows array if no glazing data exists
    const hasGlazingData = (targetRoom as any).windowGlazingGap || (targetRoom as any).windowGlazingType || (targetRoom as any).windowCategory;
    if (!hasGlazingData) {
      targetRoom.windows = []; // Safe to clear array since no glazing data to preserve
      console.log(`🪟 Cleared windows array for room "${roomName}" (no glazing data to preserve)`);
    } else {
      console.log(`🛡️ GLAZING PROTECTION: Preserving windows array for room "${roomName}" due to existing glazing data`);
    }
  } else if (windowsWithDimensions.length === 1 && windowsWithoutDimensions.length === 0) {
    // Single window with dimensions - use the single fields for backward compatibility (convert mm to meters)
    const window = windowsWithDimensions[0];
    
    // 🛡️ PROTECTION: Preserve existing dimensions if new values are empty/zero (prevent auto-sync overwrites)
    const newWidth = window.width > 0 ? (window.width / 1000).toFixed(2) : '';
    const newHeight = window.height > 0 ? (window.height / 1000).toFixed(2) : '';
    
    // Only update dimensions if we have valid new values OR if current values are empty
    if (newWidth || !targetRoom.windowWidth) {
      const oldWidth = targetRoom.windowWidth || 'empty';
      targetRoom.windowWidth = newWidth;
      console.log(`🛡️ SINGLE WINDOW PROTECTION: windowWidth updated to "${newWidth}" (was "${oldWidth}")`);
    } else {
      console.log(`🛡️ SINGLE WINDOW PROTECTION: Preserving existing windowWidth "${targetRoom.windowWidth}" (would overwrite with empty)`);
    }
    
    if (newHeight || !targetRoom.windowHeight) {
      const oldHeight = targetRoom.windowHeight || 'empty';
      targetRoom.windowHeight = newHeight;
      console.log(`🛡️ SINGLE WINDOW PROTECTION: windowHeight updated to "${newHeight}" (was "${oldHeight}")`);
    } else {
      console.log(`🛡️ SINGLE WINDOW PROTECTION: Preserving existing windowHeight "${targetRoom.windowHeight}" (would overwrite with empty)`);
    }

    targetRoom.windowTrickleVents = typeof window.trickleVents === 'number' ? String(window.trickleVents) : (window.trickleVents ? 'Yes' : 'No');
    
    // 🔧 PRESERVE GLAZING DATA: Only clear windows array if no glazing data exists
    const hasGlazingData = (targetRoom as any).windowGlazingGap || (targetRoom as any).windowGlazingType || (targetRoom as any).windowCategory;
    if (!hasGlazingData) {
      targetRoom.windows = []; // Safe to clear array since we're using single fields and no glazing data
      console.log(`🪟 Cleared windows array for single window room "${roomName}" (no glazing data to preserve)`);
    } else {
      console.log(`🛡️ GLAZING PROTECTION: Preserving windows array for room "${roomName}" due to existing glazing data`);
    }
    console.log(`🪟 Single window updated for room "${roomName}" on floor ${floorNumber}: ${window.width}mm → ${newWidth}m × ${window.height}mm → ${newHeight}m`);
  } else if (windowsWithDimensions.length === 0 && windowsWithoutDimensions.length > 0) {
    // Only windows without dimensions - preserve existing data and create placeholders
    console.log(`🪟 Found ${windowsWithoutDimensions.length} windows without dimensions for room "${roomName}" - creating placeholders`);
    
    // 🛡️ PROTECTION: Don't clear manually entered dimensions unless this is a manual sync
    if (isManualSync || (!targetRoom.windowWidth && !targetRoom.windowHeight)) {
      // Clear single window fields since we're using the array (manual sync or no existing dimensions)
      targetRoom.windowWidth = '';
      targetRoom.windowHeight = '';
      targetRoom.windowTrickleVents = '';
      console.log(`🪟 Cleared single window fields for room "${roomName}" (manual: ${isManualSync}) - using windows array`);
    } else {
      // Preserve manually entered dimensions, only clear trickle vents
      targetRoom.windowTrickleVents = '';
      console.log(`🛡️ NO-DIMENSIONS PROTECTION: Preserving manual dimensions for room "${roomName}" (${targetRoom.windowWidth}m × ${targetRoom.windowHeight}m) - using windows array`);
    }
    
    // Ensure windows array exists and has placeholders
    const existingWindows = targetRoom.windows || [];
    
    // Create placeholders for windows without dimensions
    windowsWithoutDimensions.forEach((window, index) => {
      const mappedOrientation = (window.orientation && window.orientation !== 'Unknown') ? 
        mapFloorPlanOrientationToSiteNotes(window.orientation) : '';
      
      if (index >= existingWindows.length) {
        // Add new placeholder
        existingWindows.push({
          id: index + 1,
          displayId: window.displayId || '', // Include sequential window ID
          width: '',
          height: '',
          orientation: mappedOrientation,
          trickleVents: window.trickleVents || false,
          isDraughtProofed: window.isDraughtProofed || false,
          windowType: window.windowType || '',
          // 🔧 POSITION-BASED MATCHING: Store position and wall ID for placeholders too
          position: window.position ? { x: window.position.x, y: window.position.y } : undefined,
          wallId: window.wallId || undefined
        });
        console.log(`🪟 Created placeholder ${index + 1} for room "${roomName}" with orientation: ${mappedOrientation || 'none'}`);
      } else {
        // Update existing placeholder
        const existingWindow = existingWindows[index];
        if (mappedOrientation && !existingWindow.orientation) {
          existingWindow.orientation = mappedOrientation;
          console.log(`🧭 Updated placeholder ${index + 1} orientation for room "${roomName}": ${mappedOrientation}`);
        }
        // Update position data for existing placeholders
        if (window.position) {
          existingWindow.position = { x: window.position.x, y: window.position.y };
        }
        if (window.wallId) {
          existingWindow.wallId = window.wallId;
        }
      }
    });
    
    targetRoom.windows = existingWindows;
    console.log(`🪟 Created/updated ${windowsWithoutDimensions.length} window placeholders for room "${roomName}"`);
      } else {
    // Multiple windows or mix of windows with/without dimensions - clear single fields and use array
    
    // 🔧 PRESERVE GLAZING DATA: Before clearing fields, save existing glazing data for UI compatibility
    const existingGlazingGap = (targetRoom as any).windowGlazingGap;
    const existingGlazingType = (targetRoom as any).windowGlazingType;
    const existingWindowCategory = (targetRoom as any).windowCategory;
    const existingWindowType = (targetRoom as any).windowType;
    const existingWindowOrientation = (targetRoom as any).windowOrientation;
    
    // 🛡️ PROTECTION: Don't clear manually entered dimensions unless this is a manual sync
    if (isManualSync || (!targetRoom.windowWidth && !targetRoom.windowHeight)) {
      // Clear single window fields since we're using the array (manual sync or no existing dimensions)
      targetRoom.windowWidth = '';
      targetRoom.windowHeight = '';
      targetRoom.windowTrickleVents = '';
      console.log(`🪟 Cleared single window fields for room "${roomName}" (manual: ${isManualSync}) - using multiple windows array`);
    } else {
      // Preserve manually entered dimensions, only clear trickle vents
      targetRoom.windowTrickleVents = '';
      console.log(`🛡️ MULTIPLE WINDOWS PROTECTION: Preserving manual dimensions for room "${roomName}" (${targetRoom.windowWidth}m × ${targetRoom.windowHeight}m) - using windows array`);
    }
    
    // 🔧 RESTORE GLAZING DATA: Preserve glazing data for UI compatibility with multiple windows
    if (existingGlazingGap) {
      (targetRoom as any).windowGlazingGap = existingGlazingGap;
      console.log(`🔧 TRANSITION: Preserved windowGlazingGap "${existingGlazingGap}" for multi-window room "${roomName}"`);
    }
    if (existingGlazingType) {
      (targetRoom as any).windowGlazingType = existingGlazingType;
      console.log(`🔧 TRANSITION: Preserved windowGlazingType "${existingGlazingType}" for multi-window room "${roomName}"`);
    }
    if (existingWindowCategory) {
      (targetRoom as any).windowCategory = existingWindowCategory;
      console.log(`🔧 TRANSITION: Preserved windowCategory "${existingWindowCategory}" for multi-window room "${roomName}"`);
    }
    if (existingWindowType) {
      (targetRoom as any).windowType = existingWindowType;
      console.log(`🔧 TRANSITION: Preserved windowType "${existingWindowType}" for multi-window room "${roomName}"`);
    }
    if (existingWindowOrientation) {
      (targetRoom as any).windowOrientation = existingWindowOrientation;
      console.log(`🔧 TRANSITION: Preserved windowOrientation "${existingWindowOrientation}" for multi-window room "${roomName}"`);
    }
    
    // Update windows array - handle both windows with and without dimensions
    const existingWindows = targetRoom.windows || [];
    
    // Combine all windows for processing
    const allWindowsToProcess = [...windowsWithDimensions, ...windowsWithoutDimensions];
    
    // Process existing windows - match and update them with floor plan data
    if (existingWindows.length > 0) {
      // Track debug info for matching logic
      console.log(`🪟 🔧 MULTI-WINDOW MATCHING DEBUG for room "${roomName}":`);
      console.log(`🪟 🔧 Existing windows in SiteNotes: ${existingWindows.length}`);
      existingWindows.forEach((ew: any, i: number) => {
        const widthDisplay = ew.width ? `${ew.width}m` : 'no width';
        const heightDisplay = ew.height ? `${ew.height}m` : 'no height';
        console.log(`🪟 🔧 Existing ${i + 1}: ${widthDisplay} × ${heightDisplay}`);
      });
      console.log(`🪟 🔧 Floor plan windows: ${allWindowsToProcess.length}`);
      allWindowsToProcess.forEach((fw, i) => {
        const widthDisplay = fw.width ? `${(fw.width / 1000).toFixed(2)}m` : 'no width';
        const heightDisplay = fw.height ? `${(fw.height / 1000).toFixed(2)}m` : 'no height';
        const posDisplay = fw.position ? `pos(${fw.position.x},${fw.position.y})` : 'no pos';
        const wallIdDisplay = fw.wallId ? `wallId="${fw.wallId}"` : 'no wallId';
        console.log(`🪟 🔧 FloorPlan ${i + 1}: ${widthDisplay} × ${heightDisplay} ${posDisplay} ${wallIdDisplay}`);
      });
      
      // Track which floor plan windows have been matched to prevent duplicates
      const matchedFloorPlanWindows = new Set<number>();
      
      existingWindows.forEach((existingWindow: any, index: number) => {
        let floorPlanWindow;
        let floorPlanWindowIndex = -1;
        let matchMethod = 'none';
        
        // Enhanced matching logic with backward compatibility
        // Strategy 1: Exact position match (modern data with position fields)
        if (existingWindow.position && existingWindow.position.x !== undefined && existingWindow.position.y !== undefined) {
          const tightTolerance = 10; // Very tight tolerance for exact matches
          floorPlanWindowIndex = allWindowsToProcess.findIndex(fpw => {
            if (fpw.position && fpw.position.x !== undefined && fpw.position.y !== undefined && 
                !matchedFloorPlanWindows.has(allWindowsToProcess.indexOf(fpw))) {
              const exactMatch = Math.abs(fpw.position.x - existingWindow.position.x) < tightTolerance && 
                                Math.abs(fpw.position.y - existingWindow.position.y) < tightTolerance;
              if (exactMatch) matchMethod = 'exact_position';
              return exactMatch;
            }
            return false;
          });
          if (floorPlanWindowIndex >= 0) {
            floorPlanWindow = allWindowsToProcess[floorPlanWindowIndex];
            matchedFloorPlanWindows.add(floorPlanWindowIndex);
          }
        }
        
        // Strategy 2: Wall ID matching (including split wall detection)
        if (!floorPlanWindow && existingWindow.wallId && existingWindow.wallId.trim() !== '') {
          floorPlanWindowIndex = allWindowsToProcess.findIndex(fpw => {
            if (!matchedFloorPlanWindows.has(allWindowsToProcess.indexOf(fpw))) {
              // Direct wall ID match
              if (fpw.wallId && fpw.wallId === existingWindow.wallId) {
                matchMethod = 'direct_wallId';
                return true;
              }
              
              // Split wall detection - check if wall IDs are related (parent-child relationship)
              if (fpw.wallId && existingWindow.wallId) {
                const isSplitWallRelated = fpw.wallId.includes('split') && existingWindow.wallId.includes('split') &&
                  fpw.wallId.split('_')[0] === existingWindow.wallId.split('_')[0];
                if (isSplitWallRelated) {
                  matchMethod = 'split_wallId';
                  return true;
                }
              }
            }
            return false;
          });
          if (floorPlanWindowIndex >= 0) {
            floorPlanWindow = allWindowsToProcess[floorPlanWindowIndex];
            matchedFloorPlanWindows.add(floorPlanWindowIndex);
          }
          console.log(`🪟 🔧 WALL ID MATCHING: Existing wallId="${existingWindow.wallId}", found match: ${!!floorPlanWindow} (${matchMethod})`);
        }
        
        // Strategy 3: Broader position matching (for coordinate drift in modern data)
        if (!floorPlanWindow && existingWindow.position && existingWindow.position.x !== undefined && existingWindow.position.y !== undefined) {
          const broadTolerance = 100; // Broader tolerance for coordinate drift
          floorPlanWindowIndex = allWindowsToProcess.findIndex(fpw => {
            if (fpw.position && fpw.position.x !== undefined && fpw.position.y !== undefined && 
                !matchedFloorPlanWindows.has(allWindowsToProcess.indexOf(fpw))) {
              const broadMatch = Math.abs(fpw.position.x - existingWindow.position.x) < broadTolerance && 
                                Math.abs(fpw.position.y - existingWindow.position.y) < broadTolerance;
              if (broadMatch) matchMethod = 'broad_position';
              return broadMatch;
            }
            return false;
          });
          if (floorPlanWindowIndex >= 0) {
            floorPlanWindow = allWindowsToProcess[floorPlanWindowIndex];
            matchedFloorPlanWindows.add(floorPlanWindowIndex);
          }
          console.log(`🪟 🔧 BROAD POSITION MATCHING: Existing pos(${existingWindow.position.x},${existingWindow.position.y}), found match: ${!!floorPlanWindow}`);
        }
        
        // Strategy 4: Dimension-based matching (backward compatible - legacy data priority)
        if (!floorPlanWindow && existingWindow.width && existingWindow.height && 
            existingWindow.width !== '' && existingWindow.height !== '') {
          const existingWidthMm = Math.round(parseFloat(existingWindow.width) * 1000);
          const existingHeightMm = Math.round(parseFloat(existingWindow.height) * 1000);
          const tolerance = 50; // 50mm tolerance for dimension matching
          
          floorPlanWindowIndex = allWindowsToProcess.findIndex(fpw => {
            if (fpw.width && fpw.height && !matchedFloorPlanWindows.has(allWindowsToProcess.indexOf(fpw))) {
              const dimMatch = Math.abs(fpw.width - existingWidthMm) < tolerance && 
                             Math.abs(fpw.height - existingHeightMm) < tolerance;
              if (dimMatch) matchMethod = 'dimensions';
              return dimMatch;
            }
            return false;
          });
          
          if (floorPlanWindowIndex >= 0) {
            floorPlanWindow = allWindowsToProcess[floorPlanWindowIndex];
            matchedFloorPlanWindows.add(floorPlanWindowIndex);
          }
          
          console.log(`🪟 🔧 DIMENSION MATCHING: Existing ${existingWidthMm}mm × ${existingHeightMm}mm, found match: ${!!floorPlanWindow}`);
        }
        
        // Strategy 5: Orientation + dimension matching (legacy compatibility)
        if (!floorPlanWindow && existingWindow.orientation && existingWindow.orientation !== '' && 
            existingWindow.width && existingWindow.height) {
          floorPlanWindowIndex = allWindowsToProcess.findIndex(fpw => {
            if (!matchedFloorPlanWindows.has(allWindowsToProcess.indexOf(fpw))) {
              // Check orientation match (accounting for mapping differences)
              const fpOrientation = fpw.orientation ? mapFloorPlanOrientationToSiteNotes(fpw.orientation) : '';
              const orientationMatch = fpOrientation === existingWindow.orientation;
              
              // Loose dimension match for legacy data
              let dimensionMatch = false;
              if (fpw.width && fpw.height && existingWindow.width && existingWindow.height) {
                const existingWidthMm = Math.round(parseFloat(existingWindow.width) * 1000);
                const existingHeightMm = Math.round(parseFloat(existingWindow.height) * 1000);
                const looseTolerance = 100; // Looser tolerance for legacy matching
                dimensionMatch = Math.abs(fpw.width - existingWidthMm) < looseTolerance && 
                               Math.abs(fpw.height - existingHeightMm) < looseTolerance;
              }
              
              if (orientationMatch && dimensionMatch) {
                matchMethod = 'orientation_dimension';
                return true;
              }
            }
            return false;
          });
          
          if (floorPlanWindowIndex >= 0) {
            floorPlanWindow = allWindowsToProcess[floorPlanWindowIndex];
            matchedFloorPlanWindows.add(floorPlanWindowIndex);
            console.log(`🪟 🔧 ORIENTATION+DIMENSION MATCHING: Found match for "${existingWindow.orientation}" window`);
          }
        }
        
        // Enhanced fallback logic with data preservation
        if (!floorPlanWindow) {
          // Try smart dimensional matching with looser criteria for split wall scenarios
          if (existingWindow.width && existingWindow.height) {
            const existingWidthMm = Math.round(parseFloat(existingWindow.width) * 1000);
            const existingHeightMm = Math.round(parseFloat(existingWindow.height) * 1000);
            const looseTolerance = 200; // Very loose tolerance for split wall scenarios
            
            floorPlanWindowIndex = allWindowsToProcess.findIndex(fpw => {
              if (fpw.width && fpw.height && !matchedFloorPlanWindows.has(allWindowsToProcess.indexOf(fpw))) {
                const looseMatch = Math.abs(fpw.width - existingWidthMm) < looseTolerance && 
                                 Math.abs(fpw.height - existingHeightMm) < looseTolerance;
                if (looseMatch) matchMethod = 'loose_dimensions';
                return looseMatch;
              }
              return false;
            });
            
            if (floorPlanWindowIndex >= 0) {
              floorPlanWindow = allWindowsToProcess[floorPlanWindowIndex];
              matchedFloorPlanWindows.add(floorPlanWindowIndex);
              console.log(`🪟 🔧 LOOSE DIMENSION MATCHING: Found match with loose criteria for existing window ${index + 1}`);
            }
          }
        }
        
        // Strategy 6: Legacy index-based matching (backward compatibility priority)
        if (!floorPlanWindow && existingWindows.length === allWindowsToProcess.length && 
            index < allWindowsToProcess.length && !matchedFloorPlanWindows.has(index)) {
          // This is safe for legacy data where windows were stored in order
          floorPlanWindow = allWindowsToProcess[index];
          floorPlanWindowIndex = index;
          matchedFloorPlanWindows.add(floorPlanWindowIndex);
          matchMethod = 'legacy_index';
          console.log(`🪟 🔧 LEGACY INDEX: Using index-based match for window ${index + 1} (counts match - legacy mode)`);
        }
        
        // Strategy 7: Loose index matching for legacy data (when counts don't match perfectly)
        if (!floorPlanWindow && index < allWindowsToProcess.length && !matchedFloorPlanWindows.has(index)) {
          // For legacy data, try to match by index even if counts don't match exactly
          const potentialMatch = allWindowsToProcess[index];
          if (potentialMatch) {
            // Validate this is a reasonable match for legacy data
            let isReasonableMatch = false;
            
            // Check if dimensions are somewhat reasonable
            if (existingWindow.width && existingWindow.height && potentialMatch.width && potentialMatch.height) {
              const existingWidthMm = Math.round(parseFloat(existingWindow.width) * 1000);
              const existingHeightMm = Math.round(parseFloat(existingWindow.height) * 1000);
              const veryLooseTolerance = 500; // Very loose for legacy
              isReasonableMatch = Math.abs(potentialMatch.width - existingWidthMm) < veryLooseTolerance && 
                                Math.abs(potentialMatch.height - existingHeightMm) < veryLooseTolerance;
            } else if (!existingWindow.width || !existingWindow.height) {
              // If existing window has no dimensions, it's reasonable to match
              isReasonableMatch = true;
            }
            
            if (isReasonableMatch) {
              floorPlanWindow = potentialMatch;
              floorPlanWindowIndex = index;
              matchedFloorPlanWindows.add(floorPlanWindowIndex);
              matchMethod = 'loose_legacy_index';
              console.log(`🪟 🔧 LOOSE LEGACY INDEX: Reasonable match found for window ${index + 1}`);
            }
          }
        }
        
        // Strategy 8: Data preservation mode (keep existing data if no reasonable match found)
        if (!floorPlanWindow) {
          console.log(`🪟 🔧 DATA PRESERVATION: No match found for existing window ${index + 1}, preserving all existing data`);
          matchMethod = 'preserved';
          // Don't set floorPlanWindow - this will preserve existing window data completely
        }
        
        console.log(`🪟 🔧 MATCHING: Existing window ${index + 1} matched via ${matchMethod}`);
        
        if (floorPlanWindow) {
          
          // 🛡️ DIMENSION PROTECTION: Only update dimensions if manual sync or window has no manual dimensions
          const hasManualDimensions = existingWindow.width && existingWindow.height && 
                                    existingWindow.width !== '' && existingWindow.height !== '' &&
                                    existingWindow.width !== '0.00' && existingWindow.height !== '0.00';
          
          if (floorPlanWindow.width && floorPlanWindow.height) {
            if (isManualSync || !hasManualDimensions) {
              // Update dimensions from floor plan (convert mm to meters)
              existingWindow.width = (floorPlanWindow.width / 1000).toFixed(2);
              existingWindow.height = (floorPlanWindow.height / 1000).toFixed(2);
              console.log(`🪟 Updated window ${index + 1} dimensions from floor plan: ${existingWindow.width}m × ${existingWindow.height}m (manual: ${isManualSync})`);
            } else {
              // Preserve manually entered dimensions
              console.log(`🛡️ DIMENSION PROTECTION: Preserving manual dimensions for window ${index + 1}: ${existingWindow.width}m × ${existingWindow.height}m`);
            }
          } else {
            // Preserve existing dimensions for windows without floor plan dimensions
            existingWindow.width = existingWindow.width || '';
            existingWindow.height = existingWindow.height || '';
          }
          // Update orientation from floor plan if available and valid
          if (floorPlanWindow.orientation && 
              floorPlanWindow.orientation !== 'Unknown' && 
              floorPlanWindow.orientation !== '' &&
              floorPlanWindow.orientation.trim() !== '') {
            // Map floor plan orientation format to site notes format
            const mappedOrientation = mapFloorPlanOrientationToSiteNotes(floorPlanWindow.orientation);
            existingWindow.orientation = mappedOrientation;
            console.log(`🧭 Updated window ${index + 1} orientation from "${floorPlanWindow.orientation}" to "${mappedOrientation}" for room "${roomName}"`);
          } else if (!existingWindow.orientation || 
                     existingWindow.orientation === 'Unknown' || 
                     existingWindow.orientation === '' ||
                     existingWindow.orientation.trim() === '') {
            // Set empty string if no valid orientation available (shows "Select Orientation" in dropdown)
            existingWindow.orientation = '';
          }
          // Update trickle vents if available
          if (floorPlanWindow.trickleVents !== undefined) {
            existingWindow.trickleVents = floorPlanWindow.trickleVents;
          }
          // Update new window properties if available
          if (floorPlanWindow.isDraughtProofed !== undefined) {
            existingWindow.isDraughtProofed = floorPlanWindow.isDraughtProofed;
          }
          if (floorPlanWindow.windowType) {
            existingWindow.windowType = floorPlanWindow.windowType;
          }
          // Update glazing properties if available
          if ((floorPlanWindow as any).glazingGap) {
            existingWindow.glazingGap = (floorPlanWindow as any).glazingGap;
          }
          if ((floorPlanWindow as any).glazingType) {
            existingWindow.glazingType = (floorPlanWindow as any).glazingType;
          }
          if ((floorPlanWindow as any).windowCategory) {
            existingWindow.windowCategory = (floorPlanWindow as any).windowCategory;
          }
          if ((floorPlanWindow as any).condition) {
            existingWindow.condition = (floorPlanWindow as any).condition;
          }
          
          // 🔧 POSITION-BASED MATCHING: Store position and wall ID for reliable matching
          if (floorPlanWindow.position) {
            existingWindow.position = { x: floorPlanWindow.position.x, y: floorPlanWindow.position.y };
          }
          if (floorPlanWindow.wallId) {
            existingWindow.wallId = floorPlanWindow.wallId;
          }
          
          // Update displayId from floor plan window
          if (floorPlanWindow.displayId) {
            existingWindow.displayId = floorPlanWindow.displayId;
            console.log(`🪟 Updated window ${index + 1} displayId: ${floorPlanWindow.displayId} for room "${roomName}"`);
          }
        }
      });
      
      
      // Smart handling of unmatched floor plan windows (prevent dummy creation)
      if (!isManualSync) {
        const unmatchedWindows = allWindowsToProcess.filter((_, index) => !matchedFloorPlanWindows.has(index));
        
        // Only create new windows if:
        // 1. There are significantly more floor plan windows than existing windows, OR
        // 2. There are no existing windows at all, OR  
        // 3. The unmatched windows have valid dimensions/data (not just placeholders)
        const shouldCreateNewWindows = existingWindows.length === 0 || 
          unmatchedWindows.length > existingWindows.length * 0.5 ||
          unmatchedWindows.some(w => w.width > 0 && w.height > 0);
        
        if (shouldCreateNewWindows && unmatchedWindows.length > 0) {
          // Filter out likely duplicate/phantom windows
          const validUnmatchedWindows = unmatchedWindows.filter(window => {
            // Only create windows that have meaningful data
            const hasValidDimensions = window.width > 0 && window.height > 0;
            const hasValidPosition = window.position && window.position.x !== undefined && window.position.y !== undefined;
            const hasValidWallId = window.wallId && window.wallId.trim() !== '';
            
            return hasValidDimensions || hasValidPosition || hasValidWallId;
          });
          
          validUnmatchedWindows.forEach((window, index) => {
            const mappedOrientation = (window.orientation && window.orientation !== 'Unknown') ? mapFloorPlanOrientationToSiteNotes(window.orientation) : '';
            console.log(`🪟 Creating VALIDATED NEW window ${existingWindows.length + index + 1} for room "${roomName}" - Original orientation: "${window.orientation}", Mapped: "${mappedOrientation}"`);
            
            existingWindows.push({
              id: existingWindows.length + index + 1,
              displayId: window.displayId || '', // Include sequential window ID
              width: window.width ? (window.width / 1000).toFixed(2) : '',
              height: window.height ? (window.height / 1000).toFixed(2) : '',
              orientation: mappedOrientation,
              trickleVents: window.trickleVents || false,
              isDraughtProofed: window.isDraughtProofed || false,
              windowType: window.windowType || '',
              condition: (window as any).condition || '',
              glazingGap: (window as any).glazingGap || '',
              glazingType: (window as any).glazingType || '',
              windowCategory: (window as any).windowCategory || '',
              position: window.position ? { x: window.position.x, y: window.position.y } : undefined,
              wallId: window.wallId || undefined
            });
          });
          
          if (validUnmatchedWindows.length > 0) {
            console.log(`🪟 Created ${validUnmatchedWindows.length} validated new windows for room "${roomName}" (filtered from ${unmatchedWindows.length} candidates)`);
          }
          
          if (unmatchedWindows.length > validUnmatchedWindows.length) {
            console.log(`🛡️ DUMMY PREVENTION: Filtered out ${unmatchedWindows.length - validUnmatchedWindows.length} potential dummy windows for room "${roomName}"`);
          }
        } else {
          console.log(`🛡️ DUMMY PREVENTION: Skipped creating ${unmatchedWindows.length} unmatched windows for room "${roomName}" to prevent dummy entries (existing: ${existingWindows.length})`);
        }
      }
      
      console.log(`🪟 Updated ${Math.min(existingWindows.length, allWindowsToProcess.length)} existing windows for room "${roomName}"`);
        } else if (existingWindows.length === 0 && allWindowsToProcess.length > 0 && !isManualSync) {
      // Only create new windows if none exist AND this is not a manual sync
      allWindowsToProcess.forEach((window, index) => {
        const mappedOrientation = (window.orientation && window.orientation !== 'Unknown') ? mapFloorPlanOrientationToSiteNotes(window.orientation) : '';
        console.log(`🪟 Creating window ${index + 1} for room "${roomName}" - Original orientation: "${window.orientation}", Mapped: "${mappedOrientation}"`);
        
        existingWindows.push({
          id: index + 1,
          width: window.width ? (window.width / 1000).toFixed(2) : '', // Convert mm to meters, may be empty for windows without dimensions
          height: window.height ? (window.height / 1000).toFixed(2) : '', // Convert mm to meters, may be empty for windows without dimensions
          orientation: mappedOrientation, // Use actual window orientation, mapped to site notes format
          trickleVents: window.trickleVents || false, // Add trickle vents
          isDraughtProofed: window.isDraughtProofed || false,
          windowType: window.windowType || '',
          condition: (window as any).condition || '',
          glazingGap: (window as any).glazingGap || '',
          glazingType: (window as any).glazingType || '',
          windowCategory: (window as any).windowCategory || '',
          // 🔧 POSITION-BASED MATCHING: Store position and wall ID for reliable future matching
          position: window.position ? { x: window.position.x, y: window.position.y } : undefined,
          wallId: window.wallId || undefined
        });
      });
      
      console.log(`🪟 Created ${allWindowsToProcess.length} new windows for room "${roomName}" (initial auto-sync)`);
    } else if (existingWindows.length === 0 && allWindowsToProcess.length > 0 && isManualSync) {
      console.log(`🪟 Manual sync: Skipping creation of ${allWindowsToProcess.length} windows for room "${roomName}" - manual sync should not create new elements`);
    }
    
    targetRoom.windows = existingWindows;
    
    // 🔧 ROOMSURVEY COMPATIBILITY: Sync windowDetails with windows array for RoomSurvey form
    // RoomSurvey loads from windowDetails, so we need to keep it in sync with windows array
    (targetRoom as any).windowDetails = existingWindows.map((window: any) => ({
      id: window.id,
      width: window.width || '',
      height: window.height || '',
      trickleVents: window.trickleVents !== undefined ? window.trickleVents : 0,
      isDraughtProofed: window.isDraughtProofed || false,
      windowType: window.windowType || '',
      condition: window.condition || '',
      orientation: window.orientation || '',
      glazingGap: window.glazingGap || '',
      glazingType: window.glazingType || '',
      photos: window.photos || []
    }));
    
    // 🔧 UI COMPATIBILITY: Aggregate glazing data from windows array to room-level fields
    // The UI expects glazing data in room-level fields, not just in individual window objects
    if (existingWindows.length > 0) {
      // Find the first window with glazing data to use as representative values
      const representativeWindow = existingWindows.find((w: any) => 
        w.glazingGap || w.glazingType || w.windowCategory
      );
      
      if (representativeWindow) {
        // Store representative glazing values in room-level fields for UI compatibility
        // 🔧 PRESERVE EXISTING DATA: Only update if representative window has data AND room doesn't have existing data
        targetRoom.windowGlazingGap = (targetRoom as any).windowGlazingGap || representativeWindow.glazingGap || '';
        targetRoom.windowGlazingType = (targetRoom as any).windowGlazingType || representativeWindow.glazingType || '';
        targetRoom.windowCategory = (targetRoom as any).windowCategory || representativeWindow.windowCategory || '';
        targetRoom.windowType = (targetRoom as any).windowType || representativeWindow.windowType || '';
        
        // 🔧 INDIVIDUAL WINDOW SYNC: Ensure ALL window objects have the same glazing data
        // This fixes the issue where window modal shows empty fields
        existingWindows.forEach((window: any, windowIndex: number) => {
          console.log(`🔍 WINDOW ${windowIndex + 1} DEBUG: Before sync - glazingGap="${window.glazingGap}", glazingType="${window.glazingType}"`);
          
          // Only populate if the field is currently empty (check for both undefined/null and empty string)
          if ((!window.glazingGap || window.glazingGap.trim() === '') && representativeWindow.glazingGap) {
            window.glazingGap = representativeWindow.glazingGap;
            console.log(`🔧 Populated window ${windowIndex + 1} glazingGap with "${representativeWindow.glazingGap}"`);
          }
          if ((!window.glazingType || window.glazingType.trim() === '') && representativeWindow.glazingType) {
            window.glazingType = representativeWindow.glazingType;
            console.log(`🔧 Populated window ${windowIndex + 1} glazingType with "${representativeWindow.glazingType}"`);
          }
          if ((!window.windowCategory || window.windowCategory.trim() === '') && representativeWindow.windowCategory) {
            window.windowCategory = representativeWindow.windowCategory;
            console.log(`🔧 Populated window ${windowIndex + 1} windowCategory with "${representativeWindow.windowCategory}"`);
          }
          if ((!window.windowType || window.windowType.trim() === '') && representativeWindow.windowType) {
            window.windowType = representativeWindow.windowType;
            console.log(`🔧 Populated window ${windowIndex + 1} windowType with "${representativeWindow.windowType}"`);
          }
          
          console.log(`🔍 WINDOW ${windowIndex + 1} DEBUG: After sync - glazingGap="${window.glazingGap}", glazingType="${window.glazingType}"`);
        });
        
        console.log(`🪟 UI COMPATIBILITY: Stored representative glazing data for room "${roomName}": Gap="${targetRoom.windowGlazingGap}", Type="${targetRoom.windowGlazingType}", Category="${targetRoom.windowCategory}"`);
        console.log(`🪟 INDIVIDUAL WINDOW SYNC: Populated ${existingWindows.length} window objects with glazing data`);
      }
    }
    
    console.log(`🪟 Multiple windows (${allWindowsToProcess.length}) updated for room "${roomName}" on floor ${floorNumber} (${windowsWithDimensions.length} with dimensions, ${windowsWithoutDimensions.length} without)`);
  }
  
  // 🔧 ROOMSURVEY COMPATIBILITY: Sync windowDetails with windows array
  // This ensures RoomSurvey loads the correct data including condition field
  if (targetRoom.windows && targetRoom.windows.length > 0) {
    (targetRoom as any).windowDetails = targetRoom.windows.map((window: any) => ({
      id: window.id,
      width: window.width || '',
      height: window.height || '',
      trickleVents: window.trickleVents !== undefined ? window.trickleVents : 0,
      isDraughtProofed: window.isDraughtProofed || false,
      windowType: window.windowType || '',
      condition: window.condition || '',
      orientation: window.orientation || '',
      glazingGap: window.glazingGap || '',
      glazingType: window.glazingType || '',
      photos: window.photos || []
    }));
    console.log(`🪟 ROOMSURVEY SYNC: windowDetails synced for room "${roomName}":`, {
      windowDetailsLength: (targetRoom as any).windowDetails.length,
      conditions: (targetRoom as any).windowDetails.map((w: any) => w.condition),
      windowTypes: (targetRoom as any).windowDetails.map((w: any) => w.windowType)
    });
  } else {
    (targetRoom as any).windowDetails = [];
  }

  return updatedSiteNotesData;
};

/**
 * Updates radiator dimensions and type for a specific room in surveyor data
 */
export const updateRoomRadiatorDimensions = (
  roomName: string,
  width: number,
  height: number,
  radiatorType: string,
  siteNotesData: SiteNotesData,
  floorNumber: number = 1
): SiteNotesData => {
  // Clone the site notes data to avoid mutations
  const updatedSiteNotesData = JSON.parse(JSON.stringify(siteNotesData));

  // Find the target floor
  const targetFloor = updatedSiteNotesData.floors.find((f: SurveyorFloor) => f.id === floorNumber);
  if (!targetFloor) {
    console.warn(`Floor ${floorNumber} not found when updating radiator dimensions for room "${roomName}"`);
    return siteNotesData;
  }

  // Find the room by name
  const targetRoom = targetFloor.rooms.find((room: SurveyorRoom) => room.name === roomName);
  if (!targetRoom) {
    console.warn(`Room "${roomName}" not found when updating radiator dimensions`);
    return siteNotesData;
  }

  // Update radiator dimensions and type - if width or height is 0, clear the fields
  if (width === 0 && height === 0) {
    targetRoom.radiatorWidth = '';
    targetRoom.radiatorHeight = '';
    targetRoom.radiatorType = '';
    // Clear radiators array when clearing single radiator fields
    targetRoom.radiators = [];
    console.log(`🔥 Cleared radiator data for room "${roomName}"`);
  } else {
    targetRoom.radiatorWidth = width.toString();
    targetRoom.radiatorHeight = height.toString();
    targetRoom.radiatorType = radiatorType;
    // Clear radiators array when using single radiator fields to prevent duplicates
    targetRoom.radiators = [];
    // console.log(`🔥 Updated radiator for room "${roomName}": ${width}mm × ${height}mm (${radiatorType})`);
  }

  // Update the total radiator count across all floors
  const finalData = syncAllFloorsRadiatorsToHeatingSystem(updatedSiteNotesData);

  return finalData;
};

/**
 * Updates multiple radiator dimensions for a specific room in surveyor data
 * This replaces the single radiator fields and populates the radiators array
 */
export const updateRoomMultipleRadiatorDimensions = (
  roomName: string,
  radiators: RadiatorElement[],
  siteNotesData: SiteNotesData,
  floorNumber: number = 1,
  isManualSync: boolean = false
): SiteNotesData => {
  // 🧹 DEDUP PROTECTION: Check if this room was recently synced
  const roomKey = `${roomName}_${floorNumber}`;
  const now = Date.now();
  const lastSyncTime = radiatorSyncState.syncedRooms.get(roomKey) || 0;
  
  if (!isManualSync && now - lastSyncTime < 1000) {
    console.log(`🧹 DEDUP: Skipping multiple radiator sync for room "${roomName}" - recently synced ${now - lastSyncTime}ms ago`);
    return siteNotesData;
  }
  
  // Update sync timestamp
  radiatorSyncState.syncedRooms.set(roomKey, now);

  // Clone the site notes data to avoid mutations
  const updatedSiteNotesData = JSON.parse(JSON.stringify(siteNotesData));

  // Find the target floor
  const targetFloor = updatedSiteNotesData.floors.find((f: SurveyorFloor) => f.id === floorNumber);
  if (!targetFloor) {
    console.warn(`Floor ${floorNumber} not found when updating multiple radiator dimensions for room "${roomName}"`);
    return siteNotesData;
  }

  // Find the room by name
  const targetRoom = targetFloor.rooms.find((room: SurveyorRoom) => room.name === roomName);
  if (!targetRoom) {
    console.warn(`Room "${roomName}" not found when updating multiple radiator dimensions`);
    return siteNotesData;
  }

  // Filter radiators that belong to this room and this floor (include all, even without dimensions)
  const roomRadiators = radiators.filter(radiator => {
    const belongsToThisFloor = radiator.floor === floorNumber;
    
    if (!belongsToThisFloor) {
      console.log(`🔥 Filtering out radiator from floor ${radiator.floor} (target floor: ${floorNumber}) for room "${roomName}"`);
    }
    
    return belongsToThisFloor;
  });
  
  // Separate radiators with and without dimensions for logging
  const radiatorsWithDimensions = roomRadiators.filter(r => r.width && r.height);
  const radiatorsWithoutDimensions = roomRadiators.filter(r => !r.width || !r.height);

  console.log(`🔥 Updating radiators for room "${roomName}" on floor ${floorNumber}:`, {
    totalRadiatorsProvided: radiators.length,
    radiatorsAfterFloorFilter: roomRadiators.length,
    radiatorsWithDimensions: radiatorsWithDimensions.length,
    radiatorsWithoutDimensions: radiatorsWithoutDimensions.length,
    radiatorFloors: radiators.map(r => r.floor),
    targetFloor: floorNumber
  });

  if (roomRadiators.length === 0) {
    // Clear all radiator data if no radiators
    targetRoom.radiatorWidth = '';
    targetRoom.radiatorHeight = '';
    targetRoom.radiatorType = '';
    targetRoom.radiators = [];
    console.log(`🔥 Cleared all radiator data for room "${roomName}" on floor ${floorNumber}`);
  } else if (roomRadiators.length === 1) {
    // Single radiator - use the single fields for backward compatibility
    const radiator = roomRadiators[0];
    targetRoom.radiatorWidth = radiator.width?.toString() || '';
    targetRoom.radiatorHeight = radiator.height?.toString() || '';
    targetRoom.radiatorType = radiator.radiatorType || '';
    targetRoom.radiators = []; // Clear array since we're using single fields
    console.log(`🔥 Single radiator updated for room "${roomName}" on floor ${floorNumber}: ${radiator.width}mm × ${radiator.height}mm (${radiator.radiatorType})`);
  } else {
    // Multiple radiators - clear single fields and use array
    targetRoom.radiatorWidth = '';
    targetRoom.radiatorHeight = '';
    targetRoom.radiatorType = '';
    
    // Update radiators array - only update dimensions, don't create new radiators during manual sync
    const existingRadiators = targetRoom.radiators || [];
    
    // 🧹 DEDUPLICATION FIX: Ensure we only have the correct number of radiators
    if (existingRadiators.length > roomRadiators.length) {
      // Remove excess radiators if we have more existing than floor plan
      existingRadiators.splice(roomRadiators.length);
      console.log(`🧹 DEDUP: Removed ${existingRadiators.length - roomRadiators.length} excess radiators from room "${roomName}"`);
    }
    
    // If there are existing radiators, update their dimensions based on floor plan data
    if (existingRadiators.length > 0 && roomRadiators.length > 0) {
      // Update existing radiators with new dimensions from floor plan
      existingRadiators.forEach((existingRadiator: any, index: number) => {
        if (index < roomRadiators.length) {
          const floorPlanRadiator = roomRadiators[index];
          existingRadiator.width = floorPlanRadiator.width?.toString() || existingRadiator.width;
          existingRadiator.height = floorPlanRadiator.height?.toString() || existingRadiator.height;
          existingRadiator.type = floorPlanRadiator.radiatorType || existingRadiator.type;
        }
      });
      
      // Add missing radiators if floor plan has more than existing
      while (existingRadiators.length < roomRadiators.length) {
        const missingIndex = existingRadiators.length;
        const floorPlanRadiator = roomRadiators[missingIndex];
        existingRadiators.push({
          id: missingIndex + 1,
          width: floorPlanRadiator.width?.toString() || '',
          height: floorPlanRadiator.height?.toString() || '',
          type: floorPlanRadiator.radiatorType || ''
        });
      }
      
      console.log(`🔥 Updated ${existingRadiators.length} radiators for room "${roomName}" (${roomRadiators.length} from floor plan)`);
    } else if (existingRadiators.length === 0 && roomRadiators.length > 0) {
      // Create new radiators for room (both auto-sync and manual sync should create missing radiators)
      roomRadiators.forEach((radiator, index) => {
        existingRadiators.push({
          id: index + 1,
          width: radiator.width?.toString() || '',
          height: radiator.height?.toString() || '',
          type: radiator.radiatorType || ''
        });
      });
      
      const radiatorsWithoutDims = roomRadiators.filter(r => !r.width || !r.height).length;
      const syncType = isManualSync ? 'manual sync' : 'auto-sync';
      console.log(`🔥 Created ${roomRadiators.length} new radiators for room "${roomName}" (${syncType})${radiatorsWithoutDims > 0 ? ` - ${radiatorsWithoutDims} without dimensions` : ''}`);
    }
    
    targetRoom.radiators = existingRadiators;
    
    console.log(`🔥 Multiple radiators (${roomRadiators.length}) updated for room "${roomName}" on floor ${floorNumber}`);
  }

  // Update the total radiator count across all floors
  const finalData = syncAllFloorsRadiatorsToHeatingSystem(updatedSiteNotesData);
  
  // Clean up old sync timestamps (older than 5 minutes)
  const fiveMinutesAgo = now - 5 * 60 * 1000;
  for (const [key, timestamp] of radiatorSyncState.syncedRooms.entries()) {
    if (timestamp < fiveMinutesAgo) {
      radiatorSyncState.syncedRooms.delete(key);
    }
  }

  return finalData;
};

/**
 * Syncs door undercut data with site notes doors & utilities section - ACROSS ALL FLOORS
 * This function counts doors from all floors for the overall property door count
 * Takes floor plans to properly find rooms by floor
 */
export const syncAllFloorsDoorUndercutsToSiteNotesWithFloorPlans = (
  allFloorDoors: DoorElement[],
  floorPlans: Record<number, Wall[]>,
  roomCustomLabels: Record<string, string>,
  siteNotesData: SiteNotesData
): SiteNotesData => {
  // Clone the site notes data to avoid mutations
  const updatedSiteNotesData = JSON.parse(JSON.stringify(siteNotesData));

  // Count total doors across ALL floors
  const totalDoors = allFloorDoors.length;
  
  // Find doors without undercuts and their room locations across all floors
  const doorsWithoutUndercuts: { door: DoorElement; roomName: string }[] = [];
  
  allFloorDoors.forEach(door => {
    // Only count doors that explicitly have hasUndercut set to false
    if (door.hasUndercut === false) {
      // Get the walls for the door's specific floor
      const floorWalls = floorPlans[door.floor] || [];
      
      if (floorWalls.length > 0) {
        // Calculate rooms for this specific floor using memoized function
        const roomsOnThisFloor = memoizedCalculateRooms(floorWalls, roomCustomLabels, 'labelPreservation', door.floor);
        
        // Use manually assigned roomId if available, otherwise fall back to auto-detection
        let roomContainingDoor;
        if (door.roomId) {
          // Find room by manually assigned roomId
          roomContainingDoor = roomsOnThisFloor.find(r => (r.stableId || r.id) === door.roomId);
        }
        
        // Fall back to auto-detection if no manual assignment or room not found
        if (!roomContainingDoor) {
          roomContainingDoor = findBestRoomForDoor(door.position, roomsOnThisFloor, door, floorWalls);
        }
        
        if (roomContainingDoor && roomContainingDoor.name) {
          doorsWithoutUndercuts.push({
            door,
            roomName: roomContainingDoor.name
          });
        }
      }
    }
  });

  // Update site notes data
  updatedSiteNotesData.formData.totalDoors = totalDoors.toString();
  updatedSiteNotesData.formData.doorsWithoutUndercuts = doorsWithoutUndercuts.length.toString();
  
  // Create location string for doors without undercuts
  // Since room names should be unique across floors, we don't need floor numbers
  const roomNames = doorsWithoutUndercuts.map(item => item.roomName);
  const uniqueRoomNames = Array.from(new Set(roomNames)); // Remove duplicates
  updatedSiteNotesData.formData.doorsWithoutUndercutsLocation = uniqueRoomNames.join(', ');

  // console.log(`🚪 Door undercuts synced across all floors to site notes (with floor plans):`, {
  //   totalDoors,
  //   doorsWithoutUndercuts: doorsWithoutUndercuts.length,
  //   floorsWithDoors: Array.from(new Set(allFloorDoors.map(d => d.floor))),
  //   uniqueRoomNames,
  //   locationString: updatedSiteNotesData.formData.doorsWithoutUndercutsLocation
  // });

  return updatedSiteNotesData;
};

/**
 * Syncs door data with site notes and room details
 * Updates doorPresent status and doorUndercutSize for each room
 */
export const syncDoorUndercutsToSiteNotes = (
  doors: DoorElement[],
  rooms: Room[],
  siteNotesData: SiteNotesData,
  floorNumber: number = 1,
  walls?: Wall[]
): SiteNotesData => {
  // Clone the site notes data to avoid mutations
  const updatedSiteNotesData = JSON.parse(JSON.stringify(siteNotesData));

  // Filter doors for the current floor
  const currentFloorDoors = doors.filter(door => door.floor === floorNumber);
  
  // Count total doors
  const totalDoors = currentFloorDoors.length;
  
  // Track rooms with doors and their undercut sizes
  const roomDoorMap = new Map<string, { hasDoor: boolean; undercutSize?: string }>();
  
  currentFloorDoors.forEach(door => {
    // Use manually assigned roomId if available, otherwise fall back to auto-detection
    let roomContainingDoor;
    if (door.roomId) {
      // Find room by manually assigned roomId
      roomContainingDoor = rooms.find(r => (r.stableId || r.id) === door.roomId);
      if (roomContainingDoor) {
        console.log(`🚪 Using manually assigned room for door: ${roomContainingDoor.name}`);
      }
    }
    
    // Fall back to auto-detection if no manual assignment or room not found
    if (!roomContainingDoor) {
      roomContainingDoor = findBestRoomForDoor(door.position, rooms, door, walls || []);
      if (roomContainingDoor) {
        console.log(`🚪 Using auto-detected room for door: ${roomContainingDoor.name}`);
      }
    }
    
    if (roomContainingDoor && roomContainingDoor.name) {
      // Determine undercut size from door.hasUndercut
      // hasUndercut: true = "More Than 10mm", false = "Less than 10mm"
      const undercutSize = door.hasUndercut === true ? 'More Than 10mm' : 
                          door.hasUndercut === false ? 'Less than 10mm' : 
                          undefined;
      
      roomDoorMap.set(roomContainingDoor.name, {
        hasDoor: true,
        undercutSize
      });
    }
  });

  // Update room details in site notes with door information
  if (updatedSiteNotesData.room_details?.rooms) {
    updatedSiteNotesData.room_details.rooms = updatedSiteNotesData.room_details.rooms.map((room: any) => {
      const doorInfo = roomDoorMap.get(room.name);
      
      if (doorInfo) {
        // Room has a door
        return {
          ...room,
          doorPresent: true,
          doorUndercutSize: doorInfo.undercutSize || ''
        };
      } else {
        // Room has no door - keep existing state if already set, otherwise set to false
        const existingDoorPresent = room.doorPresent ?? room.doorUndercutsPresent;
        return {
          ...room,
          doorPresent: existingDoorPresent === null ? null : (existingDoorPresent ?? false),
          doorUndercutSize: ''
        };
      }
    });
  }

  // Count doors without undercuts (less than 10mm)
  const doorsWithoutUndercuts = Array.from(roomDoorMap.entries())
    .filter(([_, info]) => info.undercutSize === 'Less than 10mm')
    .map(([roomName]) => roomName);

  // Update site notes data
  updatedSiteNotesData.formData.totalDoors = totalDoors.toString();
  updatedSiteNotesData.formData.doorsWithoutUndercuts = doorsWithoutUndercuts.length.toString();
  updatedSiteNotesData.formData.doorsWithoutUndercutsLocation = doorsWithoutUndercuts.join(', ');

  console.log(`🚪 Door data synced to site notes:`, {
    totalDoors,
    roomsWithDoors: roomDoorMap.size,
    doorsWithSmallUndercuts: doorsWithoutUndercuts.length,
    roomsWithSmallUndercuts: doorsWithoutUndercuts
  });

  return updatedSiteNotesData;
};

/**
 * Utility function to trigger door undercut sync from outside components
 * Useful for bulk updates or manual triggers
 */
export const triggerDoorUndercutSync = (
  doors: DoorElement[],
  rooms: Room[],
  setSiteNotesData: (data: SiteNotesData | ((prev: SiteNotesData) => SiteNotesData)) => void,
  floorNumber: number = 1,
  walls?: Wall[]
) => {
  setSiteNotesData(prevData => {
    return syncDoorUndercutsToSiteNotes(doors, rooms, prevData, floorNumber, walls);
  });
};

/**
 * Syncs window data with site notes windows section - ACROSS ALL FLOORS
 * This function counts windows from all floors for the overall property window count
 * Takes floor plans to properly find rooms by floor and populates room.windows arrays
 */
export const syncAllFloorsWindowsToSiteNotesWithFloorPlans = (
  allFloorWindows: WindowElement[],
  floorPlans: Record<number, Wall[]>,
  roomCustomLabels: Record<string, string>,
  siteNotesData: SiteNotesData
): SiteNotesData => {
  // Clone the site notes data to avoid mutations
  const updatedSiteNotesData = JSON.parse(JSON.stringify(siteNotesData));

  // Count total windows across ALL floors
  const totalWindows = allFloorWindows.length;
  
  // 🧹 CLEANUP STEP: Clear all existing window data first to avoid stale data
  // 🛡️ PROTECTION: Preserve manually entered dimensions during mass cleanup
  updatedSiteNotesData.floors.forEach((floor: any) => {
    if (floor.rooms) {
      floor.rooms.forEach((room: any) => {
        // Only clear dimensions if they don't have manual glazing data (indicating manual entry)
        const hasManualGlazingData = room.windowGlazingGap || room.windowGlazingType || room.windowCategory;
        if (!hasManualGlazingData) {
          room.windowWidth = '';
          room.windowHeight = '';
          room.windowTrickleVents = '';
          room.windowOrientation = '';
          room.windows = []; // Safe to clear windows array since no glazing data
          console.log(`🧹 MASS CLEANUP: Cleared dimensions and fields for room "${room.name}" (no manual glazing data)`);
        } else {
          console.log(`🛡️ MASS CLEANUP PROTECTION: Preserving manual window fields for room "${room.name}" (${room.windowWidth}m × ${room.windowHeight}m, orientation: ${room.windowOrientation || 'none'}, trickle vents: ${room.windowTrickleVents || 'none'}) - has glazing data`);
          console.log(`🛡️ GLAZING PROTECTION: Preserving windows array and glazing fields for room "${room.name}"`);
          console.log(`🛡️ DEBUG: Glazing data check - Gap: "${room.windowGlazingGap}", Type: "${room.windowGlazingType}", Category: "${(room as any).windowCategory}"`);
          // Don't clear trickle vents and orientation when glazing data exists
        }
      });
    }
  });
  
  // 🪟 POPULATE ROOM WINDOWS: Process all windows and add them to their respective rooms
  const windowsByFloorAndRoom: Record<number, Record<string, WindowElement[]>> = {};
  
  // Group windows by floor and room
  allFloorWindows.forEach(window => {
    const floorWalls = floorPlans[window.floor] || [];
    if (floorWalls.length > 0) {
      const roomsOnThisFloor = memoizedCalculateRooms(floorWalls, roomCustomLabels, 'labelPreservation', window.floor);
      const roomContainingWindow = findRoomAtPoint(window.position, roomsOnThisFloor);
      
      if (roomContainingWindow && roomContainingWindow.name) {
        if (!windowsByFloorAndRoom[window.floor]) {
          windowsByFloorAndRoom[window.floor] = {};
        }
        if (!windowsByFloorAndRoom[window.floor][roomContainingWindow.name]) {
          windowsByFloorAndRoom[window.floor][roomContainingWindow.name] = [];
        }
        windowsByFloorAndRoom[window.floor][roomContainingWindow.name].push(window);
      }
    }
  });
  
  // 🪟 POPULATE ROOM ARRAYS: Add windows to the room.windows arrays in site notes
  Object.entries(windowsByFloorAndRoom).forEach(([floorIdStr, roomsData]) => {
    const floorId = parseInt(floorIdStr, 10);
    const targetFloor = updatedSiteNotesData.floors.find((f: any) => f.id === floorId);
    
    if (targetFloor && targetFloor.rooms) {
      Object.entries(roomsData).forEach(([roomName, windows]) => {
        const targetRoom = targetFloor.rooms.find((r: any) => r.name === roomName);
        
        if (targetRoom) {
          // Convert FloorPlanCreator windows to SurveyorSiteNotes format
          targetRoom.windows = windows.map((window, index) => ({
            id: index + 1,
            width: window.width ? (window.width / 1000).toString() : '',
            height: window.height ? (window.height / 1000).toString() : '',
            orientation: window.orientation || '',
            trickleVents: window.trickleVents || false,
            isDraughtProofed: window.isDraughtProofed || false,
            windowType: window.windowType || ''
          }));
          
          // console.log(`🪟 Added ${windows.length} windows to room "${roomName}" on floor ${floorId}`);
        }
      });
    }
  });

  // Find windows missing trickle vents and their room locations across all floors
  const windowsMissingTrickleVents: { window: WindowElement; roomName: string }[] = [];
  
  allFloorWindows.forEach(window => {
    // Count windows that explicitly have trickleVents set to false
    if (window.trickleVents === 0 || window.trickleVents === undefined) {
      // Get the walls for the window's specific floor
      const floorWalls = floorPlans[window.floor] || [];
      
      if (floorWalls.length > 0) {
        // Calculate rooms for this specific floor using memoized function
        const roomsOnThisFloor = memoizedCalculateRooms(floorWalls, roomCustomLabels, 'labelPreservation', window.floor);
        
        // Find which room this window belongs to by checking window position
        const roomContainingWindow = findRoomAtPoint(window.position, roomsOnThisFloor);
        
        if (roomContainingWindow && roomContainingWindow.name) {
          windowsMissingTrickleVents.push({
            window,
            roomName: roomContainingWindow.name
          });
        }
      }
    }
  });

  // Update site notes data with cross-floor totals
  updatedSiteNotesData.formData.totalWindows = totalWindows.toString();
  updatedSiteNotesData.formData.windowsMissingTrickleVents = windowsMissingTrickleVents.length.toString();
  
  // Create location string for windows missing trickle vents
  // Since room names should be unique across floors, we don't need floor numbers
  const roomNames = windowsMissingTrickleVents.map(item => item.roomName);
  const uniqueRoomNames = Array.from(new Set(roomNames)); // Remove duplicates
  updatedSiteNotesData.formData.missingTrickleVentsLocation = uniqueRoomNames.join(', ');

  // console.log(`🪟 All-floors window data synced to site notes (with floor plans):`, {
  //   totalWindows,
  //   windowsMissingTrickleVents: windowsMissingTrickleVents.length,
  //   floorsWithWindows: Array.from(new Set(allFloorWindows.map(w => w.floor))),
  //   uniqueRoomNames,
  //   locationString: updatedSiteNotesData.formData.missingTrickleVentsLocation
  // });

  return updatedSiteNotesData;
};

/**
 * Syncs window data with site notes windows section - ACROSS ALL FLOORS
 * This function counts windows from all floors for the overall property window count
 */
export const syncAllFloorsWindowsToSiteNotes = (
  allFloorWindows: WindowElement[],
  allFloorRooms: Room[],
  siteNotesData: SiteNotesData
): SiteNotesData => {
  // Clone the site notes data to avoid mutations
  const updatedSiteNotesData = JSON.parse(JSON.stringify(siteNotesData));

  // Count total windows across ALL floors
  const totalWindows = allFloorWindows.length;
  
  // 🧹 CLEANUP STEP: Clear window data from rooms that don't have windows anymore (basic sync)
  const roomsWithWindowsByFloor: Record<number, Set<string>> = {};
  
  // Build a map of which rooms have windows on each floor
  allFloorWindows.forEach(window => {
    const roomContainingWindow = findRoomAtPoint(window.position, allFloorRooms);
    
    if (roomContainingWindow && roomContainingWindow.name) {
      if (!roomsWithWindowsByFloor[window.floor]) {
        roomsWithWindowsByFloor[window.floor] = new Set();
      }
      roomsWithWindowsByFloor[window.floor].add(roomContainingWindow.name);
    }
  });
  
  // Clear stale window data from rooms that don't have windows
  updatedSiteNotesData.floors.forEach((floor: any) => {
    const roomsWithWindows = roomsWithWindowsByFloor[floor.id] || new Set();
    
    if (floor.rooms) {
      floor.rooms.forEach((room: any) => {
        if (room.name && !roomsWithWindows.has(room.name)) {
          // This room has no windows in current sync - clear any stale window data
          const hasStaleWindowData = room.windowWidth || room.windowHeight || 
                                     room.windowTrickleVents || 
                                     (room.windows && room.windows.length > 0);
          
          if (hasStaleWindowData) {
            // 🛡️ PROTECTION: Don't clear manually entered dimensions during stale data cleanup
            const hasManualGlazingData = room.windowGlazingGap || room.windowGlazingType;
            if (!hasManualGlazingData) {
              console.log(`🧹 Clearing stale window data from room "${room.name}" on floor ${floor.id} (basic sync)`);
              room.windowWidth = '';
              room.windowHeight = '';
            } else {
              console.log(`🛡️ STALE CLEANUP PROTECTION: Preserving manual dimensions for room "${room.name}" (${room.windowWidth}m × ${room.windowHeight}m) - has glazing data`);
            }
            // Don't clear trickle vents and orientation when glazing data exists
            if (!hasManualGlazingData) {
              room.windowTrickleVents = '';
              room.windowOrientation = '';
            }
            room.windows = [];
          }
        }
      });
    }
  });
  
  // Find windows missing trickle vents and their room locations across all floors
  const windowsMissingTrickleVents: { window: WindowElement; roomName: string; floor: number }[] = [];
  
  allFloorWindows.forEach(window => {
    // Count windows that explicitly have trickleVents set to false or undefined
    if (window.trickleVents === 0 || window.trickleVents === undefined) {
      // Find which room this window belongs to by checking window position
      const roomContainingWindow = findRoomAtPoint(window.position, allFloorRooms);
      
      if (roomContainingWindow && roomContainingWindow.name) {
        windowsMissingTrickleVents.push({
          window,
          roomName: roomContainingWindow.name,
          floor: window.floor
        });
      }
    }
  });

  // Update site notes data with cross-floor totals
  updatedSiteNotesData.formData.totalWindows = totalWindows.toString();
  updatedSiteNotesData.formData.windowsMissingTrickleVents = windowsMissingTrickleVents.length.toString();
  
  // Create location string for windows missing trickle vents (include floor info if multi-floor)
  const locationEntries = windowsMissingTrickleVents.map(item => {
    const floors = Array.from(new Set(allFloorWindows.map(w => w.floor)));
    const hasMultipleFloors = floors.length > 1;
    return hasMultipleFloors ? `${item.roomName} (Floor ${item.floor})` : item.roomName;
  });
  const uniqueLocations = Array.from(new Set(locationEntries)); // Remove duplicates
  updatedSiteNotesData.formData.missingTrickleVentsLocation = uniqueLocations.join(', ');

  console.log(`🪟 All-floors window data synced to site notes:`, {
    totalWindows,
    windowsMissingTrickleVents: windowsMissingTrickleVents.length,
    floorsWithWindows: Array.from(new Set(allFloorWindows.map(w => w.floor))),
    locationsWithMissingTrickleVents: uniqueLocations
  });

  return updatedSiteNotesData;
};

/**
 * Syncs window data with site notes windows section - SINGLE FLOOR ONLY
 * This function is used for per-floor room dimension syncing
 */
export const syncWindowsToSiteNotes = (
  windows: WindowElement[],
  rooms: Room[],
  siteNotesData: SiteNotesData,
  floorNumber: number = 1
): SiteNotesData => {
  // This function now delegates to the all-floors version for overall counts
  // but could be used for floor-specific operations if needed
  return syncAllFloorsWindowsToSiteNotes(windows, rooms, siteNotesData);
};

/**
 * Smart window sync that automatically detects multiple windows per room
 * and uses the appropriate sync method (single vs multiple)
 */
export const smartSyncWindowsToRoom = (
  windows: WindowElement[],
  rooms: Room[],
  siteNotesData: SiteNotesData,
  floorNumber: number = 1,
  isManualSync: boolean = false
): SiteNotesData => {
  // Prevent duplicate syncing
  const currentTime = Date.now();
  if (windowSyncState.isInProgress && (currentTime - windowSyncState.lastSyncTimestamp) < 1000) {
    console.log('🪟 Window sync already in progress, skipping duplicate call');
    return siteNotesData;
  }

  windowSyncState.isInProgress = true;
  windowSyncState.lastSyncTimestamp = currentTime;
  windowSyncState.isManualSync = isManualSync;

  // Ensure all floor plan rooms exist in site notes data before element sync
  let updatedSiteNotesData = ensureRoomsExistInSiteNotes(rooms, siteNotesData, floorNumber);

  // Clear processed window IDs for this floor
  const floorWindowIds = Array.from(windowSyncState.processedWindowIds).filter(id => id.endsWith(`_${floorNumber}`));
  floorWindowIds.forEach(id => windowSyncState.processedWindowIds.delete(id));

  // 🔧 FLOOR ISOLATION FIX: Filter rooms to only include rooms from the current floor
  // This prevents windows on floor 2 from being incorrectly matched to rooms on floor 1
  const currentFloorRooms = rooms.filter(room => {
    // Extract floor from stable ID (format: floor_X_coordinates...)
    if (room.stableId && room.stableId.startsWith('floor_')) {
      const floorMatch = room.stableId.match(/^floor_(\d+)_/);
      if (floorMatch) {
        const roomFloor = parseInt(floorMatch[1], 10);
        return roomFloor === floorNumber;
      }
    }
    // Fallback: if no stable ID or floor info, assume it belongs to the current floor
    return true;
  });
  
  // 🪟 MIGRATION PROTECTION: Check if we have migration-preserved windows
  const migrationPreservedWindows = windows.filter(w => (w as any).migrationPreserved);
  const migrationRestoredWindows = windows.filter(w => (w as any).migrationRestored);
  const hasMigrationWindows = migrationPreservedWindows.length > 0 || migrationRestoredWindows.length > 0;
  
  if (hasMigrationWindows) {
    console.log(`🪟 [MIGRATION-SAFE] Processing ${windows.length} windows with ${migrationPreservedWindows.length} migration-preserved and ${migrationRestoredWindows.length} migration-restored windows on floor ${floorNumber}`);
  }
  
  // console.log(`🪟 Floor ${floorNumber} window sync: ${windows.length} windows, ${currentFloorRooms.length} rooms on this floor (filtered from ${rooms.length} total rooms)`);

  // Group windows by room using enhanced room detection
  const windowsByRoom: Record<string, WindowElement[]> = {};
  
  windows.forEach((window, index) => {
    // Create truly unique window ID - use window.id as primary key with position/dimension fallback
    // This ensures each window element gets its own unique ID, preventing identical windows from being flagged as duplicates
    const baseId = window.id || `fallback_${index}`;
    const windowId = `${baseId}_${window.position.x}_${window.position.y}_${window.width || 0}_${window.height || 0}_${floorNumber}`;
    
    console.log(`🪟 Processing window ${index + 1} (ID: ${windowId}) at (${window.position.x}, ${window.position.y}) on floor ${floorNumber}`);
    
    // 🪟 MIGRATION PROTECTION: Always process migration-preserved/restored windows, even if already processed
    const isMigrationWindow = (window as any).migrationPreserved || (window as any).migrationRestored;
    
    // 🔧 DUPLICATE WINDOW FIX: Only skip if this exact window instance was already processed
    // Multiple identical windows in the same room should all be processed
    if (windowSyncState.processedWindowIds.has(windowId) && !isManualSync && !isMigrationWindow) {
      console.log(`🪟 Skipping already processed window ${index + 1} (${windowId}) - use manual sync to force update`);
      return;
    }

    let roomContainingWindow: Room | null = null;
    
    // Detect window installation type for appropriate assignment logic
    const isRoofWindow = !window.wallId || (window as any).windowInstallationType === 'roof';
    
    if (isRoofWindow) {
      // Roof windows: Use coordinate-based detection (no wall attachment)
      roomContainingWindow = findElementRoomWithConsolidation(window.position, currentFloorRooms, 'window');
      if (roomContainingWindow) {
        console.log(`🪟 ✅ ROOF_WINDOW_SUCCESS: Roof window ${index + 1} assigned to room "${roomContainingWindow.name}" via coordinates (${window.position.x}, ${window.position.y}) on floor ${floorNumber}${isMigrationWindow ? ' [MIGRATION-PROTECTED]' : ''}`);
      } else {
        console.log(`🪟 ❌ ROOF_WINDOW_FAILED: Roof window ${index + 1} at (${window.position.x}, ${window.position.y}) could not be assigned via coordinates`);
      }
    } else if (window.wallId) {
      // Wall windows: Use wall-based assignment (most accurate for wall-mounted elements)
      roomContainingWindow = findRoomByWallId(window.wallId, currentFloorRooms, window.position);
      if (roomContainingWindow) {
        console.log(`🪟 ✅ WALL_ID_SUCCESS: Window ${index + 1} assigned to room "${roomContainingWindow.name}" via wall ID "${window.wallId}" on floor ${floorNumber}${isMigrationWindow ? ' [MIGRATION-PROTECTED]' : ''}`);
      } else {
        console.log(`🪟 ❌ WALL_ID_FAILED: Window ${index + 1} wall ID "${window.wallId}" lookup failed, trying fallback`);
        
        // Fallback: Use coordinate-based detection if wall-based fails
        roomContainingWindow = findElementRoomWithConsolidation(window.position, currentFloorRooms, 'window');
        if (roomContainingWindow) {
          console.log(`🪟 ⚠️ COORD_FALLBACK_SUCCESS: Window ${index + 1} assigned to room "${roomContainingWindow.name}" via coordinates (${window.position.x}, ${window.position.y}) on floor ${floorNumber} - wall placement may need review${isMigrationWindow ? ' [MIGRATION-PROTECTED]' : ''}`);
        } else {
          console.log(`🪟 ❌ COORD_FALLBACK_FAILED: Window ${index + 1} at (${window.position.x}, ${window.position.y}) could not be assigned via coordinates either`);
        }
      }
    } else {
      // Edge case: Window has wallId as null but windowInstallationType is not 'roof'
      console.log(`🪟 ❌ NULL_WALL_ID: Window ${index + 1} has null wallId, using coordinate fallback`);
      roomContainingWindow = findElementRoomWithConsolidation(window.position, currentFloorRooms, 'window');
      if (roomContainingWindow) {
        console.log(`🪟 ⚠️ NULL_WALL_FALLBACK_SUCCESS: Window ${index + 1} assigned to room "${roomContainingWindow.name}" via coordinates`);
      }
    }
    
    if (roomContainingWindow && roomContainingWindow.name && !/^Room \d+$/.test(roomContainingWindow.name)) {
      if (!windowsByRoom[roomContainingWindow.name]) {
        windowsByRoom[roomContainingWindow.name] = [];
        console.log(`🪟 📦 GROUPING: Created new window group for room "${roomContainingWindow.name}"`);
      }
      windowsByRoom[roomContainingWindow.name].push(window);
      console.log(`🪟 📦 GROUPING: Added window ${index + 1} (${windowId}) to room "${roomContainingWindow.name}" group (now has ${windowsByRoom[roomContainingWindow.name].length} windows)`);
      windowSyncState.processedWindowIds.add(windowId);
    } else {
      // 🪟 MIGRATION PROTECTION: Don't fail migration windows, preserve them in site notes
      if (isMigrationWindow) {
        console.warn(`🪟 [MIGRATION-PROTECTED] Window ${index + 1} could not be assigned to a room but will be preserved in site notes for manual assignment`);
        
        // Create a special "unassigned" room entry for migration windows
        const unassignedRoomName = `Unassigned Windows (Floor ${floorNumber})`;
        if (!windowsByRoom[unassignedRoomName]) {
          windowsByRoom[unassignedRoomName] = [];
        }
        windowsByRoom[unassignedRoomName].push(window);
        windowSyncState.processedWindowIds.add(windowId);
      } else {
        console.warn(`🪟 Window ${index + 1} at (${window.position.x}, ${window.position.y}) could not be assigned to a valid room`);
        windowSyncState.processedWindowIds.add(windowId);
      }
    }
  });

  // 🪟 DEBUG: Show final window grouping results
  console.log(`🪟 📊 FINAL WINDOW GROUPING for floor ${floorNumber}:`);
  Object.entries(windowsByRoom).forEach(([roomName, roomWindows]) => {
    console.log(`🪟 📦 Room "${roomName}": ${roomWindows.length} windows`);
    roomWindows.forEach((w, i) => {
      console.log(`    🪟 Window ${i + 1}: pos(${w.position.x}, ${w.position.y}) wallId="${w.wallId || 'none'}" id="${w.id || 'none'}"`);
    });
  });

  // 🧹 CLEANUP STEP: Clear window data from rooms that don't have windows anymore
  const roomsWithWindows = new Set(Object.keys(windowsByRoom));
  const targetFloor = updatedSiteNotesData.floors.find((f: any) => f.id === floorNumber);
  
  if (targetFloor && targetFloor.rooms) {
    targetFloor.rooms.forEach((room: any) => {
      if (room.name && !roomsWithWindows.has(room.name)) {
        // This room has no windows in current sync - clear any stale window data
        const hasStaleWindowData = room.windowWidth || room.windowHeight || 
                                   room.windowTrickleVents || 
                                   (room.windows && room.windows.length > 0);
        
        if (hasStaleWindowData) {
          // 🛡️ PROTECTION: Don't clear manually entered dimensions during final stale cleanup
          const hasManualGlazingData = room.windowGlazingGap || room.windowGlazingType;
          if (!hasManualGlazingData) {
            console.log(`🧹 Clearing stale window data from room "${room.name}" on floor ${floorNumber}`);
            room.windowWidth = '';
            room.windowHeight = '';
          } else {
            console.log(`🛡️ FINAL CLEANUP PROTECTION: Preserving manual dimensions for room "${room.name}" (${room.windowWidth}m × ${room.windowHeight}m) - has glazing data`);
          }
          // Don't clear trickle vents and orientation when glazing data exists
          if (!hasManualGlazingData) {
            room.windowTrickleVents = '';
            room.windowOrientation = '';
          }
          room.windows = [];
        }
      }
    });
  }

  // Sync each room's windows using the appropriate method
  Object.entries(windowsByRoom).forEach(([roomName, roomWindows]) => {
    const windowsWithDimensions = roomWindows.filter(w => w.width > 0 && w.height > 0);
    const windowsWithoutDimensions = roomWindows.filter(w => !(w.width > 0 && w.height > 0));
    
    // 🔧 MULTIPLE WINDOW SUPPORT: Log detailed window info for debugging
    console.log(`🪟 MULTI-WINDOW: Room "${roomName}" has ${roomWindows.length} total windows (${windowsWithDimensions.length} with dimensions, ${windowsWithoutDimensions.length} without)`);
    
    // 🛡️ PER-ROOM GLAZING DATA PROTECTION: Check if this specific room has manual glazing data
    const targetFloorForProtection = updatedSiteNotesData.floors.find((f: any) => f.id === floorNumber);
    
    // 🔍 DEBUG: Protection check for room K
    if (roomName === 'K') {
      console.log(`🔍 PROTECTION DEBUG: Room "K" protection check`);
      console.log(`🔍 floorNumber: ${floorNumber}`);
      console.log(`🔍 targetFloorForProtection exists: ${!!targetFloorForProtection}`);
      if (targetFloorForProtection) {
        console.log(`🔍 Floor has rooms: ${!!targetFloorForProtection.rooms}`);
        console.log(`🔍 Room count in floor: ${targetFloorForProtection.rooms?.length || 0}`);
      } else {
        console.log(`🔍 ❌ No floor found for protection check`);
      }
    }
    
    const roomWithGlazingData = targetFloorForProtection?.rooms?.find((r: any) => 
      r.name === roomName && (r.windowGlazingGap || r.windowGlazingType || r.windowCategory)
    );
    
    const isProtectedRoom = roomWithGlazingData && !windowSyncState.isManualSync;
    
    if (isProtectedRoom) {
      console.log(`🛡️ PER-ROOM PROTECTION: Room "${roomName}" has manual glazing data (Gap: ${roomWithGlazingData.windowGlazingGap}, Type: ${roomWithGlazingData.windowGlazingType}, Category: ${roomWithGlazingData.windowCategory})`);
      console.log(`🛡️ Processing room "${roomName}" but protecting glazing data from overwrites`);
      
      // 🔧 PROTECTED ROOM HANDLING: Ensure room appears in SurveyorSiteNotes with existing data
      // For rooms with multiple windows, we need to ensure the windows array is properly populated
      if (roomWindows.length > 1) {
        console.log(`🛡️ MULTI-WINDOW PROTECTED: Ensuring room "${roomName}" has proper windows array for UI`);
        updatedSiteNotesData = updateRoomMultipleWindowDimensions(roomName, roomWindows, updatedSiteNotesData, floorNumber, false, true); // false = not manual sync, true = protected mode
      }
      return; // Skip dimension updates but ensure room is processed
    }
    
    // 🔧 ENHANCED WINDOW SYNC: Handle windows without dimensions gracefully
    if (windowsWithoutDimensions.length > 0) {
      console.log(`🪟 Found ${windowsWithoutDimensions.length} windows without dimensions in room "${roomName}" - preserving in site notes for user input`);
    }
    
    // 🪟 MIGRATION PROTECTION: Special handling for unassigned migration windows
    if (roomName.startsWith('Unassigned Windows')) {
      console.log(`🪟 [MIGRATION-PROTECTED] Preserving ${roomWindows.length} unassigned migration windows in site notes for manual assignment`);
      
      // For now, we'll preserve these windows in site notes as placeholders
      // The user can manually assign them to rooms later
      updatedSiteNotesData = ensureWindowPlaceholdersExist(roomName, roomWindows, updatedSiteNotesData, floorNumber);
      return; // Skip normal processing for unassigned windows
    }
    
    if (windowsWithDimensions.length === 0 && windowsWithoutDimensions.length === 0) {
      // No windows at all - clear data
      updatedSiteNotesData = updateRoomMultipleWindowDimensions(roomName, [], updatedSiteNotesData, floorNumber);
    } else if (windowsWithDimensions.length === 0 && windowsWithoutDimensions.length > 0) {
      // Only windows without dimensions - preserve existing site notes data but ensure windows array exists
      console.log(`🪟 Preserving existing window data for room "${roomName}" - ${windowsWithoutDimensions.length} windows need dimensions`);
      // Don't clear data, just ensure the room has placeholder windows for user input
      updatedSiteNotesData = ensureWindowPlaceholdersExist(roomName, windowsWithoutDimensions, updatedSiteNotesData, floorNumber);
    } else if (windowsWithDimensions.length === 1 && windowsWithoutDimensions.length === 0) {
      // Single window with dimensions - use single field method for backward compatibility
      const window = windowsWithDimensions[0];
      updatedSiteNotesData = updateRoomWindowDimensions(
        roomName, 
        window.width || 0, 
        window.height || 0, 
        typeof window.trickleVents === 'number' ? window.trickleVents : (window.trickleVents ? 1 : 0), 
        updatedSiteNotesData, 
        floorNumber,
        window.orientation, // Add window orientation to smart sync
        (window as any).isDraughtProofed, // Add draught proofed
        (window as any).windowType || (window as any).type, // Add window type
        (window as any).glazingGap, // Add glazing gap
        (window as any).glazingType, // Add glazing type
        (window as any).windowCategory, // Add window category
        (window as any).condition, // Add window condition
        window.displayId // Add sequential window ID
      );
    } else {
      // Multiple windows or mix of windows with/without dimensions - use array method
      // Include all windows (with and without dimensions) to preserve placeholders
      const allWindows = [...windowsWithDimensions, ...windowsWithoutDimensions];
      updatedSiteNotesData = updateRoomMultipleWindowDimensions(roomName, allWindows, updatedSiteNotesData, floorNumber, windowSyncState.isManualSync);
    }
  });

  windowSyncState.isInProgress = false;
  windowSyncState.isManualSync = false;
  
  // Report auto-sync success rate
  const totalWindows = windows.length;
  const processedWindows = Object.values(windowsByRoom).flat().length;
  const successRate = totalWindows > 0 ? Math.round((processedWindows / totalWindows) * 100) : 0;
  
  // console.log(`🪟 Floor ${floorNumber} sync completed: ${processedWindows}/${totalWindows} windows synced (${successRate}% success rate)${hasMigrationWindows ? ' [MIGRATION-PROTECTED]' : ''}`);
  
  // 🪟 MIGRATION FEEDBACK: Log special migration window handling
  if (hasMigrationWindows) {
    const preservedWindows = Object.values(windowsByRoom).flat().filter(w => (w as any).migrationPreserved).length;
    const restoredWindows = Object.values(windowsByRoom).flat().filter(w => (w as any).migrationRestored).length;
    console.log(`🪟 [MIGRATION-PROTECTED] Successfully synced ${preservedWindows} preserved and ${restoredWindows} restored windows`);
  }

  return updatedSiteNotesData;
};

/**
 * Smart radiator sync that automatically detects multiple radiators per room
 * and uses the appropriate sync method (single vs multiple)
 */
export const smartSyncRadiatorsToRoom = (
  radiators: RadiatorElement[],
  rooms: Room[],
  siteNotesData: SiteNotesData,
  floorNumber: number = 1,
  isManualSync: boolean = false
): SiteNotesData => {
  // Prevent duplicate syncing
  const currentTime = Date.now();
  if (radiatorSyncState.isInProgress && (currentTime - radiatorSyncState.lastSyncTimestamp) < 1000) {
    console.log('🔥 Radiator sync already in progress, skipping duplicate call');
    return siteNotesData;
  }

  radiatorSyncState.isInProgress = true;
  radiatorSyncState.lastSyncTimestamp = currentTime;
  radiatorSyncState.isManualSync = isManualSync;

  // Ensure all floor plan rooms exist in site notes data before element sync
  let updatedSiteNotesData = ensureRoomsExistInSiteNotes(rooms, siteNotesData, floorNumber);

  // Clear processed radiator IDs for this floor
  const floorRadiatorIds = Array.from(radiatorSyncState.processedRadiatorIds).filter(id => id.endsWith(`_${floorNumber}`));
  floorRadiatorIds.forEach(id => radiatorSyncState.processedRadiatorIds.delete(id));

  // 🔧 FLOOR ISOLATION FIX: Filter rooms to only include rooms from the current floor
  // This prevents radiators on floor 2 from being incorrectly matched to rooms on floor 1
  const currentFloorRooms = rooms.filter(room => {
    // Extract floor from stable ID (format: floor_X_coordinates...)
    if (room.stableId && room.stableId.startsWith('floor_')) {
      const floorMatch = room.stableId.match(/^floor_(\d+)_/);
      if (floorMatch) {
        const roomFloor = parseInt(floorMatch[1], 10);
        return roomFloor === floorNumber;
      }
    }
    // Fallback: if no stable ID or floor info, assume it belongs to the current floor
    return true;
  });
  
  console.log(`🔥 Floor ${floorNumber} radiator sync: ${radiators.length} radiators, ${currentFloorRooms.length} rooms on this floor (filtered from ${rooms.length} total rooms)`);
  console.log(`🔥 DEBUG: All radiators being processed:`, radiators.map((r, i) => `${i+1}: (${r.position.x.toFixed(1)}, ${r.position.y.toFixed(1)}) floor=${r.floor || 'undefined'}`));

  // Group radiators by room using enhanced room detection
  const radiatorsByRoom: Record<string, RadiatorElement[]> = {};
  
  // Track processed radiators in this sync run to prevent duplicates
  const processedInThisRun = new Set<string>();
  
  radiators.forEach((radiator, index) => {
    // Create unique radiator ID with more precision
    const radiatorId = `${radiator.position.x.toFixed(2)}_${radiator.position.y.toFixed(2)}_${radiator.width || 0}_${radiator.height || 0}_${floorNumber}`;
    
    // Skip if already processed in this run OR in previous runs
    if (processedInThisRun.has(radiatorId) || radiatorSyncState.processedRadiatorIds.has(radiatorId)) {
      console.log(`🔥 DUPLICATE_SKIP: Radiator ${index + 1} at (${radiator.position.x.toFixed(1)}, ${radiator.position.y.toFixed(1)}) already processed`);
      return;
    }
    
    // Mark as processed in this run immediately
    processedInThisRun.add(radiatorId);

    // Use enhanced radiator detection that handles all scenarios including split walls
    const roomContainingRadiator = findRoomContainingRadiatorText(radiator, currentFloorRooms);
    
    if (roomContainingRadiator) {
      console.log(`🔥 ✅ ENHANCED_DETECTION: Radiator ${index + 1} assigned to room "${roomContainingRadiator.name}" at (${radiator.position.x.toFixed(1)}, ${radiator.position.y.toFixed(1)}) on floor ${floorNumber}`);
      
      // Additional check to prevent room assignment duplication
      if (roomContainingRadiator.name && !/^Room \d+$/.test(roomContainingRadiator.name)) {
        if (!radiatorsByRoom[roomContainingRadiator.name]) {
          radiatorsByRoom[roomContainingRadiator.name] = [];
        }
        
        // Check if this exact radiator is already in the room's list
        const alreadyInRoom = radiatorsByRoom[roomContainingRadiator.name].some(existingRad => 
          Math.abs(existingRad.position.x - radiator.position.x) < 0.1 && 
          Math.abs(existingRad.position.y - radiator.position.y) < 0.1
        );
        
        if (!alreadyInRoom) {
          radiatorsByRoom[roomContainingRadiator.name].push(radiator);
          console.log(`🔥 ADDED_TO_ROOM: Radiator added to "${roomContainingRadiator.name}" (total: ${radiatorsByRoom[roomContainingRadiator.name].length})`);
        } else {
          console.log(`🔥 DUPLICATE_IN_ROOM: Radiator already exists in "${roomContainingRadiator.name}", skipping`);
        }
        
        radiatorSyncState.processedRadiatorIds.add(radiatorId);
      }
    } else {
      console.warn(`🔥 ❌ DETECTION_FAILED: Radiator ${index + 1} at (${radiator.position.x.toFixed(1)}, ${radiator.position.y.toFixed(1)}) could not be assigned to any room on floor ${floorNumber}`);
      radiatorSyncState.processedRadiatorIds.add(radiatorId);
    }
  });

  // 🧹 STEP 1: Clear radiator data from ALL rooms on this floor that don't have current radiators
  // This ensures that when radiators are deleted, the room data is properly cleared
  const roomsWithCurrentRadiators = new Set(Object.keys(radiatorsByRoom));
  
  currentFloorRooms.forEach(room => {
    if (room.name && !roomsWithCurrentRadiators.has(room.name) && !/^Room \d+$/.test(room.name)) {
      // This room has no current radiators - clear any old radiator data
      updatedSiteNotesData = updateRoomMultipleRadiatorDimensions(room.name, [], updatedSiteNotesData, floorNumber, true);
      console.log(`🧹 Cleared old radiator data from room "${room.name}" (no current radiators on floor ${floorNumber})`);
    }
  });

  // 🔥 STEP 2: Sync each room's current radiators using the appropriate method
  Object.entries(radiatorsByRoom).forEach(([roomName, roomRadiators]) => {
    const radiatorsWithDimensions = roomRadiators.filter(r => r.width && r.height);
    const radiatorsWithoutDimensions = roomRadiators.filter(r => !r.width || !r.height);
    
    if (roomRadiators.length === 0) {
      // No radiators at all - clear data (redundant with step 1, but kept for safety)
      updatedSiteNotesData = updateRoomMultipleRadiatorDimensions(roomName, [], updatedSiteNotesData, floorNumber);
    } else if (roomRadiators.length === 1) {
      // Single radiator - use single field method for backward compatibility
      const radiator = roomRadiators[0];
      updatedSiteNotesData = updateRoomRadiatorDimensions(
        roomName, 
        radiator.width || 0, 
        radiator.height || 0, 
        radiator.radiatorType || '', 
        updatedSiteNotesData, 
        floorNumber
      );
      
      // Log if radiator has no dimensions
      if (!radiator.width || !radiator.height) {
        console.log(`🔥 Added radiator without dimensions to room "${roomName}" - user can add dimensions later in site notes`);
      }
    } else {
      // Multiple radiators - use array method (include all radiators, even without dimensions)
      updatedSiteNotesData = updateRoomMultipleRadiatorDimensions(roomName, roomRadiators, updatedSiteNotesData, floorNumber, radiatorSyncState.isManualSync);
      
      // Log summary of radiators with/without dimensions
      if (radiatorsWithoutDimensions.length > 0) {
        console.log(`🔥 Added ${radiatorsWithoutDimensions.length} radiator(s) without dimensions to room "${roomName}" - user can add dimensions later in site notes`);
      }
    }
  });

  // After syncing room radiators, update the total count
  updatedSiteNotesData = syncAllFloorsRadiatorsToHeatingSystem(updatedSiteNotesData);

  radiatorSyncState.isInProgress = false;
  radiatorSyncState.isManualSync = false;
  
  // Report auto-sync success rate
  const totalRadiators = radiators.length;
  const autoSyncedRadiators = Object.values(radiatorsByRoom).flat().filter(r => r.wallId).length;
  const fallbackRadiators = Object.values(radiatorsByRoom).flat().filter(r => !r.wallId).length;
  
  // console.log(`🔥 Radiator sync summary for floor ${floorNumber}:`, {
  //   total: totalRadiators,
  //   autoSynced: autoSyncedRadiators,
  //   fallback: fallbackRadiators,
  //   successRate: totalRadiators > 0 ? `${Math.round((autoSyncedRadiators / totalRadiators) * 100)}%` : '0%'
  // });

  return updatedSiteNotesData;
};

/**
 * Syncs light counts across ALL floors to the additional information section
 * This function aggregates light counts from all rooms across all floors
 */
export const syncAllFloorsLightsToAdditionalInfo = (
  siteNotesData: SiteNotesData
): SiteNotesData => {
  console.log(`🔍 AGGREGATION: Starting light aggregation across all floors`);
  console.log(`🔍 AGGREGATION: Current totals before aggregation - totalLightsX: ${siteNotesData.formData.totalLightsX}, totalLightsO: ${siteNotesData.formData.totalLightsO}`);
  
  // Clone the site notes data to avoid mutations
  const updatedSiteNotesData = JSON.parse(JSON.stringify(siteNotesData));

  let totalLowEnergyLights = 0;
  let totalNonLowEnergyLights = 0;
  const roomBreakdown: Array<{floorId: number, roomName: string, lowEnergy: number, nonLowEnergy: number}> = [];

  // Aggregate light counts from all floors and all rooms
  updatedSiteNotesData.floors.forEach((floor: SurveyorFloor, floorIndex: number) => {
    console.log(`🔍 AGGREGATION: Processing floor ${floor.id} (index ${floorIndex}) with ${floor.rooms.length} rooms`);
    
    floor.rooms.forEach((room: SurveyorRoom, roomIndex: number) => {
      const lowEnergyCount = parseInt(room.lowEnergyLights) || 0;
      const nonLowEnergyCount = parseInt(room.nonLowEnergyLights) || 0;
      
      // Track all rooms, even with 0 counts
      roomBreakdown.push({
        floorId: floor.id,
        roomName: room.name || 'Unnamed Room',
        lowEnergy: lowEnergyCount,
        nonLowEnergy: nonLowEnergyCount
      });
      
      if (lowEnergyCount > 0 || nonLowEnergyCount > 0) {
        console.log(`🔍 AGGREGATION: Room "${room.name}" (Floor ${floor.id}) has ${lowEnergyCount} low-energy, ${nonLowEnergyCount} non-low-energy lights`);
        console.log(`🔍 AGGREGATION: Raw values from room - lowEnergyLights: "${room.lowEnergyLights}", nonLowEnergyLights: "${room.nonLowEnergyLights}"`);
      }
      
      totalLowEnergyLights += lowEnergyCount;
      totalNonLowEnergyLights += nonLowEnergyCount;
    });
  });

  // Update the additional information section
  updatedSiteNotesData.formData.totalLightsX = totalLowEnergyLights.toString();
  updatedSiteNotesData.formData.totalLightsO = totalNonLowEnergyLights.toString();

  console.log(`🔧 UI_UPDATE_DEBUG: Updated formData with totals`, {
    before: { totalLightsX: siteNotesData.formData.totalLightsX, totalLightsO: siteNotesData.formData.totalLightsO },
    after: { totalLightsX: updatedSiteNotesData.formData.totalLightsX, totalLightsO: updatedSiteNotesData.formData.totalLightsO },
    calculated: { totalLowEnergyLights, totalNonLowEnergyLights }
  });
  console.log(`🔍 AGGREGATION: Detailed room breakdown:`, roomBreakdown);
  console.log(`🔍 AGGREGATION: Final totals - Low Energy: ${totalLowEnergyLights}, Non-Low Energy: ${totalNonLowEnergyLights}`);
  console.log(`🔍 AGGREGATION: Expected vs Actual - Expected: 3 low + 2 non-low, Got: ${totalLowEnergyLights} low + ${totalNonLowEnergyLights} non-low`);
  console.log(`💡 All-floors light counts synced to additional information:`, {
    totalLowEnergyLights,
    totalNonLowEnergyLights,
    floorsProcessed: updatedSiteNotesData.floors.length,
    totalRoomsProcessed: roomBreakdown.length
  });

  return updatedSiteNotesData;
};

/**
 * Syncs radiator counts across ALL floors to the heating system section
 * This function aggregates radiator counts from all rooms across all floors
 * and also counts radiators with TRVs automatically
 */
export const syncAllFloorsRadiatorsToHeatingSystem = (
  siteNotesData: SiteNotesData
): SiteNotesData => {
  // Clone the site notes data to avoid mutations
  const updatedSiteNotesData = JSON.parse(JSON.stringify(siteNotesData));

  let totalRadiators = 0;
  let totalRadiatorsWithTRVs = 0;

  // Aggregate radiator counts from all floors and all rooms
  updatedSiteNotesData.floors.forEach((floor: SurveyorFloor) => {
    floor.rooms.forEach((room: SurveyorRoom) => {
      // Count radiators in each room
      // If room has radiator data (even without dimensions), count as 1 radiator
      if (room.radiatorWidth || room.radiatorHeight || room.radiatorType) {
        totalRadiators += 1;
        
        // Check if this radiator has TRVs (only count if we have type info)
        if (room.radiatorType === 'with-trvs') {
          totalRadiatorsWithTRVs += 1;
        }
      }
      
      // Also check for multiple radiators array if it exists
      if (room.radiators && Array.isArray(room.radiators)) {
        room.radiators.forEach((radiator: any) => {
          // Count any radiator that has at least some data (width, height, or type)
          if (radiator.width || radiator.height || radiator.type) {
            totalRadiators += 1;
            
            // Check if this radiator has TRVs (only count if we have type info)
            if (radiator.type === 'with-trvs') {
              totalRadiatorsWithTRVs += 1;
            }
          }
        });
      }
    });
  });

  // Update the heating system section
  updatedSiteNotesData.formData.totalRads = totalRadiators.toString();
  updatedSiteNotesData.formData.trvs = totalRadiatorsWithTRVs.toString();

  // Also update multiple heating systems if they exist
  if (updatedSiteNotesData.heatingSystems && updatedSiteNotesData.heatingSystems.length > 0) {
    // Update heating systems that haven't been manually overridden
    updatedSiteNotesData.heatingSystems.forEach((system: any) => {
      // Only sync if this system hasn't been manually overridden
      if (!system.isRadiatorCountManuallyOverridden) {
        system.totalRads = totalRadiators.toString();
        system.trvs = totalRadiatorsWithTRVs.toString();
      }
    });
  }

  // Log sync results with override information
  const overriddenSystems = updatedSiteNotesData.heatingSystems?.filter((system: any) => system.isRadiatorCountManuallyOverridden) || [];
  const syncedSystems = updatedSiteNotesData.heatingSystems?.filter((system: any) => !system.isRadiatorCountManuallyOverridden) || [];
  
  // console.log(`🔥 All-floors radiator counts synced to heating system:`, {
  //   totalRadiators,
  //   totalRadiatorsWithTRVs,
  //   floorsProcessed: updatedSiteNotesData.floors.length,
  //   trvPercentage: totalRadiators > 0 ? `${Math.round((totalRadiatorsWithTRVs / totalRadiators) * 100)}%` : '0%',
  //   heatingSystems: {
  //     total: updatedSiteNotesData.heatingSystems?.length || 0,
  //     synced: syncedSystems.length,
  //     manuallyOverridden: overriddenSystems.length
  //   }
  // });

  return updatedSiteNotesData;
};

/**
 * Hook factory to create room sync functionality
 */
export const createRoomSyncHook = (
  setSiteNotesData: (data: SiteNotesData | ((prev: SiteNotesData) => SiteNotesData)) => void,
  getSiteNotesData: () => SiteNotesData
) => {
  return {
    /**
     * Syncs a newly created room to surveyor data
     */
    syncNewRoom: (room: Room, floorNumber: number = 1) => {
      setSiteNotesData(prevData => {
        return syncRoomToSurveyorData(room, prevData, floorNumber);
      });
    },

    /**
     * Syncs a newly created room with custom height to surveyor data
     */
    syncNewRoomWithHeight: (room: Room, height: number, floorNumber: number = 1) => {
      setSiteNotesData(prevData => {
        return syncRoomWithCustomHeight(room, prevData, height, floorNumber);
      });
    },

    /**
     * Syncs all rooms to surveyor data
     */
    syncAllRooms: (rooms: Room[], floorNumber: number = 1, customLabels: Record<string, string> = {}) => {
      setSiteNotesData(prevData => {
        return syncAllRoomsToSurveyorData(rooms, prevData, floorNumber, customLabels);
      });
    },

    /**
     * Syncs all rooms with consolidation (combines main rooms + excursions)
     */
    syncAllRoomsWithConsolidation: (rooms: Room[], floorNumber: number = 1, customLabels: Record<string, string> = {}, creationContext?: {
      isFromEndpoint?: boolean;
      isExplicitRoomCreation?: boolean;
      originalRoomCount?: number; // Track original room count for endpoint creation
    }) => {
      setSiteNotesData(prevData => {
        return syncAllRoomsToSurveyorDataWithConsolidation(rooms, prevData, floorNumber, customLabels, false, creationContext);
      });
    },

    /**
     * Updates room dimensions when room is resized
     */
    updateRoomDimensions: (room: Room, floorNumber: number = 1) => {
      setSiteNotesData(prevData => {
        return syncRoomToSurveyorData(room, prevData, floorNumber);
      });
    },

    /**
     * Updates light count when a light is placed in a room
     */
    updateLightCount: (roomName: string, lightType: 'low-energy' | 'non-low-energy', increment: number = 1, floorNumber: number = 1) => {
      setSiteNotesData(prevData => {
        return updateRoomLightCount(roomName, lightType, increment, prevData, floorNumber);
      });
    },

    /**
     * Smart light sync that prevents duplication and unnecessary clearing
     */
    smartSyncLights: (lights: LightElement[], rooms: Room[], floorNumber: number = 1, clearExisting: boolean = true) => {
      setSiteNotesData(prevData => {
        return smartSyncLightsToRooms(lights, rooms, prevData, floorNumber, clearExisting);
      });
    },

    /**
     * Updates radiator dimensions and type when a radiator is placed in a room
     */
    updateRadiatorDimensions: (roomName: string, width: number, height: number, radiatorType: string, floorNumber: number = 1) => {
      setSiteNotesData(prevData => {
        return updateRoomRadiatorDimensions(roomName, width, height, radiatorType, prevData, floorNumber);
      });
    },

    /**
     * Updates multiple radiator dimensions for a room (handles 2+ radiators)
     */
    updateMultipleRadiatorDimensions: (roomName: string, radiators: RadiatorElement[], floorNumber: number = 1) => {
      setSiteNotesData(prevData => {
        return updateRoomMultipleRadiatorDimensions(roomName, radiators, prevData, floorNumber);
      });
    },

    /**
     * Updates window dimensions when a window is placed in a room
     */
    updateWindowDimensions: (roomName: string, width: number, height: number, trickleVents: number | boolean, floorNumber: number = 1, orientation?: string, isDraughtProofed?: boolean, windowType?: 'pvc' | 'wood' | 'metal', glazingGap?: string, glazingType?: string, windowCategory?: string, condition?: 'good' | 'average' | 'poor') => {
      setSiteNotesData(prevData => {
        return updateRoomWindowDimensions(roomName, width, height, trickleVents, prevData, floorNumber, orientation, isDraughtProofed, windowType, glazingGap, glazingType, windowCategory, condition);
      });
    },

    /**
     * Updates multiple window dimensions for a room (handles 2+ windows)
     */
    updateMultipleWindowDimensions: (roomName: string, windows: WindowElement[], floorNumber: number = 1) => {
      setSiteNotesData(prevData => {
        return updateRoomMultipleWindowDimensions(roomName, windows, prevData, floorNumber);
      });
    },

    /**
     * Syncs door undercut data with site notes doors & utilities section
     */
    syncDoorUndercuts: (doors: DoorElement[], rooms: Room[], floorNumber: number = 1, walls?: Wall[]) => {
      setSiteNotesData(prevData => {
        return syncDoorUndercutsToSiteNotes(doors, rooms, prevData, floorNumber, walls);
      });
    },

    /**
     * Syncs door undercut data across ALL floors with site notes doors & utilities section
     */
    syncAllFloorsDoorUndercuts: (allFloorDoors: DoorElement[], floorPlans: Record<number, Wall[]>, roomCustomLabels: Record<string, string>) => {
      setSiteNotesData(prevData => {
        return syncAllFloorsDoorUndercutsToSiteNotesWithFloorPlans(allFloorDoors, floorPlans, roomCustomLabels, prevData);
      });
    },

    /**
     * Syncs window data with site notes windows section
     */
    syncWindows: (windows: WindowElement[], rooms: Room[], floorNumber: number = 1) => {
      setSiteNotesData(prevData => {
        return syncWindowsToSiteNotes(windows, rooms, prevData, floorNumber);
      });
    },

    /**
     * Syncs window data across ALL floors with site notes windows section
     */
    syncAllFloorsWindows: (allFloorWindows: WindowElement[], floorPlans: Record<number, Wall[]>, roomCustomLabels: Record<string, string>) => {
      setSiteNotesData(prevData => {
        return syncAllFloorsWindowsToSiteNotesWithFloorPlans(allFloorWindows, floorPlans, roomCustomLabels, prevData);
      });
    },

    /**
     * Smart sync for windows - automatically handles single vs multiple windows per room
     */
    smartSyncWindows: (windows: WindowElement[], rooms: Room[], floorNumber: number = 1, isManualSync: boolean = false) => {
      setSiteNotesData(prevData => {
        return smartSyncWindowsToRoom(windows, rooms, prevData, floorNumber, isManualSync);
      });
    },

    /**
     * Smart sync for radiators - automatically handles single vs multiple radiators per room
     */
    smartSyncRadiators: (radiators: RadiatorElement[], rooms: Room[], floorNumber: number = 1, isManualSync: boolean = false) => {
      setSiteNotesData(prevData => {
        return smartSyncRadiatorsToRoom(radiators, rooms, prevData, floorNumber, isManualSync);
      });
    },

    /**
     * Migration function to re-evaluate existing radiators using improved text-based detection
     */
    migrateRadiatorAssignments: (radiators: RadiatorElement[], rooms: Room[], floorNumber: number = 1) => {
      setSiteNotesData(prevData => {
        const { updatedSiteNotesData, migratedCount } = migrateRadiatorRoomAssignments(radiators, rooms, prevData, floorNumber);
        console.log(`🔄 Migrated ${migratedCount} radiator assignments on floor ${floorNumber}`);
        return updatedSiteNotesData;
      });
    },

    /**
     * Syncs floor dimensions (area, HLP, PW) with debouncing for performance
     */
    syncFloorDimensions: (rooms: Room[], walls: Wall[], floorNumber: number = 1, immediate: boolean = false, creationContext?: {
      isFromEndpoint?: boolean;
      isExplicitRoomCreation?: boolean;
      originalRoomCount?: number; // Track original room count for endpoint creation
    }) => {
      if (immediate) {
        // Immediate sync for critical updates
        setSiteNotesData(prevData => {
          return syncFloorDimensions(rooms, walls, prevData, floorNumber);
        });
      } else {
        // Debounced sync for frequent updates
        debouncedSyncFloorDimensions(rooms, walls, setSiteNotesData, floorNumber);
      }
    },

    /**
     * Cleans up duplicate rooms across floors (fixes cross-floor duplication bug)
     */
    cleanupDuplicateRooms: () => {
      setSiteNotesData(prevData => {
        const cleanedData = cleanupDuplicateRooms(prevData);
        console.log('🧹 Duplicate rooms cleanup completed');
        
        // Show user feedback about what was cleaned up
        const originalRoomCount = prevData.floors.reduce((total, floor) => total + floor.rooms.length, 0);
        const cleanedRoomCount = cleanedData.floors.reduce((total, floor) => total + floor.rooms.length, 0);
        const removedCount = originalRoomCount - cleanedRoomCount;
        
        if (removedCount > 0) {
          alert(`✅ Cleanup completed! Removed ${removedCount} invalid/duplicate room${removedCount > 1 ? 's' : ''}.\n\nThis includes rooms without proper IDs and duplicate entries.`);
        } else {
          alert('ℹ️ No invalid or duplicate rooms found to clean up.');
        }
        
        return cleanedData;
      });
    },

    /**
     * Syncs light counts across ALL floors to the additional information section
     */
    syncAllFloorsLightsToAdditionalInfo: () => {
      setSiteNotesData(prevData => {
        return syncAllFloorsLightsToAdditionalInfo(prevData);
      });
    },

    /**
     * Syncs utility locations (gas meter, electric meter, stop tap) to site notes
     */
    syncUtilityLocations: (utilities: UtilityElement[], rooms: Room[]) => {
      setSiteNotesData(prevData => {
        return syncUtilityLocationsToSiteNotes(utilities, rooms, prevData);
      });
    },

    /**
     * Clears window sync state to allow reprocessing of all windows
     */
    clearWindowSyncState: () => {
      clearWindowSyncState();
    },

    /**
     * Gets window sync debug information
     */
    getWindowSyncDebugInfo: () => {
      return getWindowSyncDebugInfo();
    },

    /**
     * Debug and force window sync with comprehensive logging
     */
    debugAndForceWindowSync: (windows: WindowElement[], rooms: Room[], floorNumber: number = 1) => {
      setSiteNotesData(prevData => {
        const result = debugAndForceWindowSync(windows, rooms, prevData, floorNumber);
        console.log('🔍 Window sync debug completed:', result.syncResults);
        return result.updatedSiteNotesData;
      });
    },

    /**
     * Syncs boiler locations to heating system source location in site notes
     */
    syncBoilerLocations: (boilers: BoilerElement[], rooms: Room[]) => {
      setSiteNotesData(prevData => {
        return syncBoilerLocationsToSiteNotes(boilers, rooms, prevData);
      });
    },

    /**
     * Syncs radiator counts across ALL floors to the heating system section
     */
    syncAllFloorsRadiatorsToHeatingSystem: () => {
      setSiteNotesData(prevData => {
        return syncAllFloorsRadiatorsToHeatingSystem(prevData);
      });
    },

    /**
     * Re-detects element locations for existing files (returns updated elements)
     */
    redetectElementLocations: (
      elements: FloorPlanElement[],
      floorPlans: Record<number, Wall[]>,
      roomCustomLabels: Record<string, string>
    ) => {
      return redetectElementLocations(elements, floorPlans, roomCustomLabels);
    },

    /**
     * Ultra-comprehensive sync with advanced re-detection and validation
     * This provides the highest possible accuracy for element-room assignments
     */
    comprehensiveSyncWithRedetection: (
      elements: FloorPlanElement[],
      floorPlans: Record<number, Wall[]>,
      roomCustomLabels: Record<string, string>,
      siteNotesData: SiteNotesData
    ) => {
      return comprehensiveSyncWithRedetection(elements, floorPlans, roomCustomLabels, siteNotesData);
    },

    /**
     * Validate sync accuracy and provide detailed reporting
     */
    validateSyncAccuracy: (
      elements: FloorPlanElement[],
      floorPlans: Record<number, Wall[]>,
      roomCustomLabels: Record<string, string>
    ) => {
      const currentSiteNotesData = getSiteNotesData();
      return validateSyncAccuracy(elements, currentSiteNotesData, floorPlans, roomCustomLabels);
    },

    /**
     * Removes a specific room from site notes when deleted from floor plan
     */
    removeRoomFromSiteNotes: (roomName: string, floorNumber: number = 1) => {
      setSiteNotesData(prevData => {
        return removeRoomFromSiteNotes(roomName, floorNumber, prevData);
      });
    },

    /**
     * Cleans up orphaned elements that no longer belong to any room
     */
    syncOrphanedElementsCleanup: (allElements: FloorPlanElement[], allRooms: Room[], floorNumber: number = 1) => {
      setSiteNotesData(prevData => {
        return syncOrphanedElementsCleanup(allElements, allRooms, prevData, floorNumber);
      });
    },

    /**
     * Comprehensive sync for room deletion with element cleanup
     */
    syncRoomDeletionAndCleanup: (deletedRoomNames: string[], remainingElements: FloorPlanElement[], remainingRooms: Room[], floorNumber: number = 1) => {
      setSiteNotesData(prevData => {
        return syncRoomDeletionAndCleanup(deletedRoomNames, remainingElements, remainingRooms, prevData, floorNumber);
      });
    }
  };
};

/**
 * Maps floor plan floor numbers to site notes floor levels
 */
const mapFloorNumberToLevel = (floorNumber: number): string => {
  switch (floorNumber) {
    case 0:
      return 'basement';
    case 1:
      return 'ground floor';
    case 2:
      return 'first floor';
    case 3:
      return 'second floor';
    case 4:
      return 'third floor';
    case 5:
      return 'fourth floor';
    case 6:
      return 'fifth floor';
    default:
      return floorNumber > 1 ? 'upper floor' : 'ground floor';
  }
};

/**
 * Calculates total floor area from rooms on a specific floor
 */
const calculateFloorArea = (rooms: Room[]): number => {
  return rooms.reduce((sum: number, room: Room) => sum + room.area, 0);
};

/**
 * Calculates Heat Loss Parameter (HLP) - the total length of external walls
 * HLP includes both HLP-type walls and SW (Sheltered Wall) type walls
 * Uses the same advanced calculation as the manual "Get Calculations" button
 */
const calculateHLP = (walls: Wall[], rooms: Room[]): number => {
  let totalLength = 0;
  
  walls.forEach(wall => {
    if (wall.sections && wall.sections.length > 0) {
      // Enhanced section-based calculation: Calculate HLP+SW length from wall sections
      const wallPixelLength = Math.hypot(wall.end.x - wall.start.x, wall.end.y - wall.start.y);
      wall.sections.forEach(section => {
        if (section.type === 'HLP' || section.type === 'SW') {
          const sectionPixelLength = wallPixelLength * (section.endPercent - section.startPercent);
          const sectionLengthMeters = sectionPixelLength / 100; // PIXELS_PER_METER = 100
          totalLength += sectionLengthMeters;
        }
      });
    } else if (wall.type === 'HLP' || wall.type === 'SW') {
      // Standard calculation for walls without sections
      const length = Math.hypot(wall.end.x - wall.start.x, wall.end.y - wall.start.y);
      totalLength += pixelsToMeters(length);
    }
  });
  
  return Math.round(totalLength * 100) / 100; // Round to 2 decimal places
};

/**
 * Calculates Party Wall area - sum of walls marked as PW type
 * Uses the same advanced calculation as the manual "Get Calculations" button  
 */
const calculatePartyWallArea = (walls: Wall[]): number => {
  let totalLength = 0;
  
  walls.forEach(wall => {
    if (wall.sections && wall.sections.length > 0) {
      // Enhanced section-based calculation: Calculate PW length from wall sections
      const wallPixelLength = Math.hypot(wall.end.x - wall.start.x, wall.end.y - wall.start.y);
      wall.sections.forEach(section => {
        if (section.type === 'PW') {
          const sectionPixelLength = wallPixelLength * (section.endPercent - section.startPercent);
          const sectionLengthMeters = sectionPixelLength / 100; // PIXELS_PER_METER = 100
          totalLength += sectionLengthMeters;
        }
      });
    } else if (wall.type === 'PW') {
      // Standard calculation for walls without sections
      const length = Math.hypot(wall.end.x - wall.start.x, wall.end.y - wall.start.y);
      totalLength += pixelsToMeters(length);
    }
  });
  
  return Math.round(totalLength * 100) / 100; // Round to 2 decimal places
};

/**
 * Syncs floor dimensions with smart debouncing
 * Preserves manually assigned floor calculations from highlight tool
 */
export const syncFloorDimensions = (
  rooms: Room[],
  walls: Wall[],
  siteNotesData: SiteNotesData,
  floorNumber: number = 1
): SiteNotesData => {
  // Clone the site notes data to avoid mutations
  const updatedSiteNotesData = JSON.parse(JSON.stringify(siteNotesData));

  // Filter rooms and walls for the current floor
  const currentFloorRooms = rooms.filter(room => {
    // Assuming rooms inherit floor from their walls
    return room.walls.length > 0 ? room.walls[0].floor === floorNumber : true;
  });
  
  const currentFloorWalls = walls.filter(wall => wall.floor === floorNumber);

  // Find or create the appropriate floor
  let targetFloor = updatedSiteNotesData.floors.find((f: SurveyorFloor) => f.id === floorNumber);
  
  if (!targetFloor) {
    const floorLevel = mapFloorNumberToLevel(floorNumber);
    targetFloor = {
      id: floorNumber,
      floorLevel,
      buildingType: 'main building',
      height: '',
      floorArea: '',
      hlp: '',
      pw: '',
      rooms: []
    };
    updatedSiteNotesData.floors.push(targetFloor);
  }

  // Check if this floor has manually assigned values from highlight tool
  const hasManuallyAssignedValues = targetFloor.floorArea && 
                                    targetFloor.floorArea.trim() !== '' && 
                                    targetFloor.buildingType !== 'main building';

  // For main building floors, we need to recalculate considering extensions
  const isMainBuildingFloor = targetFloor.buildingType === 'main building';

  if (hasManuallyAssignedValues && !isMainBuildingFloor) {
    // Preserve manually assigned values for extensions
    console.log(`📐 Preserving manually assigned floor dimensions for ${targetFloor.floorLevel} - ${targetFloor.buildingType}:`, {
      floorArea: `${targetFloor.floorArea} m²`,
      hlp: `${targetFloor.hlp} m`,
      pw: `${targetFloor.pw} m`,
      source: 'highlight tool assignment'
    });
    return updatedSiteNotesData;
  }

  // Calculate dimensions from geometry
  const floorArea = calculateFloorArea(currentFloorRooms);
  const hlp = calculateHLP(currentFloorWalls, currentFloorRooms);
  const pw = calculatePartyWallArea(currentFloorWalls);

  if (isMainBuildingFloor) {
    // For main building floors, subtract any assigned extension areas
    const extensionFloors = updatedSiteNotesData.floors.filter((f: SurveyorFloor) => 
      f.floorLevel === targetFloor.floorLevel && 
      f.buildingType !== 'main building' &&
      f.floorArea && f.floorArea.trim() !== ''
    );
    
    let totalExtensionArea = 0;
    let totalExtensionHlp = 0;
    let totalExtensionPw = 0;
    
    extensionFloors.forEach((extFloor: SurveyorFloor) => {
      totalExtensionArea += parseFloat(extFloor.floorArea?.replace(' m²', '')) || 0;
      totalExtensionHlp += parseFloat(extFloor.hlp?.replace(' m', '')) || 0;
      totalExtensionPw += parseFloat(extFloor.pw?.replace(' m', '')) || 0;
    });
    
    // Check if main building already has properly calculated HLP/PW values (e.g., from highlighter tool)
    const existingHlp = parseFloat(targetFloor.hlp?.replace(' m', '')) || 0;
    const existingPw = parseFloat(targetFloor.pw?.replace(' m', '')) || 0;
    const hasExistingHlpPw = existingHlp > 0 || existingPw > 0;
    
    // Calculate base values to use for subtraction
    let baseFloorArea = floorArea;
    let baseHlp = hlp;
    let basePw = pw;
    
    // If main building already has HLP/PW values and calculated ones are 0, use existing + extension values as base
    if (hasExistingHlpPw && hlp === 0 && pw === 0) {
      baseHlp = existingHlp + totalExtensionHlp;
      basePw = existingPw + totalExtensionPw;
      console.log(`🔧 ROOM SYNC: Using existing HLP/PW values as base for ${targetFloor.floorLevel}:`, {
        existingHlp,
        existingPw,
        totalExtensionHlp,
        totalExtensionPw,
        calculatedBaseHlp: baseHlp,
        calculatedBasePw: basePw
      });
    }
    
    // Calculate net main building values
    const netMainArea = Math.max(0, baseFloorArea - totalExtensionArea);
    const netMainHlp = Math.max(0, baseHlp - totalExtensionHlp);
    const netMainPw = Math.max(0, basePw - totalExtensionPw);
    
    // Update main building floor with net values
    targetFloor.floorArea = netMainArea > 0 ? netMainArea.toFixed(2) : '';
    targetFloor.hlp = netMainHlp > 0 ? netMainHlp.toFixed(2) : '';
    targetFloor.pw = netMainPw > 0 ? netMainPw.toFixed(2) : '';
    
    // console.log(`📐 Main building floor dimensions calculated (net of extensions) for ${targetFloor.floorLevel}:`, {
    //   originalTotal: `${floorArea.toFixed(2)} m²`,
    //   extensionTotal: `${totalExtensionArea.toFixed(2)} m²`,
    //   netMainBuilding: `${netMainArea.toFixed(2)} m²`,
    //   hlp: `${netMainHlp.toFixed(2)} m`,
    //   pw: `${netMainPw.toFixed(2)} m`,
    //   roomCount: currentFloorRooms.length,
    //   wallCount: currentFloorWalls.length,
    //   extensionCount: extensionFloors.length
    // });
  } else {
    // For non-main building floors without manual assignment, use calculated values
    targetFloor.floorArea = floorArea > 0 ? floorArea.toFixed(2) : '';
    targetFloor.hlp = hlp > 0 ? hlp.toFixed(2) : '';
    targetFloor.pw = pw > 0 ? pw.toFixed(2) : '';
    
    console.log(`📐 Floor dimensions synced for ${targetFloor.floorLevel} - ${targetFloor.buildingType}:`, {
      floorArea: `${floorArea.toFixed(2)} m²`,
      hlp: `${hlp.toFixed(2)} m`,
      partyWall: `${pw.toFixed(2)} m`,
      roomCount: currentFloorRooms.length,
      wallCount: currentFloorWalls.length
    });
  }

  return updatedSiteNotesData;
};

// Debounced version to prevent too many calls
let floorDimensionsTimeout: NodeJS.Timeout | null = null;

/**
 * Smart debounced floor dimension sync - batches updates to avoid performance issues
 */
export const debouncedSyncFloorDimensions = (
  rooms: Room[],
  walls: Wall[],
  setSiteNotesData: (data: SiteNotesData | ((prev: SiteNotesData) => SiteNotesData)) => void,
  floorNumber: number = 1,
  delay: number = 1000 // Default 1 second delay
) => {
  // Clear existing timeout
  if (floorDimensionsTimeout) {
    clearTimeout(floorDimensionsTimeout);
  }

  // Set new timeout
  floorDimensionsTimeout = setTimeout(() => {
    setSiteNotesData(prevData => {
      return syncFloorDimensions(rooms, walls, prevData, floorNumber);
    });
  }, delay);
};

/**
 * Syncs utility locations (gas meter, electric meter, stop tap) to site notes
 */
export const syncUtilityLocationsToSiteNotes = (
  utilities: UtilityElement[],
  rooms: Room[],
  siteNotesData: SiteNotesData
): SiteNotesData => {
  const updatedData = { ...siteNotesData };
  
  // Find gas meters, electric meters, and stop taps
  const gasMeters = utilities.filter(u => u.utilityType === 'gas-meter');
  const electricMeters = utilities.filter(u => u.utilityType === 'electric-meter');
  const stopTaps = utilities.filter(u => u.utilityType === 'stop-tap');
  
  // Helper function to determine location description using enhanced room detection
  const getLocationDescription = (utility: UtilityElement): string => {
    // Try to find which room the utility is in using enhanced detection
    const room = findElementRoomWithConsolidation(utility.position, rooms, 'utility');
    if (room && room.name && !/^Room \d+$/.test(room.name)) {
      return room.name;
    }
    
    // If not in a named room, provide a generic description based on position
    console.warn(`🔧 Utility "${utility.utilityType}" at (${utility.position.x}, ${utility.position.y}) could not be assigned to a valid room`);
    return `Floor ${utility.floor}`;
  };
  
  // Sync gas meter locations
  if (gasMeters.length > 0) {
    const locations = gasMeters.map(gm => getLocationDescription(gm));
    const uniqueLocations = Array.from(new Set(locations)); // Remove duplicates
    updatedData.formData.gmLocation = uniqueLocations.join(', ');
  }
  
  // Sync electric meter locations
  if (electricMeters.length > 0) {
    const locations = electricMeters.map(em => getLocationDescription(em));
    const uniqueLocations = Array.from(new Set(locations)); // Remove duplicates
    updatedData.formData.emLocations = uniqueLocations.join(', ');
  }
  
  // Sync stop tap locations
  if (stopTaps.length > 0) {
    const locations = stopTaps.map(st => getLocationDescription(st));
    const uniqueLocations = Array.from(new Set(locations)); // Remove duplicates
    updatedData.formData.stapLocation = uniqueLocations.join(', ');
  }
  
  return updatedData;
};

/**
 * Syncs boiler locations to heating system source location in site notes
 */
export const syncBoilerLocationsToSiteNotes = (
  boilers: BoilerElement[],
  rooms: Room[],
  siteNotesData: SiteNotesData
): SiteNotesData => {
  const updatedData = { ...siteNotesData };
  
  if (boilers.length === 0) {
    console.log('🔥 No boilers found to sync');
    return updatedData;
  }
  
  // Helper function to determine location description using enhanced room detection
  const getBoilerLocationDescription = (boiler: BoilerElement): string => {
    // Try to find which room the boiler is in using enhanced detection
    const room = findElementRoomWithConsolidation(boiler.position, rooms, 'boiler');
    if (room && room.name && !/^Room \d+$/.test(room.name)) {
      return room.name;
    }
    
    // If not in a named room, provide a generic description based on position
    console.warn(`🔥 Boiler at (${boiler.position.x}, ${boiler.position.y}) could not be assigned to a valid room`);
    return `Floor ${boiler.floor}`;
  };
  
  // Get boiler locations from floor plan
  const boilerLocations = boilers.map(boiler => getBoilerLocationDescription(boiler));
  const uniqueLocations = Array.from(new Set(boilerLocations)); // Remove duplicates
  const locationString = uniqueLocations.join(', ');
  
  console.log(`🔥 Syncing boiler locations to heating system: ${locationString}`);
  
  // Update legacy heating system location
  updatedData.formData.heatingSourceLocation = locationString;
  
  // Also update multiple heating systems if they exist
  if (updatedData.heatingSystems && updatedData.heatingSystems.length > 0) {
    // Find the primary heating system or update the first one
    const primarySystem = updatedData.heatingSystems.find(system => system.isPrimary);
    const targetSystem = primarySystem || updatedData.heatingSystems[0];
    
    if (targetSystem) {
      targetSystem.heatingSourceLocation = locationString;
      console.log(`🔥 Updated heating system "${targetSystem.id}" source location to: ${locationString}`);
    }
  }
  
  console.log(`🔥 Boiler location sync completed:`, {
    totalBoilers: boilers.length,
    uniqueLocations: uniqueLocations.length,
    locationString,
    updatedLegacySystem: true,
    updatedModernSystems: updatedData.heatingSystems?.length || 0
  });
  
  return updatedData;
};

/**
 * Calculates a data score for a room based on how much meaningful data it contains
 */
const calculateRoomDataScore = (room: SurveyorRoom): number => {
  let score = 0;
  
  // Dimensions (most important)
  if (room.length && room.length !== '0' && room.length !== '') score += 10;
  if (room.width && room.width !== '0' && room.width !== '') score += 10;
  if (room.height && room.height !== '0' && room.height !== '') score += 5;
  
  // Lights
  if (room.lowEnergyLights && room.lowEnergyLights !== '0') score += parseInt(room.lowEnergyLights) || 0;
  if (room.nonLowEnergyLights && room.nonLowEnergyLights !== '0') score += parseInt(room.nonLowEnergyLights) || 0;
  
  // Radiators
  if (room.radiatorWidth && room.radiatorWidth !== '0' && room.radiatorWidth !== '') score += 3;
  if (room.radiatorHeight && room.radiatorHeight !== '0' && room.radiatorHeight !== '') score += 3;
  if (room.radiatorType && room.radiatorType !== '') score += 2;
  if (room.radiators && room.radiators.length > 0) score += room.radiators.length * 3;
  
  // Windows
  if (room.windowWidth && room.windowWidth !== '0' && room.windowWidth !== '') score += 3;
  if (room.windowHeight && room.windowHeight !== '0' && room.windowHeight !== '') score += 3;

  if (room.windowTrickleVents && room.windowTrickleVents !== '') score += 1;
  if (room.windows && room.windows.length > 0) score += room.windows.length * 3;
  
  return score;
};

/**
 * Cleans up duplicate rooms across floors that have the same name
 * This helps fix data that was corrupted by the previous cross-floor duplication bug
 */
export const cleanupDuplicateRooms = (siteNotesData: SiteNotesData): SiteNotesData => {
  const updatedSiteNotesData = JSON.parse(JSON.stringify(siteNotesData));
  
  // Reset inflated light counts to zero before cleanup to prevent aggregation errors
  console.log(`🧹 CLEANUP: Resetting potentially inflated light counts before room cleanup`);
  updatedSiteNotesData.floors.forEach((floor: SurveyorFloor) => {
    floor.rooms.forEach((room: SurveyorRoom) => {
      if (room.lowEnergyLights && parseInt(room.lowEnergyLights) > 0) {
        console.log(`🧹 CLEANUP: Clearing inflated count for room "${room.name}": ${room.lowEnergyLights} low-energy lights`);
      }
      if (room.nonLowEnergyLights && parseInt(room.nonLowEnergyLights) > 0) {
        console.log(`🧹 CLEANUP: Clearing inflated count for room "${room.name}": ${room.nonLowEnergyLights} non-low-energy lights`);
      }
      room.lowEnergyLights = '0';
      room.nonLowEnergyLights = '0';
    });
  });
  
  // Clear processed light IDs to allow fresh re-sync after cleanup
  console.log(`🧹 CLEANUP: Clearing ${lightSyncState.processedLightIds.size} processed light IDs to enable fresh light sync`);
  lightSyncState.processedLightIds.clear();
  
  // First pass: Remove rooms without valid IDs or names, and auto-generated rooms
  let removedInvalidRooms = 0;
  updatedSiteNotesData.floors.forEach((floor: SurveyorFloor) => {
    const originalCount = floor.rooms.length;
    floor.rooms = floor.rooms.filter((room: SurveyorRoom) => {
      const hasValidId = room.id && room.id > 0;
      const hasValidName = room.name && room.name.trim() !== '';
      const isAutoGeneratedName = /^Room \d+$/.test(room.name || '');
      
      if (!hasValidId || !hasValidName || isAutoGeneratedName) {
        console.log(`🚫 Removing invalid/auto-generated room - ID: ${room.id}, Name: "${room.name}" from floor ${floor.id}`);
        return false;
      }
      return true;
    });
    removedInvalidRooms += originalCount - floor.rooms.length;
  });
  
  if (removedInvalidRooms > 0) {
    console.log(`🧹 Removed ${removedInvalidRooms} invalid room(s) without proper IDs or names`);
  }
  
  // Track rooms by name across all floors AND within floors (for exact duplicates)
  const roomsByName: Record<string, Array<{ floorId: number; roomId: number; hasData: boolean; hasElements: boolean }>> = {};
  const roomsWithinFloors: Record<number, Record<string, Array<{ roomId: number; hasData: boolean; hasElements: boolean }>>> = {};
  
  // First pass: catalog all rooms
  updatedSiteNotesData.floors.forEach((floor: SurveyorFloor) => {
    roomsWithinFloors[floor.id] = {};
    
    floor.rooms.forEach((room: SurveyorRoom) => {
      if (room.name) {
        // Track across all floors
        if (!roomsByName[room.name]) {
          roomsByName[room.name] = [];
        }
        
        // Track within this floor
        if (!roomsWithinFloors[floor.id][room.name]) {
          roomsWithinFloors[floor.id][room.name] = [];
        }
        
        // Check if room has meaningful data (dimensions, lights, etc.)
        const hasData = !!(
          room.length || room.width || room.height ||
          (room.lowEnergyLights && room.lowEnergyLights !== '0') ||
          (room.nonLowEnergyLights && room.nonLowEnergyLights !== '0') ||
          room.radiatorWidth || room.radiatorHeight ||
          room.windowWidth || room.windowHeight ||
          (room.radiators && room.radiators.length > 0) ||
          (room.windows && room.windows.length > 0)
        );
        
        // Check if room has elements assigned (lights, radiators, windows)
        const hasElements = !!(
          (room.lowEnergyLights && parseInt(room.lowEnergyLights) > 0) ||
          (room.nonLowEnergyLights && parseInt(room.nonLowEnergyLights) > 0) ||
          room.radiatorWidth || room.radiatorHeight ||
          room.windowWidth || room.windowHeight ||
          (room.radiators && room.radiators.length > 0) ||
          (room.windows && room.windows.length > 0)
        );
        
        const roomInfo = {
          floorId: floor.id,
          roomId: room.id,
          hasData,
          hasElements
        };
        
        roomsByName[room.name].push(roomInfo);
        roomsWithinFloors[floor.id][room.name].push({
          roomId: room.id,
          hasData,
          hasElements
        });
      }
    });
  });
  
  // Second pass: remove duplicates within the same floor first
  Object.entries(roomsWithinFloors).forEach(([floorIdStr, floorRooms]) => {
    const floorId = parseInt(floorIdStr);
    Object.entries(floorRooms).forEach(([roomName, instances]) => {
      if (instances.length > 1) {
        console.log(`🧹 Found ${instances.length} duplicate instances of room "${roomName}" on floor ${floorId}:`, instances);
        
        // Sort by: has elements first, then has data, then by room ID
        instances.sort((a, b) => {
          if (a.hasElements && !b.hasElements) return -1;
          if (!a.hasElements && b.hasElements) return 1;
          if (a.hasData && !b.hasData) return -1;
          if (!a.hasData && b.hasData) return 1;
          return a.roomId - b.roomId;
        });
        
        // Keep the first one, remove the rest
        const toKeep = instances[0];
        const toRemove = instances.slice(1);
        
        console.log(`🧹 Keeping room "${roomName}" (ID: ${toKeep.roomId}) on floor ${floorId}, removing duplicates:`, toRemove.map(r => r.roomId));
        
        const floor = updatedSiteNotesData.floors.find((f: SurveyorFloor) => f.id === floorId);
        if (floor) {
          toRemove.forEach(({ roomId }) => {
            floor.rooms = floor.rooms.filter((r: SurveyorRoom) => r.id !== roomId);
          });
        }
      }
    });
  });

  // Third pass: remove duplicates across floors, keeping the one with the most data and elements
  Object.entries(roomsByName).forEach(([roomName, instances]) => {
    if (instances.length > 1) {
      console.log(`🧹 Found ${instances.length} instances of room "${roomName}" across floors:`, instances);
      
      // Get the actual room data to make better decisions
      const instancesWithRoomData = instances.map(instance => {
        const floor = updatedSiteNotesData.floors.find((f: SurveyorFloor) => f.id === instance.floorId);
        const room = floor?.rooms.find((r: SurveyorRoom) => r.id === instance.roomId);
        return {
          ...instance,
          room,
          dataScore: room ? calculateRoomDataScore(room) : 0
        };
      });
      
      // Smart floor assignment logic based on UK property naming conventions
      const getPreferredFloor = (roomName: string): number => {
        // UK property floor mapping:
        // - Floor ID 1 = Ground floor: LR1, K, W/C (living room, kitchen, WC)
        // - Floor ID 2 = First floor: BR1, BR2, BR3, H1, H2 (bedrooms, halls)
        // - Floor ID 3 = Second floor: BR4+, H3+ (additional bedrooms, halls)
        
        const lowerName = roomName.toLowerCase();
        
        // Ground floor rooms (floor ID 1 = "ground floor")
        if (lowerName.includes('lr') || lowerName.includes('living') || 
            lowerName.includes('lounge') || lowerName === 'k' || 
            lowerName.includes('kitchen') || lowerName.includes('w/c') ||
            lowerName.includes('wc') || lowerName.includes('toilet') ||
            lowerName.includes('dining') || lowerName.includes('utility')) {
          return 1;
        }
        
        // First floor rooms (floor ID 2 = "first floor") - bedrooms and halls
        if (lowerName.includes('br') || lowerName.includes('bedroom') ||
            lowerName.includes('hall') || lowerName.includes('landing') ||
            lowerName.includes('bathroom') || lowerName.includes('ensuite')) {
          return 2;
        }
        
        // Extract numbers from room names for more specific logic
        const numberMatch = roomName.match(/(\d+)/);
        if (numberMatch) {
          const number = parseInt(numberMatch[1]);
          
          // BR1, BR2, BR3 typically on first floor (floor ID 2)
          if (lowerName.includes('br') && number <= 3) {
            return 2;
          }
          
          // BR4+ typically on second floor (floor ID 3) if it exists
          if (lowerName.includes('br') && number >= 4) {
            return 3;
          }
          
          // H1, H2 typically on first floor (floor ID 2)
          if (lowerName.includes('h') && number <= 2) {
            return 2;
          }
          
          // H3+ typically on second floor (floor ID 3)
          if (lowerName.includes('h') && number >= 3) {
            return 3;
          }
          
          // LR1 typically on ground floor (floor ID 1)
          if (lowerName.includes('lr') && number === 1) {
            return 1;
          }
        }
        
        // Default: prefer ground floor for unknown rooms
        return 1;
      };
      
      // Get preferred floor for this room name
      const preferredFloor = getPreferredFloor(roomName);
      
      console.log(`🏠 Room "${roomName}" analysis:`, {
        preferredFloor,
        instances: instancesWithRoomData.map(i => ({
          floorId: i.floorId,
          hasElements: i.hasElements,
          dataScore: i.dataScore,
          onPreferredFloor: i.floorId === preferredFloor
        }))
      });
      
      // Sort by: elements and data first, then preferred floor as secondary consideration
      instancesWithRoomData.sort((a, b) => {
        // PRIORITY 1: Strongly prefer rooms with actual element data
        if (a.hasElements && !b.hasElements) return -1;
        if (!a.hasElements && b.hasElements) return 1;
        
        // PRIORITY 2: If both have elements or both don't, prefer higher data score
        if (a.dataScore !== b.dataScore) return b.dataScore - a.dataScore; // Higher score first
        
        // PRIORITY 3: Only then consider preferred floor (but don't override element data)
        const aOnPreferredFloor = a.floorId === preferredFloor;
        const bOnPreferredFloor = b.floorId === preferredFloor;
        
        if (aOnPreferredFloor && !bOnPreferredFloor) return -1;
        if (!aOnPreferredFloor && bOnPreferredFloor) return 1;
        
        // PRIORITY 4: Finally by floor ID as tiebreaker
        return a.floorId - b.floorId; // Lower floor ID as tiebreaker
      });
      
      // Keep the one that best matches expected floor assignment
      const toKeep = instancesWithRoomData[0];
      const toRemove = instancesWithRoomData.slice(1);
      
      // Safety check: warn if we're removing a room with significantly more data
      toRemove.forEach(removed => {
        if (removed.hasElements && !toKeep.hasElements) {
          console.warn(`⚠️ WARNING: Removing room "${roomName}" from floor ${removed.floorId} that has elements while keeping floor ${toKeep.floorId} without elements. This may cause data loss.`);
        }
        if (removed.dataScore > toKeep.dataScore + 10) {
          console.warn(`⚠️ WARNING: Removing room "${roomName}" from floor ${removed.floorId} with higher data score (${removed.dataScore}) than kept room on floor ${toKeep.floorId} (${toKeep.dataScore}).`);
        }
      });
      
      const isOnPreferredFloor = toKeep.floorId === preferredFloor;
      
      console.log(`🧹 Room "${roomName}" decision: Keeping on floor ${toKeep.floorId} (elements: ${toKeep.hasElements}, data score: ${toKeep.dataScore}, preferred floor: ${preferredFloor}, match: ${isOnPreferredFloor}), removing from floors:`, 
        toRemove.map(r => `${r.floorId} (elements: ${r.hasElements}, score: ${r.dataScore})`));
      
      toRemove.forEach(({ floorId, roomId }) => {
        const floor = updatedSiteNotesData.floors.find((f: SurveyorFloor) => f.id === floorId);
        if (floor) {
          floor.rooms = floor.rooms.filter((r: SurveyorRoom) => r.id !== roomId);
        }
      });
    }
  });
  
  return updatedSiteNotesData;
};

/**
 * Consolidates related rooms (main room + excursions) into primary rooms
 * This fixes the issue where excursions create separate rooms that don't sync properly
 */
const consolidateRelatedRooms = (
  rooms: Room[], 
  creationContext?: {
    isFromEndpoint?: boolean;
    isExplicitRoomCreation?: boolean;
    originalRoomCount?: number; // Track original room count for endpoint creation
  },
  roomCustomLabels?: Record<string, string>
): Room[] => {
  if (rooms.length <= 1) return rooms;
  
  // 🔧 ENDPOINT CREATION FIX: Skip consolidation during endpoint room creation
  if (creationContext?.isFromEndpoint || creationContext?.isExplicitRoomCreation) {
    console.log('🏠 CONSOLIDATION DEBUG: Skipping consolidation during endpoint/explicit room creation', {
      roomCount: rooms.length,
      isFromEndpoint: creationContext?.isFromEndpoint,
      isExplicitRoomCreation: creationContext?.isExplicitRoomCreation,
      rooms: rooms.map(r => ({ name: r.name, area: r.area.toFixed(2) }))
    });
    return rooms;
  }
  
  // 🔧 CRITICAL FIX: Skip consolidation if ANY room has a custom label
  // This prevents labeled rooms from being merged/destroyed during resize operations
  const hasLabeledRooms = rooms.some(room => {
    // Check if room has a meaningful name (not auto-generated)
    const hasCustomName = room.name && !room.name.startsWith('Room ') && !room.name.startsWith('room_');
    
    // Check if room has labels in the custom labels system
    let hasCustomLabel = false;
    if (roomCustomLabels) {
      // Check stable ID and geometric ID patterns
      hasCustomLabel = !!(room.stableId && roomCustomLabels[room.stableId]) ||
                      !!(room.id && roomCustomLabels[room.id]) ||
                      // Check for geometric key patterns (geo_x_y_area)
                      Object.keys(roomCustomLabels).some(key => 
                        key.startsWith('geo_') && room.center && room.area &&
                        key.includes(`${Math.round(room.center.x)}_${Math.round(room.center.y)}_${room.area.toFixed(1)}`)
                      );
    }
    
    return hasCustomName || hasCustomLabel;
  });
  
  if (hasLabeledRooms) {
    const labeledRoomDetails = rooms.filter(room => {
      const hasCustomName = room.name && !room.name.startsWith('Room ') && !room.name.startsWith('room_');
      const hasCustomLabel = roomCustomLabels && (
        !!(room.stableId && roomCustomLabels[room.stableId]) ||
        !!(room.id && roomCustomLabels[room.id])
      );
      return hasCustomName || hasCustomLabel;
    }).map(r => ({
      name: r.name,
      stableId: r.stableId?.substring(0, 20) + '...',
      hasLabel: roomCustomLabels && (!!(r.stableId && roomCustomLabels[r.stableId]) || !!(r.id && roomCustomLabels[r.id]))
    }));
    
    console.log('🏠 CONSOLIDATION DEBUG: Skipping consolidation - labeled rooms detected', {
      roomCount: rooms.length,
      labeledRoomsCount: labeledRoomDetails.length,
      labeledRooms: labeledRoomDetails,
      reason: 'preserve_labeled_rooms'
    });
    return rooms;
  }
  
  console.log('🏠 CONSOLIDATION DEBUG: Processing consolidation', {
    roomCount: rooms.length,
    hasCreationContext: !!creationContext,
    hasRoomLabels: !!roomCustomLabels && Object.keys(roomCustomLabels).length > 0,
    labelCount: roomCustomLabels ? Object.keys(roomCustomLabels).length : 0,
    rooms: rooms.map(r => ({ name: r.name, area: r.area.toFixed(2) }))
  });

  // First pass: Remove rooms with duplicate stable IDs (keep the one with a proper name)
  const roomsByStableId = new Map<string, Room[]>();
  
  rooms.forEach(room => {
    if (room.stableId) {
      if (!roomsByStableId.has(room.stableId)) {
        roomsByStableId.set(room.stableId, []);
      }
      roomsByStableId.get(room.stableId)!.push(room);
    }
  });
  
  // For each stable ID, keep only the best room
  const deduplicatedRooms: Room[] = [];
  roomsByStableId.forEach((roomsWithSameStableId, stableId) => {
    if (roomsWithSameStableId.length === 1) {
      deduplicatedRooms.push(roomsWithSameStableId[0]);
    } else {
      // Multiple rooms with same stable ID - keep the one with the best name
      console.log(`🔍 Found ${roomsWithSameStableId.length} rooms with same stable ID "${stableId}":`, 
        roomsWithSameStableId.map(r => ({ name: r.name, area: r.area.toFixed(2) })));
      
      const bestRoom = roomsWithSameStableId.reduce((best, current) => {
        // Prefer rooms with non-auto-generated names
        const bestIsAutoGenerated = /^Room \d+$/.test(best.name);
        const currentIsAutoGenerated = /^Room \d+$/.test(current.name);
        
        if (!bestIsAutoGenerated && currentIsAutoGenerated) return best;
        if (bestIsAutoGenerated && !currentIsAutoGenerated) return current;
        
        // If both have same name type, prefer larger area
        return current.area > best.area ? current : best;
      });
      
      console.log(`🔍 Keeping room "${bestRoom.name}" (${bestRoom.area.toFixed(2)}m²) for stable ID "${stableId}"`);
      deduplicatedRooms.push(bestRoom);
    }
  });
  
  // Add rooms without stable IDs
  rooms.forEach(room => {
    if (!room.stableId) {
      deduplicatedRooms.push(room);
    }
  });

  const consolidatedRooms: Room[] = [];
  const processedRoomIds = new Set<string>();

  deduplicatedRooms.forEach(room => {
    if (processedRoomIds.has(room.id)) return;

    // Find rooms that might be related to this one (excursions, alcoves, etc.)
    const relatedRooms = deduplicatedRooms.filter(otherRoom => {
      if (otherRoom.id === room.id || processedRoomIds.has(otherRoom.id)) return false;
      
      // Check if rooms share walls or are very close to each other
      const roomCenter = room.center;
      const otherCenter = otherRoom.center;
      const distance = Math.hypot(roomCenter.x - otherCenter.x, roomCenter.y - otherCenter.y);
      
      // VERY restrictive consolidation: only consolidate if BOTH rooms have generic names
      // AND they are extremely close AND share walls
      const roomHasGenericName = /^Room \d+$/.test(room.name);
      const otherHasGenericName = /^Room \d+$/.test(otherRoom.name);
      const bothHaveGenericNames = roomHasGenericName && otherHasGenericName;
      
      // NEVER consolidate if either room has a proper name
      if (!bothHaveGenericNames) {
        return false;
      }
      
      // Check for shared walls (must be very precise)
      const shareWalls = room.walls.some(wall1 => 
        otherRoom.walls.some(wall2 => {
          // Check if walls are nearly identical (shared wall)
          const startMatch = Math.abs(wall1.start.x - wall2.start.x) < 2 && Math.abs(wall1.start.y - wall2.start.y) < 2;
          const endMatch = Math.abs(wall1.end.x - wall2.end.x) < 2 && Math.abs(wall1.end.y - wall2.end.y) < 2;
          const reverseStartMatch = Math.abs(wall1.start.x - wall2.end.x) < 2 && Math.abs(wall1.start.y - wall2.end.y) < 2;
          const reverseEndMatch = Math.abs(wall1.end.x - wall2.start.x) < 2 && Math.abs(wall1.end.y - wall2.start.y) < 2;
          
          return (startMatch && endMatch) || (reverseStartMatch && reverseEndMatch);
        })
      );
      
      // Only consolidate if:
      // 1. Both rooms have generic names (Room 1, Room 2, etc.)
      // 2. Rooms are very close (< 50 pixels, very restrictive)
      // 3. They share actual walls (not just proximity)
      // 4. One room is significantly smaller (true excursion pattern)
      const isVeryClose = distance < 50;
      const areaRatio = Math.min(room.area, otherRoom.area) / Math.max(room.area, otherRoom.area);
      const isTrueExcursion = areaRatio < 0.3; // One room is less than 30% of the other
      
      // 🔧 SMART CONSOLIDATION: Only consolidate true excursions, not adjacent rooms
      const shouldConsolidate = bothHaveGenericNames && isVeryClose && shareWalls && isTrueExcursion;
      
      // Additional check: rooms must be similar in size OR one is much smaller (excursion)
      const areaSimilar = Math.abs(room.area - otherRoom.area) / Math.max(room.area, otherRoom.area) < 0.3;
      
      const finalDecision = shouldConsolidate && (areaSimilar || isTrueExcursion);
      
      if (shareWalls && !finalDecision) {
        console.log(`🏠 CONSOLIDATION: Keeping separate - "${room.name}" and "${otherRoom.name}" share walls but are distinct rooms`, {
          distance: distance.toFixed(1),
          areaRatio: areaRatio.toFixed(2),
          bothGeneric: bothHaveGenericNames,
          shareWalls,
          isVeryClose,
          isTrueExcursion,
          areaSimilar,
          areas: [room.area.toFixed(2), otherRoom.area.toFixed(2)]
        });
      }
      
      return finalDecision;
    });

    if (relatedRooms.length > 0) {
      // Consolidate into the room with the better name (non-generic if possible)
      const allRelatedRooms = [room, ...relatedRooms];
      const primaryRoom = allRelatedRooms.find(r => !/^Room \d+$/.test(r.name)) || room;
      
      // Combine walls from all related rooms
      const allWalls = allRelatedRooms.flatMap(r => r.walls);
      const uniqueWalls = allWalls.filter((wall, index, arr) => 
        arr.findIndex(w => 
          Math.abs(w.start.x - wall.start.x) < 1 && 
          Math.abs(w.start.y - wall.start.y) < 1 &&
          Math.abs(w.end.x - wall.end.x) < 1 && 
          Math.abs(w.end.y - wall.end.y) < 1
        ) === index
      );

      // Create consolidated room
      const consolidatedRoom: Room = {
        ...primaryRoom,
        walls: uniqueWalls,
        area: allRelatedRooms.reduce((total, r) => total + r.area, 0),
        // Keep the primary room's center and name
      };

      consolidatedRooms.push(consolidatedRoom);
      
      // Mark all related rooms as processed
      allRelatedRooms.forEach(r => processedRoomIds.add(r.id));
      
      console.log(`🔗 Consolidated ${allRelatedRooms.length} related rooms into "${primaryRoom.name}"`);
    } else {
      // No related rooms found, add as-is
      consolidatedRooms.push(room);
      processedRoomIds.add(room.id);
    }
  });

  return consolidatedRooms;
};

/**
 * Find room by wall ID for wall-mounted elements (windows, radiators)
 * Enhanced to handle shared walls between neighboring rooms
 */
export const findRoomByWallId = (
  wallId: string,
  rooms: Room[],
  elementPosition?: { x: number; y: number }
): Room | null => {
  if (!wallId || !rooms || rooms.length === 0) {
    return null;
  }

  // Find all rooms that contain the wall with the given wallId
  const candidateRooms: Room[] = [];
  
  // First, look for direct wall ID matches in all rooms
  for (const room of rooms) {
    if (!room.walls || room.walls.length === 0) continue;
    
    const matchingWall = room.walls.find(wall => {
      const wallIds = [
        (wall as any).stableId,
        (wall as any).id,
        (wall as any).tempId,
        // Also try coordinate-based matching for split walls
        `wall_${wall.start.x.toFixed(2)}_${wall.start.y.toFixed(2)}_${wall.end.x.toFixed(2)}_${wall.end.y.toFixed(2)}`,
        `wall_${wall.start.x}_${wall.start.y}_${wall.end.x}_${wall.end.y}`
      ].filter(id => id);
      
      return wallIds.includes(wallId);
    });
    
    if (matchingWall) {
      candidateRooms.push(room);
    }
  }

  // Also try more flexible wall ID matching using coordinates
  if (candidateRooms.length === 0) {
    for (const room of rooms) {
      if (!room.walls || room.walls.length === 0) continue;
      
      const matchingWall = room.walls.find(wall => {
        const wallIds = [
          (wall as any).stableId,
          (wall as any).id,
          (wall as any).tempId
        ].filter(id => id);
        
        if (wallIds.includes(wallId)) {
          return true;
        }
        
        // Try multiple coordinate-based ID formats
        const generatedId1 = `wall_${wall.start.x.toFixed(2)}_${wall.start.y.toFixed(2)}_${wall.end.x.toFixed(2)}_${wall.end.y.toFixed(2)}`;
        const generatedId2 = `wall_${wall.start.x}_${wall.start.y}_${wall.end.x}_${wall.end.y}`;
        const generatedId3 = `wall_${Math.round(wall.start.x)}_${Math.round(wall.start.y)}_${Math.round(wall.end.x)}_${Math.round(wall.end.y)}`;
        
        // Try to extract timestamp-based ID pattern and match based on coordinates
        const wallIdMatch = wallId.match(/wall_(\d+)_(.+)/);
        if (wallIdMatch) {
          const coordPart = wallIdMatch[2];
          const coords = coordPart.split('_');
          if (coords.length >= 4) {
            const [x1, y1, x2, y2] = coords.map(Number);
            if (!isNaN(x1) && !isNaN(y1) && !isNaN(x2) && !isNaN(y2)) {
              // Check if coordinates match (with some tolerance for floating point precision)
              const tolerance = 0.1;
              const startMatches = Math.abs(wall.start.x - x1) < tolerance && Math.abs(wall.start.y - y1) < tolerance;
              const endMatches = Math.abs(wall.end.x - x2) < tolerance && Math.abs(wall.end.y - y2) < tolerance;
              const reverseStartMatches = Math.abs(wall.start.x - x2) < tolerance && Math.abs(wall.start.y - y2) < tolerance;
              const reverseEndMatches = Math.abs(wall.end.x - x1) < tolerance && Math.abs(wall.end.y - y1) < tolerance;
              
              if ((startMatches && endMatches) || (reverseStartMatches && reverseEndMatches)) {
                return true;
              }
            }
          }
        }
        
        return generatedId1 === wallId || generatedId2 === wallId || generatedId3 === wallId;
      });
      
      if (matchingWall) {
        candidateRooms.push(room);
      }
    }
  }
  
  // 🔧 PARENT WALL ID FIX: Use parent wall ID tracking for split wall elements (more reliable than coordinates)
  if (candidateRooms.length === 0) {
    console.log(`🔍 PARENT_WALL_FIX: No direct wall ID match for "${wallId}", trying parent wall ID lookup`);
    
    // Strategy 1: Check if this is a split wall element by looking for parent wall references
    console.log(`🔍 PARENT_WALL_DEBUG: Searching for parent wall relationships for "${wallId}"`);
    
    // Special debug for room K when dealing with the problematic split wall
    if (wallId === 'wall_-280.00_799.15_-147.73_799.15') {
      const roomK = rooms.find(r => r.name === 'K');
      if (roomK) {
        console.log(`🚨 ROOM_K_DEBUG: Detailed analysis for split wall "${wallId}" in room K:`);
        console.log(`🚨 ROOM_K_DEBUG: Room K has ${roomK.walls?.length || 0} walls:`);
        if (roomK.walls) {
          roomK.walls.forEach((wall, index) => {
            console.log(`🚨 ROOM_K_DEBUG: Wall ${index + 1}:`, {
              id: (wall as any).id,
              stableId: (wall as any).stableId,
              parentWallId: (wall as any).parentWallId,
              start: wall.start,
              end: wall.end,
              type: wall.type
            });
          });
        }
        
        // Check if we can extract coordinates from the split wall ID
        const splitWallCoords = extractWallCoordinates(wallId);
        if (splitWallCoords) {
          console.log(`🚨 ROOM_K_DEBUG: Split wall coordinates: (${splitWallCoords.x1},${splitWallCoords.y1})→(${splitWallCoords.x2},${splitWallCoords.y2})`);
          
          // Look for overlapping or containing walls in room K
          const potentialMatches = roomK.walls?.filter(wall => {
            const tolerance = 10.0; // Larger tolerance for debugging
            const startNear = Math.abs(wall.start.x - splitWallCoords.x1) < tolerance && Math.abs(wall.start.y - splitWallCoords.y1) < tolerance;
            const endNear = Math.abs(wall.end.x - splitWallCoords.x2) < tolerance && Math.abs(wall.end.y - splitWallCoords.y2) < tolerance;
            const reverseStartNear = Math.abs(wall.start.x - splitWallCoords.x2) < tolerance && Math.abs(wall.start.y - splitWallCoords.y2) < tolerance;
            const reverseEndNear = Math.abs(wall.end.x - splitWallCoords.x1) < tolerance && Math.abs(wall.end.y - splitWallCoords.y1) < tolerance;
            
            // Also check if the split wall segment is contained within this wall
            const splitInWall = (wall.start.x <= splitWallCoords.x1 && splitWallCoords.x2 <= wall.end.x && 
                               Math.abs(wall.start.y - splitWallCoords.y1) < tolerance) ||
                              (wall.start.x >= splitWallCoords.x1 && splitWallCoords.x2 >= wall.end.x && 
                               Math.abs(wall.start.y - splitWallCoords.y1) < tolerance);
            
            return startNear || endNear || reverseStartNear || reverseEndNear || splitInWall;
          });
          
          console.log(`🚨 ROOM_K_DEBUG: Found ${potentialMatches?.length || 0} potential matching walls in room K`);
          potentialMatches?.forEach((match, index) => {
            console.log(`🚨 ROOM_K_DEBUG: Potential match ${index + 1}:`, {
              id: (match as any).id,
              stableId: (match as any).stableId,
              parentWallId: (match as any).parentWallId,
              start: match.start,
              end: match.end
            });
          });
        }
      } else {
        console.log(`🚨 ROOM_K_DEBUG: Room K not found in rooms list!`);
      }
    }
    
    for (const room of rooms) {
      if (!room.walls || room.walls.length === 0) continue;
      
      console.log(`🔍 PARENT_WALL_DEBUG: Checking room "${room.name}" with ${room.walls.length} walls`);
      
      for (const wall of room.walls) {
        const wallParentId = (wall as any).parentWallId;
        const wallStableId = (wall as any).stableId || '';
        const wallRegularId = (wall as any).id || '';
        
        console.log(`🔍 WALL_DEBUG: Wall in room "${room.name}" - parentId:"${wallParentId || 'none'}", stableId:"${wallStableId}", regularId:"${wallRegularId}"`);
        
        // Strategy 1a: Check if the wallId we're looking for matches this wall's parent wall ID
        if (wallParentId && wallId.includes(wallParentId)) {
          console.log(`🔧 PARENT_WALL_MATCH: Found room "${room.name}" via parent wall ID "${wallParentId}" for split wall "${wallId}"`);
          candidateRooms.push(room);
          break;
        }
        
        // Strategy 1b: Reverse check - see if this wall's ID is contained in our target wallId (split wall contains parent ID)
        if (wallStableId && wallId.includes(wallStableId)) {
          console.log(`🔧 PARENT_WALL_MATCH: Found room "${room.name}" via stableId "${wallStableId}" contained in split wall "${wallId}"`);
          candidateRooms.push(room);
          break;
        }
        
        if (wallRegularId && wallId.includes(wallRegularId)) {
          console.log(`🔧 PARENT_WALL_MATCH: Found room "${room.name}" via regularId "${wallRegularId}" contained in split wall "${wallId}"`);
          candidateRooms.push(room);
          break;
        }
        
        // Strategy 1c: Check if split wall coordinates match this wall's coordinates (same base wall)
        if (wallStableId || wallRegularId) {
          // Extract coordinates from both wall IDs for comparison
          const currentWallCoords = extractWallCoordinates(wallStableId || wallRegularId);
          const targetWallCoords = extractWallCoordinates(wallId);
          
          if (currentWallCoords && targetWallCoords) {
            // Check if they share the same line (collinear walls with tolerance)
            if (areWallsOnSameLine(currentWallCoords, targetWallCoords)) {
              console.log(`🔧 PARENT_WALL_MATCH: Found room "${room.name}" via collinear wall match - wall:"${wallStableId || wallRegularId}" matches split:"${wallId}"`);
              candidateRooms.push(room);
              break;
            }
          }
        }
      }
      
      if (candidateRooms.length > 0) break; // Found a match, stop searching
    }
    
    // Strategy 2: If no parent wall ID match, extract base coordinates for fallback matching
    if (candidateRooms.length === 0) {
      console.log(`🔍 PARENT_WALL_FIX: No parent wall ID match found, falling back to coordinate extraction`);
      
      // Extract coordinates from different wall ID formats
      let coords = null;
      
      // Format 1: wall_-280.00_799.15_-95.70_799.15 (coordinate-based stableId from split walls)
      const coordMatch = wallId.match(/wall_([\-\d\.]+)_([\-\d\.]+)_([\-\d\.]+)_([\-\d\.]+)/);
      if (coordMatch) {
        coords = coordMatch.slice(1, 5).map(Number);
        console.log(`🔧 Extracted coordinates from split wall stableId: (${coords[0]},${coords[1]})→(${coords[2]},${coords[3]})`);
      }
      
      if (coords && coords.every(c => !isNaN(c))) {
        const [x1, y1, x2, y2] = coords;
        
        // Try to find matching walls in rooms using coordinate tolerance  
        for (const room of rooms) {
          if (!room.walls || room.walls.length === 0) continue;
          
          const matchingWall = room.walls.find(wall => {
            const tolerance = 0.1;
            const startMatches = Math.abs(wall.start.x - x1) < tolerance && Math.abs(wall.start.y - y1) < tolerance;
            const endMatches = Math.abs(wall.end.x - x2) < tolerance && Math.abs(wall.end.y - y2) < tolerance;
            const reverseStartMatches = Math.abs(wall.start.x - x2) < tolerance && Math.abs(wall.start.y - y2) < tolerance;
            const reverseEndMatches = Math.abs(wall.end.x - x1) < tolerance && Math.abs(wall.end.y - y1) < tolerance;
            
            return (startMatches && endMatches) || (reverseStartMatches && reverseEndMatches);
          });
          
          if (matchingWall) {
            console.log(`🎯 COORDINATE_FALLBACK: Found wall by coordinates in room "${room.name}": (${x1},${y1})→(${x2},${y2})`);
            candidateRooms.push(room);
            break;
          }
        }
      }
    }
  }


  if (candidateRooms.length === 0) {
    // Debug: Show available wall IDs to help troubleshoot
    const availableWallIds: string[] = [];
    for (const room of rooms) {
      if (!room.walls || room.walls.length === 0) continue;
      for (const wall of room.walls) {
        const wallIds = [
          (wall as any).stableId,
          (wall as any).id,
          `wall_${wall.start.x.toFixed(2)}_${wall.start.y.toFixed(2)}_${wall.end.x.toFixed(2)}_${wall.end.y.toFixed(2)}`,
          `wall_${wall.start.x}_${wall.start.y}_${wall.end.x}_${wall.end.y}`
        ].filter(id => id);
        availableWallIds.push(...wallIds);
      }
    }
    
    console.warn(`⚠️ AUTO-SYNC FALLBACK: No room found for wall ID "${wallId}" - will try coordinate-based detection`);
    console.log(`🔍 Available wall IDs in rooms: ${availableWallIds.slice(0, 10).join(', ')}${availableWallIds.length > 10 ? ` (showing first 10 of ${availableWallIds.length})` : ''}`);
    return null;
  }
  
  // If only one room contains the wall, return it
  if (candidateRooms.length === 1) {
    // console.log(`🎯 AUTO-SYNC SUCCESS: Found room "${candidateRooms[0].name}" for wall ID "${wallId}" (wall-based assignment)`);
    return candidateRooms[0];
  }
  
  // Multiple rooms share this wall (neighboring rooms scenario)
  console.log(`🔍 SHARED WALL DETECTED: Wall ID "${wallId}" found in ${candidateRooms.length} rooms: [${candidateRooms.map(r => r.name).join(', ')}]`);
  
  // If we have element position, use it to determine which room the element belongs to
  if (elementPosition) {
    // Strategy 1: Check which room the element is actually inside
    for (const room of candidateRooms) {
      if (isPointInsideRoom(elementPosition, room.walls)) {
        console.log(`🎯 SHARED WALL RESOLVED: Element at (${elementPosition.x}, ${elementPosition.y}) is inside room "${room.name}"`);
        return room;
      }
    }
    
    // Strategy 2: If element is not strictly inside any room (wall-mounted elements),
    // find the room where the element is closest to being inside
    let bestRoom: Room | null = null;
    let bestScore = -1;
    
    for (const room of candidateRooms) {
      const roomBounds = getRoomBounds(room.walls);
      const roomCenter = getRoomCenter(room.walls);
      
      // Calculate distance from room center (closer = better)
      const centerDistance = Math.hypot(
        elementPosition.x - roomCenter.x,
        elementPosition.y - roomCenter.y
      );
      
      // Check if element is within room bounds (better score)
      const isWithinBounds = 
        elementPosition.x >= roomBounds.minX && elementPosition.x <= roomBounds.maxX &&
        elementPosition.y >= roomBounds.minY && elementPosition.y <= roomBounds.maxY;
      
      // Calculate comprehensive score (higher = better)
      const boundsScore = isWithinBounds ? 100 : 0;
      const proximityScore = Math.max(0, 100 - centerDistance / 2);
      
      // Additional check: calculate how far the element is from the room's interior
      const distanceFromBounds = Math.max(
        0,
        Math.max(
          roomBounds.minX - elementPosition.x,
          elementPosition.x - roomBounds.maxX,
          roomBounds.minY - elementPosition.y,
          elementPosition.y - roomBounds.maxY
        )
      );
      
      const containmentScore = boundsScore + proximityScore - (distanceFromBounds * 2);
      
      if (containmentScore > bestScore) {
        bestScore = containmentScore;
        bestRoom = room;
      }
    }
    
    if (bestRoom) {
      console.log(`🎯 SHARED WALL RESOLVED: Element at (${elementPosition.x}, ${elementPosition.y}) assigned to room "${bestRoom.name}" via position analysis (score: ${bestScore.toFixed(1)})`);
      return bestRoom;
    }
  }
  
  // Fallback: If no position provided or position-based resolution failed,
  // return the first room but log a warning
  console.warn(`⚠️ SHARED WALL AMBIGUOUS: Multiple rooms share wall ID "${wallId}", returning first room "${candidateRooms[0].name}" - consider providing element position for accurate assignment`);
  return candidateRooms[0];
};

/**
 * Enhanced element room detection with multiple sophisticated strategies
 * This provides the most thorough and accurate element assignment possible
 */
export const findElementRoomWithConsolidation = (
  elementPosition: { x: number; y: number },
  rooms: Room[],
  elementType?: string
): Room | null => {
  if (rooms.length === 0) {
    return null;
  }

  // console.log(`🔍 Enhanced element detection for ${elementType || 'element'} at (${elementPosition.x}, ${elementPosition.y})`);

  // Strategy 1: Strict geometric containment - element is clearly inside room (HIGHEST PRIORITY)
  for (const room of rooms) {
    const isInsideRoom = isPointInsideRoom(elementPosition, room.walls);
    if (isInsideRoom) {
      // console.log(`📍 Element assigned to room "${room.name}" via strict containment`);
      return room;
    }
  }

  // Strategy 2: Wall-based detection for wall-mounted elements (SECONDARY PRIORITY)
  // Only use this if the element is NOT strictly inside any room
  if (elementType && ['window', 'radiator', 'door'].includes(elementType)) {
    const wallBasedCandidates: Array<{ room: Room; distance: number; wall: Wall }> = [];
    
    for (const room of rooms) {
      for (const wall of room.walls) {
        // Calculate closest point on wall to element
        const wallLength = Math.hypot(wall.end.x - wall.start.x, wall.end.y - wall.start.y);
        if (wallLength === 0) continue;

        // Calculate projection of element position onto wall
        const wallVector = { x: wall.end.x - wall.start.x, y: wall.end.y - wall.start.y };
        const elementVector = { x: elementPosition.x - wall.start.x, y: elementPosition.y - wall.start.y };
        
        const dotProduct = (elementVector.x * wallVector.x + elementVector.y * wallVector.y) / (wallLength * wallLength);
        const projectionRatio = Math.max(0, Math.min(1, dotProduct));
        
        const closestPointOnWall = {
          x: wall.start.x + projectionRatio * wallVector.x,
          y: wall.start.y + projectionRatio * wallVector.y
        };
        
        const distanceToWall = Math.hypot(
          elementPosition.x - closestPointOnWall.x,
          elementPosition.y - closestPointOnWall.y
        );
        
        // Wall-mounted elements should be very close to walls
        const wallProximityThreshold = elementType === 'window' ? 30 : 
                                      elementType === 'radiator' ? 25 : 
                                      elementType === 'door' ? 20 : 15;
        
        if (distanceToWall <= wallProximityThreshold) {
          wallBasedCandidates.push({ room, distance: distanceToWall, wall });
        }
      }
    }
    
    // For wall-mounted elements (radiators and windows), prioritize the room that the element is closest to being inside
    if ((elementType === 'radiator' || elementType === 'window') && wallBasedCandidates.length > 1) {
      console.log(`🔥 SPLIT_WALL_${elementType?.toUpperCase()}_DEBUG: Multiple candidate rooms for ${elementType} at (${elementPosition.x}, ${elementPosition.y})`);
      console.log(`🔥 SPLIT_WALL_${elementType?.toUpperCase()}_DEBUG: Candidate rooms: ${wallBasedCandidates.map(c => c.room.name).join(', ')}`);
      
      // Check which room the element position is actually closest to being inside
      let bestCandidate = wallBasedCandidates[0];
      let bestScore = -1;
      
      for (const candidate of wallBasedCandidates) {
        // Calculate how "inside" the element would be relative to this room
        const roomBounds = getRoomBounds(candidate.room.walls);
        const roomCenter = getRoomCenter(candidate.room.walls);
        
        // Distance from room center (closer = better)
        const centerDistance = Math.hypot(
          elementPosition.x - roomCenter.x,
          elementPosition.y - roomCenter.y
        );
        
        // Check if element is within room bounds (better score)
        const isWithinBounds = 
          elementPosition.x >= roomBounds.minX && elementPosition.x <= roomBounds.maxX &&
          elementPosition.y >= roomBounds.minY && elementPosition.y <= roomBounds.maxY;
        
        // 🔧 SPLIT WALL FIX: Enhanced geometry detection for rooms with split walls
        const isInsideGeometry = isPointInsideRoom(elementPosition, candidate.room.walls);
        
        // 🔧 SPLIT WALL BIAS: Give extra weight to room K if it has split walls
        let splitWallBonus = 0;
        const splitWalls = candidate.room.walls.filter(w => (w as any).isSplitOrigin || (w as any).parentWallId);
        if (splitWalls.length > 0 && candidate.room.name === 'K') {
          splitWallBonus = 50; // Bias towards room K if it has split walls
          console.log(`🔧 SPLIT_WALL_BIAS: Room K gets +${splitWallBonus} bonus for having split walls`);
        }
        
        // Calculate comprehensive score (higher = better)
        const boundsScore = isWithinBounds ? 100 : 0;
        const geometryScore = isInsideGeometry ? 300 : 0; // Increased priority for geometry
        const proximityScore = Math.max(0, 100 - centerDistance / 2);
        const wallScore = Math.max(0, 50 - candidate.distance);
        
        const totalScore = geometryScore + boundsScore + proximityScore + wallScore + splitWallBonus;
        
        console.log(`🔥 SPLIT_WALL_${elementType?.toUpperCase()}_DEBUG: Room "${candidate.room.name}":`);
        console.log(`   - bounds: ${isWithinBounds} (${boundsScore} pts)`);
        console.log(`   - geometry: ${isInsideGeometry} (${geometryScore} pts)`); 
        console.log(`   - centerDist: ${centerDistance.toFixed(1)} (${proximityScore.toFixed(1)} pts)`);
        console.log(`   - wallDist: ${candidate.distance.toFixed(1)} (${wallScore.toFixed(1)} pts)`);
        console.log(`   - splitWallBonus: ${splitWallBonus} pts`);
        console.log(`   - TOTAL SCORE: ${totalScore.toFixed(1)}`);
        
        if (totalScore > bestScore) {
          bestScore = totalScore;
          bestCandidate = candidate;
        }
      }
      
      console.log(`🎯 SPLIT_WALL_${elementType?.toUpperCase()}_RESULT: ${elementType} assigned to room "${bestCandidate.room.name}" (score: ${bestScore.toFixed(1)})`);
      return bestCandidate.room;
    } else if (wallBasedCandidates.length > 0) {
      // For other elements or single candidate, use closest wall
      const bestCandidate = wallBasedCandidates.reduce((best, current) => 
        current.distance < best.distance ? current : best
      );
      console.log(`🎯 ${elementType} assigned to room "${bestCandidate.room.name}" via wall proximity (${bestCandidate.distance.toFixed(1)}px from wall)`);
      return bestCandidate.room;
    }
  }

  // Strategy 3: Enhanced boundary detection with element-specific tolerances
  const elementTypeBuffer = getElementTypeBuffer(elementType);
  const candidates: Array<{ room: Room; distance: number; containmentScore: number }> = [];
  
  for (const room of rooms) {
    const bounds = getRoomBounds(room.walls);
    
    // Check if element is within expanded bounds
    const isWithinExpandedBounds = 
      elementPosition.x >= (bounds.minX - elementTypeBuffer) && 
      elementPosition.x <= (bounds.maxX + elementTypeBuffer) &&
      elementPosition.y >= (bounds.minY - elementTypeBuffer) && 
      elementPosition.y <= (bounds.maxY + elementTypeBuffer);
    
    if (isWithinExpandedBounds) {
      // Calculate multiple distance metrics for better accuracy
      const roomCenter = getRoomCenter(room.walls);
      const centerDistance = Math.hypot(
        elementPosition.x - roomCenter.x,
        elementPosition.y - roomCenter.y
      );
      
      // Calculate minimum distance to any wall of the room
      let minWallDistance = Infinity;
      for (const wall of room.walls) {
        const wallDistance = pointToLineDistance(elementPosition, wall.start, wall.end);
        minWallDistance = Math.min(minWallDistance, wallDistance);
      }
      
      // Calculate containment score based on how "inside" the element appears to be
      const distanceFromBounds = Math.max(
        0,
        Math.max(
          bounds.minX - elementPosition.x,
          elementPosition.x - bounds.maxX,
          bounds.minY - elementPosition.y,
          elementPosition.y - bounds.maxY
        )
      );
      
      const containmentScore = 100 - distanceFromBounds - minWallDistance * 0.5;
      
      candidates.push({
        room,
        distance: centerDistance,
        containmentScore
      });
    }
  }
  
  // Sort candidates by containment score first, then by distance
  candidates.sort((a, b) => {
    if (Math.abs(a.containmentScore - b.containmentScore) > 10) {
      return b.containmentScore - a.containmentScore; // Higher score is better
    }
    return a.distance - b.distance; // Lower distance is better
  });
  
  if (candidates.length > 0) {
    const bestCandidate = candidates[0];
    // console.log(`📍 Element assigned to room "${bestCandidate.room.name}" via enhanced boundary detection (score: ${bestCandidate.containmentScore.toFixed(1)}, distance: ${bestCandidate.distance.toFixed(1)}px)`);
    return bestCandidate.room;
  }

  // Strategy 4: Intelligent proximity-based assignment with room area consideration
  let bestMatch: { room: Room; score: number } | null = null;
  const maxSearchDistance = 100; // Maximum distance to consider
  
  for (const room of rooms) {
    const roomCenter = getRoomCenter(room.walls);
    const centerDistance = Math.hypot(
      elementPosition.x - roomCenter.x,
      elementPosition.y - roomCenter.y
    );
    
    if (centerDistance <= maxSearchDistance) {
      // Calculate a comprehensive score considering distance, room size, and element type
      const roomArea = room.area || 1;
      const normalizedDistance = centerDistance / maxSearchDistance;
      const sizeWeight = Math.min(1, Math.log(roomArea + 1) / 10); // Larger rooms get slight preference
      
      // Element-specific scoring adjustments
      let typeWeight = 1;
      if (elementType === 'light' && roomArea > 20) typeWeight = 1.2; // Lights prefer larger rooms
      if (elementType === 'radiator' && roomArea < 50) typeWeight = 1.1; // Radiators common in smaller rooms
      if (elementType === 'utility' && room.name.toLowerCase().includes('kitchen')) typeWeight = 1.5; // Utilities prefer kitchens
      
      const comprehensiveScore = (1 - normalizedDistance) * sizeWeight * typeWeight;
      
      if (!bestMatch || comprehensiveScore > bestMatch.score) {
        bestMatch = { room, score: comprehensiveScore };
      }
    }
  }
  
  if (bestMatch && bestMatch.score > 0.3) { // Minimum threshold for assignment
    const distance = Math.hypot(
      elementPosition.x - getRoomCenter(bestMatch.room.walls).x,
      elementPosition.y - getRoomCenter(bestMatch.room.walls).y
    );
    console.log(`📍 Element assigned to room "${bestMatch.room.name}" via intelligent proximity (score: ${bestMatch.score.toFixed(2)}, distance: ${distance.toFixed(1)}px)`);
    return bestMatch.room;
  }

  // Strategy 5: Last resort - closest room with detailed logging
  let closestRoom: Room | null = null;
  let closestDistance = Infinity;
  const absoluteMaxDistance = 150; // Absolute maximum for any assignment
  
  for (const room of rooms) {
    const roomCenter = getRoomCenter(room.walls);
    const distance = Math.hypot(
      elementPosition.x - roomCenter.x,
      elementPosition.y - roomCenter.y
    );
    
    if (distance < closestDistance && distance <= absoluteMaxDistance) {
      closestDistance = distance;
      closestRoom = room;
    }
  }
  
  if (closestRoom) {
    console.warn(`⚠️ Element assigned to room "${closestRoom.name}" as last resort (distance: ${closestDistance.toFixed(1)}px) - consider adjusting floor plan`);
    return closestRoom;
  }

  // No suitable room found
  console.error(`❌ NO ROOM FOUND for ${elementType || 'element'} at (${elementPosition.x}, ${elementPosition.y}):`, {
    totalRooms: rooms.length,
    roomNames: rooms.map(r => r.name),
    roomCenters: rooms.map(r => ({ name: r.name, center: getRoomCenter(r.walls) }))
  });
  return null;
};

/**
 * Get sophisticated buffer sizes based on element type and placement characteristics
 */
const getElementTypeBuffer = (elementType?: string): number => {
  switch (elementType) {
    case 'window':
      return 35; // Windows are wall-mounted and need generous buffer for wall detection
    case 'door':
      return 30; // Doors span wall boundaries and need substantial buffer
    case 'radiator':
      return 25; // Radiators are wall-mounted but positioned slightly away from walls
    case 'light':
      return 20; // Lights can be ceiling-mounted near walls or in room centers
    case 'utility':
      return 15; // Utilities can be in corners, against walls, or in specific areas
    case 'boiler':
      return 20; // Boilers are typically wall-mounted or in dedicated spaces
    case 'smoke-alarm':
      return 25; // Smoke alarms are ceiling-mounted and cover room areas
    default:
      return 15; // Conservative default buffer for unknown element types
  }
};

/**
 * Validates and reports on room consolidation effectiveness
 */
export const validateRoomConsolidation = (
  originalRooms: Room[],
  consolidatedRooms: Room[]
): { 
  originalCount: number;
  consolidatedCount: number;
  genericRoomsRemoved: number;
  consolidationReport: string[];
} => {
  const originalGenericCount = originalRooms.filter(r => /^Room \d+$/.test(r.name)).length;
  const consolidatedGenericCount = consolidatedRooms.filter(r => /^Room \d+$/.test(r.name)).length;
  
  const report = [
    `📊 Room Consolidation Report:`,
    `   Original rooms: ${originalRooms.length}`,
    `   Consolidated rooms: ${consolidatedRooms.length}`,
    `   Generic rooms removed: ${originalGenericCount - consolidatedGenericCount}`,
    `   Rooms consolidated: ${originalRooms.length - consolidatedRooms.length}`
  ];

  console.log(report.join('\n'));

  return {
    originalCount: originalRooms.length,
    consolidatedCount: consolidatedRooms.length,
    genericRoomsRemoved: originalGenericCount - consolidatedGenericCount,
    consolidationReport: report
  };
};

/**
 * Re-detects and updates element locations for existing files
 * This fixes old files where elements were placed with the old room detection system
 */
export const redetectElementLocations = (
  elements: FloorPlanElement[],
  floorPlans: Record<number, Wall[]>,
  roomCustomLabels: Record<string, string>
): FloorPlanElement[] => {
  const updatedElements: FloorPlanElement[] = [];
  let redetectedCount = 0;
  
  elements.forEach(element => {
    // Skip elements that don't have a floor property (BayWindow, TextElement, Compass)
    if (!('floor' in element)) {
      updatedElements.push(element);
      return;
    }
    
    // Skip elements that don't have a position property (Walls)
    if (!('position' in element)) {
      updatedElements.push(element);
      return;
    }
    
    // Get walls for this element's floor
    const floorWalls = floorPlans[element.floor] || [];
    
    if (floorWalls.length === 0) {
      // No walls on this floor, keep element as-is
      updatedElements.push(element);
      return;
    }
    
    // Calculate rooms for this floor using multiple contexts for better coverage
    let roomsForFloor: Room[] = [];
    
    // Try explicit room creation first (includes all rooms)
    try {
      roomsForFloor = memoizedCalculateRooms(floorWalls, roomCustomLabels, 'explicitRoomCreation', element.floor);
    } catch (error) {
      console.warn('Failed to calculate rooms with explicitRoomCreation, trying labelPreservation');
    }
    
    // If no rooms found, try label preservation
    if (roomsForFloor.length === 0) {
      try {
        roomsForFloor = memoizedCalculateRooms(floorWalls, roomCustomLabels, 'labelPreservation', element.floor);
      } catch (error) {
        console.warn('Failed to calculate rooms with labelPreservation, trying geometryUpdate');
      }
    }
    
    // If still no rooms, try geometry update as last resort
    if (roomsForFloor.length === 0) {
      try {
        roomsForFloor = memoizedCalculateRooms(floorWalls, roomCustomLabels, 'geometryUpdate', element.floor);
      } catch (error) {
        console.error('Failed to calculate rooms with any context');
        updatedElements.push(element);
        return;
      }
    }
    
    // Find the room this element should be in using enhanced detection
    const correctRoom = findElementRoomWithConsolidation(element.position, roomsForFloor, element.type);
    
    if (correctRoom && correctRoom.name) {
      // Update element with correct room information
      const updatedElement = {
        ...element,
        roomName: correctRoom.name,
        roomId: correctRoom.id
      };
      
      updatedElements.push(updatedElement);
      redetectedCount++;
    } else {
      // Keep original element if no valid room found
      updatedElements.push(element);
      console.warn(`⚠️ Could not re-detect room for ${element.type} at (${element.position.x}, ${element.position.y})`);
    }
  });
  
  console.log(`✅ Re-detection complete: ${redetectedCount}/${elements.length} elements updated`);
  return updatedElements;
};

/**
 * Ultra-comprehensive sync with advanced re-detection and validation
 * This provides the highest possible accuracy for element-room assignments
 */
export const comprehensiveSyncWithRedetection = (
  elements: FloorPlanElement[],
  floorPlans: Record<number, Wall[]>,
  roomCustomLabels: Record<string, string>,
  siteNotesData: SiteNotesData
): { 
  updatedElements: FloorPlanElement[];
  updatedSiteNotesData: SiteNotesData;
  redetectionReport: string;
  accuracyReport: {
    totalElements: number;
    successfulAssignments: number;
    wallBasedAssignments: number;
    geometricAssignments: number;
    proximityAssignments: number;
    failedAssignments: number;
    accuracyPercentage: number;
  };
} => {
  console.log('🚀 Starting ultra-comprehensive sync with advanced detection...');
  
  let wallBasedCount = 0;
  let geometricCount = 0;
  let proximityCount = 0;
  let failedCount = 0;
  
  // Step 1: Enhanced element re-detection with tracking
  const redetectedElements: FloorPlanElement[] = [];
  let redetectedCount = 0;
  
  elements.forEach(element => {
    // Debug light elements specifically
    if ((element as any).type === 'light') {
      console.log('💡 DEBUG: Processing light element in comprehensiveSyncWithRedetection:', {
        id: (element as any).id,
        type: (element as any).type,
        hasFloor: 'floor' in element,
        floorValue: (element as any).floor,
        hasPosition: 'position' in element,
        position: (element as any).position
      });
    }
    
    // Skip elements that don't have a floor property
    if (!('floor' in element)) {
      if ((element as any).type === 'light') {
        console.log('💡 DEBUG: Light element missing floor property, keeping as-is');
      }
      redetectedElements.push(element);
      return;
    }
    
    // Skip elements that don't have a position property
    if (!('position' in element)) {
      if ((element as any).type === 'light') {
        console.log('💡 DEBUG: Light element missing position property, keeping as-is');
      }
      redetectedElements.push(element);
      return;
    }
    
    // Get walls for this element's floor
    const floorWalls = floorPlans[element.floor] || [];
    
    if (floorWalls.length === 0) {
      redetectedElements.push(element);
      return;
    }
    
    // Calculate rooms with multiple fallback strategies
    let roomsForFloor: Room[] = [];
    
    // Try multiple room calculation contexts for maximum coverage
    const contexts: RoomCalculationContext[] = ['explicitRoomCreation', 'labelPreservation', 'geometryUpdate'];
    
    for (const context of contexts) {
      try {
        roomsForFloor = memoizedCalculateRooms(floorWalls, roomCustomLabels, context, element.floor);
        if (roomsForFloor.length > 0) {
          break;
        }
      } catch (error) {
        console.warn(`Failed to calculate rooms with ${context}, trying next strategy`);
      }
    }
    
    if (roomsForFloor.length === 0) {
      console.error(`No rooms calculated for floor ${element.floor}, keeping element unchanged`);
      redetectedElements.push(element);
      return;
    }
    
    // Enhanced element detection with detailed tracking
    const originalDetectionLog = console.log;
    let detectionMethod = 'unknown';
    
    // Override console.log temporarily to capture detection method
    console.log = (...args: any[]) => {
      const message = args.join(' ');
      if (message.includes('via wall proximity')) detectionMethod = 'wall-based';
      else if (message.includes('via strict containment')) detectionMethod = 'geometric';
      else if (message.includes('via enhanced boundary')) detectionMethod = 'boundary';
      else if (message.includes('via intelligent proximity')) detectionMethod = 'intelligent-proximity';
      else if (message.includes('as last resort')) detectionMethod = 'last-resort';
      originalDetectionLog(...args);
    };
    
    const correctRoom = findElementRoomWithConsolidation(element.position, roomsForFloor, element.type);
    
    // Restore original console.log
    console.log = originalDetectionLog;
    
    // Track detection method statistics
    if (correctRoom) {
      switch (detectionMethod) {
        case 'wall-based':
          wallBasedCount++;
          break;
        case 'geometric':
        case 'boundary':
          geometricCount++;
          break;
        case 'intelligent-proximity':
        case 'last-resort':
          proximityCount++;
          break;
      }
    } else {
      failedCount++;
    }
    
    if (correctRoom && correctRoom.name) {
      // Update element with enhanced room information
      const updatedElement = {
        ...element,
        roomName: correctRoom.name,
        roomId: correctRoom.id,
        detectionMethod, // Add detection method for debugging
        assignmentConfidence: detectionMethod === 'wall-based' ? 'high' : 
                             detectionMethod === 'geometric' ? 'high' :
                             detectionMethod === 'boundary' ? 'medium' :
                             detectionMethod === 'intelligent-proximity' ? 'medium' : 'low'
      };
      
      if ((element as any).type === 'light') {
        console.log('💡 DEBUG: Adding light element to redetectedElements with room assignment:', {
          id: (updatedElement as any).id,
          roomName: updatedElement.roomName,
          detectionMethod
        });
      }
      
      redetectedElements.push(updatedElement);
      redetectedCount++;
    } else {
      if ((element as any).type === 'light') {
        console.log('💡 DEBUG: Adding light element to redetectedElements WITHOUT room assignment:', {
          id: (element as any).id,
          position: (element as any).position
        });
      }
      redetectedElements.push(element);
      console.warn(`⚠️ Could not assign ${(element as any).type} at (${(element as any).position.x}, ${(element as any).position.y}) to any room`);
    }
  });
  
  // Step 2: Cross-validation and accuracy verification
  const assignedElements = redetectedElements.filter(el => 
    'roomName' in el && el.roomName && typeof el.roomName === 'string' && el.roomName.trim() !== ''
  );
  
  console.log(`🔍 Element assignment summary:`, {
    totalElements: elements.length,
    redetectedElements: redetectedCount,
    wallBasedAssignments: wallBasedCount,
    geometricAssignments: geometricCount,
    proximityAssignments: proximityCount,
    failedAssignments: failedCount,
    assignmentRate: `${((assignedElements.length / elements.length) * 100).toFixed(1)}%`
  });
  
  // Step 3: Group elements by type and floor for systematic sync
  console.log('💡 DEBUG: Checking redetectedElements for lights:', {
    totalRedetectedElements: redetectedElements.length,
    lightElementsFound: redetectedElements.filter(el => (el as any).type === 'light').length,
    lightElementIds: redetectedElements.filter(el => (el as any).type === 'light').map(el => (el as any).id)
  });
  
  const allWindows = redetectedElements.filter(el => el.type === 'window') as WindowElement[];
  const allDoors = redetectedElements.filter(el => el.type === 'door') as DoorElement[];
  const allRadiators = redetectedElements.filter(el => el.type === 'radiator') as RadiatorElement[];
  const allBoilers = redetectedElements.filter(el => el.type === 'boiler') as BoilerElement[];
  const allLights = redetectedElements.filter(el => (el as any).type === 'light') as LightElement[];
  const allUtilities = redetectedElements.filter(el => el.type === 'utility') as UtilityElement[];
  const allSmokeAlarms = redetectedElements.filter(el => el.type === 'smoke-alarm');
  
  console.log('💡 DEBUG: Light elements after filtering:', {
    allLightsCount: allLights.length,
    allLightsIds: allLights.map(l => (l as any).id)
  });
  
  // Step 4: Enhanced room calculation and sync
  let updatedSiteNotesData = JSON.parse(JSON.stringify(siteNotesData));
  const floorNumbers = Object.keys(floorPlans).map(Number).sort();
  let totalSyncedItems = 0;
  
  // Pre-calculate all rooms with multiple strategies for maximum accuracy
  const allFloorRooms: Record<number, Room[]> = {};
  const syncableFloorRooms: Record<number, Room[]> = {};
  
  for (const floorNum of floorNumbers) {
    const wallsForFloor = floorPlans[floorNum] || [];
    
    console.log(`🏠 Enhanced room calculation for floor ${floorNum}:`, {
      wallCount: wallsForFloor.length,
      customLabelsCount: Object.keys(roomCustomLabels).length
    });
    
    // Use all available strategies to ensure maximum room coverage
    let roomsForFloor: Room[] = [];
    const roomSets: Room[][] = [];
    
    // Try all contexts and merge results
    const contexts: RoomCalculationContext[] = ['explicitRoomCreation', 'labelPreservation', 'geometryUpdate'];
    
    for (const context of contexts) {
      try {
        const contextRooms = memoizedCalculateRooms(wallsForFloor, roomCustomLabels, context, floorNum);
        if (contextRooms.length > 0) {
          roomSets.push(contextRooms);
        }
      } catch (error) {
        console.warn(`Floor ${floorNum}: Failed ${context}`);
      }
    }
    
    // Use the room set with the most comprehensive coverage
    if (roomSets.length > 0) {
      // Choose the room set with the best balance of count and valid names
      roomsForFloor = roomSets.reduce((best, current) => {
        const currentScore = current.length + current.filter(r => 
          r.name && !r.name.match(/^Room \d+$/)
        ).length;
        const bestScore = best.length + best.filter(r => 
          r.name && !r.name.match(/^Room \d+$/)
        ).length;
        return currentScore > bestScore ? current : best;
      });
    }
    
    // Store ALL rooms for element detection
    allFloorRooms[floorNum] = roomsForFloor;
    
    // Filter rooms for surveyor sync
    const syncableRooms = roomsForFloor.filter(room => {
      const hasValidId = (room.id && room.id.trim() !== '') || (room.stableId && room.stableId.trim() !== '');
      const hasValidName = room.name && room.name.trim() !== '';
      const isAutoGeneratedName = /^Room \d+$/.test(room.name || '');
      const hasCustomLabel = room.stableId && roomCustomLabels && roomCustomLabels[room.stableId] && typeof roomCustomLabels[room.stableId] === 'string' && roomCustomLabels[room.stableId].trim() !== '';
      
      const shouldSync = hasValidId && hasValidName && (hasCustomLabel || !isAutoGeneratedName);
      

      
      return shouldSync;
    });
    
    syncableFloorRooms[floorNum] = syncableRooms;
    
    console.log(`🏠 Floor ${floorNum} comprehensive room analysis:`, {
      totalCalculated: roomsForFloor.length,
      syncableRooms: syncableRooms.length,
      namedRooms: roomsForFloor.filter(r => r.name && !r.name.match(/^Room \d+$/)).length,
      customLabeledRooms: roomsForFloor.filter(r => r.stableId && roomCustomLabels[r.stableId] && typeof roomCustomLabels[r.stableId] === 'string').length
    });
  }
  
  // Step 5: Floor-by-floor comprehensive sync
  for (const floorNum of floorNumbers) {
    const syncableRoomsForFloor = syncableFloorRooms[floorNum];
    const allRoomsForFloor = allFloorRooms[floorNum];
    
         // Sync rooms with consolidation
     if (syncableRoomsForFloor.length > 0) {

       updatedSiteNotesData = syncAllRoomsToSurveyorData(syncableRoomsForFloor, updatedSiteNotesData, floorNum, roomCustomLabels, true);
       totalSyncedItems += syncableRoomsForFloor.length;
     } else {

     }
     
     // Enhanced element sync with detailed logging
     const floorWindows = allWindows.filter(w => w.floor === floorNum);
     const floorDoors = allDoors.filter(d => d.floor === floorNum);
     const floorRadiators = allRadiators.filter(r => r.floor === floorNum);
     const floorLights = allLights.filter(l => l.floor === floorNum);
     const floorUtilities = allUtilities.filter(u => u.floor === floorNum);
     const floorSmokeAlarms = allSmokeAlarms.filter(s => s.floor === floorNum);
     
     console.log(`🔧 Floor ${floorNum} element sync:`, {
       windows: floorWindows.length,
       doors: floorDoors.length,
       radiators: floorRadiators.length,
       lights: floorLights.length,
       utilities: floorUtilities.length,
       smokeAlarms: floorSmokeAlarms.length,
       availableRooms: allRoomsForFloor.length
     });
     
     // Smart sync with comprehensive coverage
     if (floorWindows.length > 0) {
       updatedSiteNotesData = smartSyncWindowsToRoom(floorWindows, allRoomsForFloor, updatedSiteNotesData, floorNum);
       totalSyncedItems += floorWindows.length;
     }
     
     if (floorRadiators.length > 0) {
       updatedSiteNotesData = smartSyncRadiatorsToRoom(floorRadiators, allRoomsForFloor, updatedSiteNotesData, floorNum);
       totalSyncedItems += floorRadiators.length;
     }
     
     if (floorLights.length > 0) {
       updatedSiteNotesData = smartSyncLightsToRooms(floorLights, allRoomsForFloor, updatedSiteNotesData, floorNum, true);
       totalSyncedItems += floorLights.length;
     }
     
     if (floorUtilities.length > 0) {
       updatedSiteNotesData = syncUtilityLocationsToSiteNotes(floorUtilities, allRoomsForFloor, updatedSiteNotesData);
       totalSyncedItems += floorUtilities.length;
     }
   }
   
   // Step 6: Final cleanup and cross-floor aggregations
   updatedSiteNotesData = cleanupDuplicateRooms(updatedSiteNotesData);
   
   // Comprehensive cross-floor syncing
   updatedSiteNotesData = syncAllFloorsWindowsToSiteNotesWithFloorPlans(allWindows, floorPlans, roomCustomLabels, updatedSiteNotesData);
   updatedSiteNotesData = syncAllFloorsDoorUndercutsToSiteNotesWithFloorPlans(allDoors, floorPlans, roomCustomLabels, updatedSiteNotesData);
   updatedSiteNotesData = syncAllFloorsLightsToAdditionalInfo(updatedSiteNotesData);
   updatedSiteNotesData = syncAllFloorsRadiatorsToHeatingSystem(updatedSiteNotesData);
   
   // Generate comprehensive reports
   const accuracyReport = {
     totalElements: elements.length,
     successfulAssignments: assignedElements.length,
     wallBasedAssignments: wallBasedCount,
     geometricAssignments: geometricCount,
     proximityAssignments: proximityCount,
     failedAssignments: failedCount,
     accuracyPercentage: (assignedElements.length / elements.length) * 100
   };
   
   const redetectionReport = `Ultra-comprehensive sync completed: ${totalSyncedItems} items synced across ${floorNumbers.length} floors with ${accuracyReport.accuracyPercentage.toFixed(1)}% accuracy (${accuracyReport.wallBasedAssignments} wall-based, ${accuracyReport.geometricAssignments} geometric, ${accuracyReport.proximityAssignments} proximity-based assignments)`;
   
   console.log(`✅ ${redetectionReport}`);
   
   // Final debug check for light elements
   const finalLightElements = redetectedElements.filter(el => (el as any).type === 'light');
   console.log('💡 DEBUG: Final redetectedElements being returned:', {
     totalElements: redetectedElements.length,
     lightElementsCount: finalLightElements.length,
     lightElementsIds: finalLightElements.map(l => (l as any).id)
   });
   
   return {
     updatedElements: redetectedElements,
     updatedSiteNotesData,
     redetectionReport,
     accuracyReport
   };
};

/**
 * Enhanced room sync that consolidates related rooms before syncing
 */
export const syncAllRoomsToSurveyorDataWithConsolidation = (
  floorPlanRooms: Room[],
  siteNotesData: SiteNotesData,
  floorNumber: number = 1,
  roomCustomLabels: Record<string, string> = {},
  skipCleanup: boolean = false,
  creationContext?: {
    isFromEndpoint?: boolean;
    isExplicitRoomCreation?: boolean;
    originalRoomCount?: number; // Track original room count for endpoint creation
  }
): SiteNotesData => {
  console.log('🔧 SYNC WITH CONSOLIDATION: Starting', {
    inputRoomCount: floorPlanRooms.length,
    floorNumber,
    skipCleanup,
    creationContext,
    inputRooms: floorPlanRooms.map(r => ({
      name: r.name,
      area: r.area.toFixed(2),
      stableId: r.stableId?.substring(0, 30) + '...'
    }))
  });

  // First consolidate related rooms (main + excursions)
  const consolidatedRooms = consolidateRelatedRooms(floorPlanRooms, creationContext, roomCustomLabels);
  
  console.log('🔧 SYNC WITH CONSOLIDATION: After consolidation', {
    originalRoomCount: floorPlanRooms.length,
    consolidatedRoomCount: consolidatedRooms.length,
    wasConsolidationSkipped: consolidatedRooms.length === floorPlanRooms.length,
    consolidatedRooms: consolidatedRooms.map(r => ({
      name: r.name,
      area: r.area.toFixed(2),
      stableId: r.stableId?.substring(0, 30) + '...'
    }))
  });
  
  // Validate consolidation effectiveness
  validateRoomConsolidation(floorPlanRooms, consolidatedRooms);
  
  // Then apply the existing sync logic with custom labels
  let result = syncAllRoomsToSurveyorData(consolidatedRooms, siteNotesData, floorNumber, roomCustomLabels);
  
  // Only cleanup if not skipped (allows caller to control timing)
  if (!skipCleanup) {
    result = cleanupDuplicateRooms(result);
  }
  
  console.log('🔧 SYNC WITH CONSOLIDATION: Completed', {
    finalRoomCount: consolidatedRooms.length,
    skippedCleanup: skipCleanup
  });
  
  return result;
};

/**
 * Comprehensive sync validation and accuracy analysis
 * This function provides detailed reporting on sync quality and identifies potential issues
 */
export const validateSyncAccuracy = (
  elements: FloorPlanElement[],
  siteNotesData: SiteNotesData,
  floorPlans: Record<number, Wall[]>,
  roomCustomLabels: Record<string, string>
): {
  overallAccuracy: number;
  elementValidation: {
    type: string;
    total: number;
    synced: number;
    accuracy: number;
    issues: string[];
  }[];
  roomValidation: {
    floorNumber: number;
    totalRooms: number;
    syncedRooms: number;
    missingRooms: string[];
    duplicateRooms: string[];
  }[];
  recommendations: string[];
  detailedReport: string;
} => {
  console.log('🔍 Starting comprehensive sync validation...');
  
  const elementTypes = ['window', 'door', 'radiator', 'light', 'utility', 'boiler', 'smoke-alarm'];
  const elementValidation: any[] = [];
  const roomValidation: any[] = [];
  const recommendations: string[] = [];
  
  // Validate elements by type
  for (const type of elementTypes) {
    const elementsOfType = elements.filter(el => el.type === type);
    const syncedElements = elementsOfType.filter(el => 
      'roomName' in el && el.roomName && typeof el.roomName === 'string' && el.roomName.trim() !== ''
    );
    
    const accuracy = elementsOfType.length > 0 ? (syncedElements.length / elementsOfType.length) * 100 : 100;
    const issues: string[] = [];
    
    // Identify specific issues
    const unsyncedElements = elementsOfType.filter(el => 
      !('roomName' in el) || !el.roomName || typeof el.roomName !== 'string' || el.roomName.trim() === ''
    );
    
    if (unsyncedElements.length > 0) {
      issues.push(`${unsyncedElements.length} ${type}(s) not assigned to any room`);
      
      // Detailed analysis of why elements weren't synced
      unsyncedElements.forEach((el, index) => {
        if ('position' in el && 'floor' in el && el.position && typeof el.floor === 'number') {
          const floorWalls = floorPlans[el.floor] || [];
          if (floorWalls.length === 0) {
            issues.push(`${type} ${index + 1}: No walls available on floor ${el.floor}`);
          } else {
            try {
              const rooms = memoizedCalculateRooms(floorWalls, roomCustomLabels, 'labelPreservation', el.floor);
              if (rooms.length === 0) {
                issues.push(`${type} ${index + 1}: No rooms calculated for floor ${el.floor}`);
              } else {
                issues.push(`${type} ${index + 1}: Position (${el.position.x}, ${el.position.y}) not assignable to any of ${rooms.length} rooms`);
              }
            } catch (error) {
              issues.push(`${type} ${index + 1}: Room calculation failed for floor ${el.floor}`);
            }
          }
        }
      });
    }
    
    // Check for elements with low confidence assignments
    const lowConfidenceElements = syncedElements.filter(el => 
      'assignmentConfidence' in el && el.assignmentConfidence === 'low'
    );
    
    if (lowConfidenceElements.length > 0) {
      issues.push(`${lowConfidenceElements.length} ${type}(s) have low-confidence room assignments`);
    }
    
    elementValidation.push({
      type,
      total: elementsOfType.length,
      synced: syncedElements.length,
      accuracy,
      issues
    });
    
    // Add recommendations based on accuracy
    if (accuracy < 95) {
      recommendations.push(`${type} sync accuracy is ${accuracy.toFixed(1)}% - consider reviewing floor plan geometry`);
    }
    if (lowConfidenceElements.length > 0) {
      recommendations.push(`${lowConfidenceElements.length} ${type}(s) have low-confidence assignments - manual review recommended`);
    }
  }
  
  // Validate rooms by floor
  const floorNumbers = Object.keys(floorPlans).map(Number).sort();
  
  for (const floorNumber of floorNumbers) {
    const wallsForFloor = floorPlans[floorNumber] || [];
    const floorData = siteNotesData.floors.find(f => f.id === floorNumber);
    
    if (wallsForFloor.length === 0) {
      roomValidation.push({
        floorNumber,
        totalRooms: 0,
        syncedRooms: 0,
        missingRooms: [],
        duplicateRooms: []
      });
      continue;
    }
    
    let calculatedRooms: Room[] = [];
    try {
      calculatedRooms = memoizedCalculateRooms(wallsForFloor, roomCustomLabels, 'labelPreservation', floorNumber);
    } catch (error) {
      console.warn(`Failed to calculate rooms for floor ${floorNumber}`);
    }
    
    const validCalculatedRooms = calculatedRooms.filter(room => {
      const hasValidId = (room.id && room.id.trim() !== '') || (room.stableId && room.stableId.trim() !== '');
      const hasValidName = room.name && room.name.trim() !== '';
      const isAutoGeneratedName = /^Room \d+$/.test(room.name || '');
      const hasCustomLabel = room.stableId && roomCustomLabels && roomCustomLabels[room.stableId] && typeof roomCustomLabels[room.stableId] === 'string';
      
      return hasValidId && hasValidName && (hasCustomLabel || !isAutoGeneratedName);
    });
    
    const syncedRoomsCount = floorData ? floorData.rooms.filter(room => 
      room.length && room.width && 
      parseFloat(room.length) > 0 && 
      parseFloat(room.width) > 0
    ).length : 0;
    
    // Find missing rooms (calculated but not synced)
    const missingRooms: string[] = [];
    if (floorData) {
      validCalculatedRooms.forEach(calcRoom => {
        const foundInSiteNotes = floorData.rooms.find(room => 
          room.name === calcRoom.name && room.length && room.width
        );
        if (!foundInSiteNotes) {
          missingRooms.push(calcRoom.name);
        }
      });
    } else {
      missingRooms.push(...validCalculatedRooms.map(r => r.name));
    }
    
    // Find duplicate rooms (same name appearing multiple times)
    const duplicateRooms: string[] = [];
    if (floorData) {
      const roomNames = floorData.rooms.map(r => r.name).filter((name): name is string => name != null && name.trim() !== '');
      const nameCount = roomNames.reduce((acc, name) => {
        acc[name] = (acc[name] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);
      
      Object.entries(nameCount).forEach(([name, count]) => {
        if (count > 1) {
          duplicateRooms.push(`${name} (${count} instances)`);
        }
      });
    }
    
    roomValidation.push({
      floorNumber,
      totalRooms: validCalculatedRooms.length,
      syncedRooms: syncedRoomsCount,
      missingRooms,
      duplicateRooms
    });
    
    // Add recommendations for room issues
    if (missingRooms.length > 0) {
      recommendations.push(`Floor ${floorNumber}: ${missingRooms.length} calculated rooms not synced - check room labels and naming`);
    }
    if (duplicateRooms.length > 0) {
      recommendations.push(`Floor ${floorNumber}: Found duplicate rooms - ${duplicateRooms.join(', ')}`);
    }
  }
  
  // Calculate overall accuracy
  const totalElements = elements.length;
  const totalSyncedElements = elements.filter(el => 
    'roomName' in el && el.roomName && typeof el.roomName === 'string' && el.roomName.trim() !== ''
  ).length;
  const overallAccuracy = totalElements > 0 ? (totalSyncedElements / totalElements) * 100 : 100;
  
  // Generate detailed report
  const detailedReport = `
SYNC VALIDATION REPORT
=====================

Overall Accuracy: ${overallAccuracy.toFixed(1)}%
Total Elements: ${totalElements}
Successfully Synced: ${totalSyncedElements}

ELEMENT BREAKDOWN:
${elementValidation.map(ev => `
  ${ev.type.toUpperCase()}:
    - Total: ${ev.total}
    - Synced: ${ev.synced}
    - Accuracy: ${ev.accuracy.toFixed(1)}%
    ${ev.issues.length > 0 ? '    - Issues: ' + ev.issues.join('; ') : '    - No issues detected'}
`).join('')}

ROOM BREAKDOWN:
${roomValidation.map(rv => `
  Floor ${rv.floorNumber}:
    - Calculated Rooms: ${rv.totalRooms}
    - Synced Rooms: ${rv.syncedRooms}
    ${rv.missingRooms.length > 0 ? '    - Missing: ' + rv.missingRooms.join(', ') : ''}
    ${rv.duplicateRooms.length > 0 ? '    - Duplicates: ' + rv.duplicateRooms.join(', ') : ''}
`).join('')}

RECOMMENDATIONS:
${recommendations.length > 0 ? recommendations.map(r => `- ${r}`).join('\n') : '- No recommendations - sync quality is excellent!'}
  `.trim();
  
  console.log('📊 Sync validation complete:', {
    overallAccuracy: `${overallAccuracy.toFixed(1)}%`,
    totalRecommendations: recommendations.length
  });
  
  return {
    overallAccuracy,
    elementValidation,
    roomValidation,
    recommendations,
    detailedReport
  };
};

/**
 * Migration function to re-evaluate existing radiators using improved text-based detection
 * This helps fix radiator assignments in old files that may have been incorrectly assigned
 */
export const migrateRadiatorRoomAssignments = (
  radiators: RadiatorElement[],
  rooms: Room[],
  siteNotesData: SiteNotesData,
  floorNumber: number = 1
): { updatedSiteNotesData: SiteNotesData; migratedCount: number; } => {
  console.log(`🔄 Migrating radiator room assignments for floor ${floorNumber}...`);
  
  let updatedSiteNotesData = JSON.parse(JSON.stringify(siteNotesData));
  let migratedCount = 0;
  
  // Clear existing radiator data for this floor to start fresh
  const targetFloor = updatedSiteNotesData.floors.find((f: any) => f.id === floorNumber);
  if (!targetFloor) {
    console.warn(`Floor ${floorNumber} not found during migration`);
    return { updatedSiteNotesData: siteNotesData, migratedCount: 0 };
  }
  
  // Clear all radiator data in rooms for this floor
  targetFloor.rooms.forEach((room: any) => {
    const hadRadiatorData = room.radiatorWidth || room.radiatorHeight || (room.radiators && room.radiators.length > 0);
    if (hadRadiatorData) {
      room.radiatorWidth = '';
      room.radiatorHeight = '';
      room.radiatorType = '';
      room.radiators = [];
    }
  });
  
  // Re-sync all radiators using the new text-based detection
  updatedSiteNotesData = smartSyncRadiatorsToRoom(radiators, rooms, updatedSiteNotesData, floorNumber, false);
  
  // Count how many radiators were successfully migrated
  targetFloor.rooms.forEach((room: any) => {
    if (room.radiatorWidth || room.radiatorHeight || (room.radiators && room.radiators.length > 0)) {
      migratedCount++;
    }
  });
  
  console.log(`🔄 Migration complete: ${migratedCount} radiators re-assigned using text-based detection`);
  
  return { updatedSiteNotesData, migratedCount };
};

/**
 * Re-evaluates room assignments for wall-mounted elements when wall types change
 * This should be called whenever wall types are modified to ensure elements
 * are assigned to the correct rooms, especially for shared walls
 */
export const reassignElementsAfterWallTypeChange = (
  elements: FloorPlanElement[],
  rooms: Room[],
  siteNotesData: SiteNotesData,
  floorNumber: number = 1
): { 
  updatedElements: FloorPlanElement[];
  updatedSiteNotesData: SiteNotesData;
  reassignmentReport: string;
} => {
  console.log(`🔄 WALL TYPE CHANGE: Re-evaluating room assignments for ${elements.length} elements`);
  
  const reassignmentCounts = {
    windows: 0,
    radiators: 0,
    doors: 0,
    unchanged: 0,
    total: elements.length
  };
  
  const reassignments: string[] = [];
  
  // Filter elements that need reassignment (wall-mounted elements)
  const elementsToReassign = elements.filter(el => {
    return ['window', 'radiator', 'door'].includes(el.type) && 
           (el as any).wallId && 
           (el as any).floor === floorNumber;
  });
  
  console.log(`🔍 Found ${elementsToReassign.length} wall-mounted elements on floor ${floorNumber} to re-evaluate`);
  
  // Re-assign each element using the enhanced findRoomByWallId function
  const updatedElements = elements.map(element => {
    const typedElement = element as any;
    
    // Only process wall-mounted elements on the current floor
    if (!['window', 'radiator', 'door'].includes(element.type) || 
        !typedElement.wallId || 
        typedElement.floor !== floorNumber) {
      return element;
    }
    
    // Find the new room assignment using enhanced wall-based detection
    const newRoom = findRoomByWallId(typedElement.wallId, rooms, typedElement.position);
    
    if (newRoom) {
      const oldRoomName = typedElement.roomName || 'Unknown';
      const newRoomName = newRoom.name;
      
      if (oldRoomName !== newRoomName) {
        // Room assignment changed
        reassignments.push(`${element.type} moved from "${oldRoomName}" to "${newRoomName}"`);
        reassignmentCounts[element.type as keyof typeof reassignmentCounts]++;
        
        console.log(`🔄 REASSIGNED: ${element.type} at (${typedElement.position.x}, ${typedElement.position.y}) moved from "${oldRoomName}" to "${newRoomName}"`);
        
        return {
          ...element,
          roomName: newRoomName,
          roomId: newRoom.id
        };
      } else {
        // Room assignment unchanged
        reassignmentCounts.unchanged++;
        return element;
      }
    } else {
      // Could not find room assignment
      console.warn(`⚠️ Could not reassign ${element.type} at (${typedElement.position.x}, ${typedElement.position.y}) - no room found for wall ID "${typedElement.wallId}"`);
      reassignmentCounts.unchanged++;
      return element;
    }
  });
  
  // Update site notes data with new room assignments
  let updatedSiteNotesData = { ...siteNotesData };
  
  // Group reassigned elements by type for site notes sync
  const reassignedWindows = updatedElements.filter(el => el.type === 'window') as WindowElement[];
  const reassignedRadiators = updatedElements.filter(el => el.type === 'radiator') as RadiatorElement[];
  
  // Sync windows to site notes if any were reassigned
  if (reassignmentCounts.windows > 0) {
    updatedSiteNotesData = smartSyncWindowsToRoom(
      reassignedWindows,
      rooms,
      updatedSiteNotesData,
      floorNumber,
      true // isManualSync = true to force update
    );
  }
  
  // Sync radiators to site notes if any were reassigned
  if (reassignmentCounts.radiators > 0) {
    updatedSiteNotesData = smartSyncRadiatorsToRoom(
      reassignedRadiators,
      rooms,
      updatedSiteNotesData,
      floorNumber,
      true // isManualSync = true to force update
    );
  }
  
  const reassignmentReport = `Wall Type Change Reassignment Summary:
- Windows reassigned: ${reassignmentCounts.windows}
- Radiators reassigned: ${reassignmentCounts.radiators}
- Doors reassigned: ${reassignmentCounts.doors}
- Elements unchanged: ${reassignmentCounts.unchanged}
- Total elements processed: ${reassignmentCounts.total}

Specific reassignments:
${reassignments.length > 0 ? reassignments.map(r => `• ${r}`).join('\n') : '• No reassignments needed'}`;
  
  console.log(`✅ WALL TYPE CHANGE COMPLETE: ${reassignmentCounts.windows + reassignmentCounts.radiators + reassignmentCounts.doors} elements reassigned`);
  
  return {
    updatedElements,
    updatedSiteNotesData,
    reassignmentReport
  };
};

/**
 * Comprehensive window sync debugging and forced resync function
 * This function helps diagnose and fix window sync issues
 */
export const debugAndForceWindowSync = (
  windows: WindowElement[],
  rooms: Room[],
  siteNotesData: SiteNotesData,
  floorNumber: number = 1
): {
  updatedSiteNotesData: SiteNotesData;
  debugReport: string;
  syncResults: {
    totalWindows: number;
    processedWindows: number;
    syncedWindows: number;
    failedWindows: number;
    windowsByRoom: Record<string, number>;
  };
} => {
  console.log('🔍 WINDOW SYNC DEBUG: Starting comprehensive window sync analysis...');
  
  // Clear sync state to force reprocessing
  clearWindowSyncState();
  
  const debugInfo: string[] = [];
  debugInfo.push('🔍 WINDOW SYNC DEBUG REPORT');
  debugInfo.push('=================================');
  debugInfo.push(`Total windows to process: ${windows.length}`);
  debugInfo.push(`Target floor: ${floorNumber}`);
  debugInfo.push(`Available rooms: ${rooms.length} (${rooms.map(r => r.name).join(', ')})`);
  debugInfo.push('');
  
  let processedCount = 0;
  let syncedCount = 0;
  let failedCount = 0;
  const windowsByRoom: Record<string, number> = {};
  
  // Force sync with manual flag to override any state checks
  const updatedSiteNotesData = smartSyncWindowsToRoom(windows, rooms, siteNotesData, floorNumber, true);
  
  // Analyze each window individually
  windows.forEach((window, index) => {
    debugInfo.push(`Window ${index + 1}: ID=${window.id}, Floor=${window.floor}, Position=(${window.position.x.toFixed(1)}, ${window.position.y.toFixed(1)})`);
    debugInfo.push(`  Dimensions: ${window.width || 'none'} x ${window.height || 'none'}, Orientation: ${window.orientation || 'none'}`);
    debugInfo.push(`  Wall ID: ${window.wallId || 'none'}`);
    
    if (window.floor !== floorNumber) {
      debugInfo.push(`  ❌ SKIPPED: Wrong floor (${window.floor} vs ${floorNumber})`);
      return;
    }
    
    processedCount++;
    
    // Try to find room using the same logic as sync
    let room: Room | null = null;
    
    if (window.wallId) {
      room = findRoomByWallId(window.wallId, rooms, window.position);
      if (room) {
        debugInfo.push(`  ✅ WALL-BASED: Found room "${room.name}"`);
      }
    }
    
    if (!room) {
      room = findElementRoomWithConsolidation(window.position, rooms, 'window');
      if (room) {
        debugInfo.push(`  ⚠️ COORDINATE-BASED: Found room "${room.name}"`);
      }
    }
    
    if (room && room.name && !/^Room \d+$/.test(room.name)) {
      syncedCount++;
      windowsByRoom[room.name] = (windowsByRoom[room.name] || 0) + 1;
      debugInfo.push(`  ✅ SYNCED: Assigned to room "${room.name}"`);
    } else {
      failedCount++;
      debugInfo.push(`  ❌ FAILED: Could not assign to valid room`);
      if (room) {
        debugInfo.push(`    Found room: "${room.name}" but it's auto-generated or invalid`);
      }
    }
    debugInfo.push('');
  });
  
  debugInfo.push('SYNC SUMMARY:');
  debugInfo.push(`  Total windows: ${windows.length}`);
  debugInfo.push(`  Processed: ${processedCount}`);
  debugInfo.push(`  Successfully synced: ${syncedCount}`);
  debugInfo.push(`  Failed to sync: ${failedCount}`);
  debugInfo.push(`  Success rate: ${processedCount > 0 ? Math.round((syncedCount / processedCount) * 100) : 0}%`);
  debugInfo.push('');
  debugInfo.push('WINDOWS BY ROOM:');
  Object.entries(windowsByRoom).forEach(([roomName, count]) => {
    debugInfo.push(`  ${roomName}: ${count} window(s)`);
  });
  
  const debugReport = debugInfo.join('\n');
  console.log(debugReport);
  
  return {
    updatedSiteNotesData,
    debugReport,
    syncResults: {
      totalWindows: windows.length,
      processedWindows: processedCount,
      syncedWindows: syncedCount,
      failedWindows: failedCount,
      windowsByRoom
    }
  };
};

/**
 * Removes all data for a specific room from site notes when the room is deleted
 * This ensures that element assignments and room dimensions are properly cleaned up
 */
export const removeRoomFromSiteNotes = (
  roomName: string,
  floorNumber: number,
  siteNotesData: SiteNotesData
): SiteNotesData => {
  const updatedSiteNotesData = JSON.parse(JSON.stringify(siteNotesData));
  
  // Find the target floor
  const targetFloor = updatedSiteNotesData.floors.find((f: SurveyorFloor) => f.id === floorNumber);
  if (!targetFloor) {
    console.warn(`Floor ${floorNumber} not found when removing room "${roomName}"`);
    return siteNotesData;
  }

  // Remove the room from the floor
  const originalRoomCount = targetFloor.rooms.length;
  targetFloor.rooms = targetFloor.rooms.filter((room: SurveyorRoom) => room.name !== roomName);
  const removedCount = originalRoomCount - targetFloor.rooms.length;
  
  if (removedCount > 0) {
    console.log(`🗑️ Removed room "${roomName}" from site notes on floor ${floorNumber} (${removedCount} room(s) removed)`);
  } else {
    console.warn(`Room "${roomName}" not found in site notes on floor ${floorNumber}`);
  }
  
  return updatedSiteNotesData;
};

/**
 * Syncs orphaned elements (elements that no longer belong to any room) by removing them from site notes
 * This handles cases where rooms are deleted from the floor plan but elements remain
 */
export const syncOrphanedElementsCleanup = (
  allElements: FloorPlanElement[],
  allRooms: Room[],
  siteNotesData: SiteNotesData,
  floorNumber: number
): SiteNotesData => {
  let updatedSiteNotesData = JSON.parse(JSON.stringify(siteNotesData));
  
  // Get elements for this floor
  const floorElements = allElements.filter(el => (el as any).floor === floorNumber);
  
  // 🧹 DEDUPLICATION FIX: Clean up duplicate radiators in site notes data
  const floorData = updatedSiteNotesData.floors?.find((f: any) => f.id === floorNumber);
  if (floorData && floorData.rooms) {
    floorData.rooms.forEach((room: any) => {
      if (room.radiators && room.radiators.length > 1) {
        // Count actual radiators in floor plan for this room
        const roomRadiators = floorElements.filter(el => 
          el.type === 'radiator' && 
          allRooms.some(r => r.name === room.name && 
            r.walls.some(w => (el as any).wallId === `wall_${w.start.x.toFixed(2)}_${w.start.y.toFixed(2)}_${w.end.x.toFixed(2)}_${w.end.y.toFixed(2)}`)
          )
        );
        
        if (roomRadiators.length < room.radiators.length) {
          // Remove excess radiators
          const excessCount = room.radiators.length - roomRadiators.length;
          room.radiators = room.radiators.slice(0, roomRadiators.length);
          console.log(`🧹 DEDUP CLEANUP: Removed ${excessCount} duplicate radiators from room "${room.name}"`);
        }
      }
    });
  }
  
  // Find elements that are no longer in any room
  const orphanedElements: { element: FloorPlanElement; type: string; previousRoom?: string }[] = [];
  
  floorElements.forEach(element => {
    const typedElement = element as any;
    
    // Skip highlight elements as they don't have a traditional position property
    // and don't belong to specific rooms in the same way other elements do
    if (element.type === 'highlight') {
      return;
    }
    
    // Defensive check: ensure element has a valid position
    if (!typedElement.position || typeof typedElement.position.x !== 'number' || typeof typedElement.position.y !== 'number') {
      console.warn('Element missing valid position property:', element.type, element);
      return;
    }
    
    const roomContainingElement = findRoomAtPoint(typedElement.position, allRooms);
    
    if (!roomContainingElement || !roomContainingElement.name || /^Room \d+$/.test(roomContainingElement.name)) {
      // Element is orphaned (no valid room found)
      orphanedElements.push({
        element,
        type: element.type,
        previousRoom: typedElement.roomName || 'Unknown'
      });
    }
  });
  
  if (orphanedElements.length === 0) {
    console.log(`🧹 No orphaned elements found on floor ${floorNumber}`);
    return updatedSiteNotesData;
  }
  
  // console.log(`🧹 Found ${orphanedElements.length} orphaned elements on floor ${floorNumber}:`, 
  //   orphanedElements.map(oe => `${oe.type} (was in: ${oe.previousRoom})`));
  
  // Clean up orphaned elements by type
  const orphanedByType = orphanedElements.reduce((acc, oe) => {
    if (!acc[oe.type]) acc[oe.type] = [];
    acc[oe.type].push(oe);
    return acc;
  }, {} as Record<string, typeof orphanedElements>);
  
  // Clean up each type of orphaned element
  Object.entries(orphanedByType).forEach(([elementType, orphanedOfType]) => {
    const previousRooms = Array.from(new Set(orphanedOfType.map(oe => oe.previousRoom).filter(Boolean)));
    
    switch (elementType) {
      case 'window':
        // Clear window data from previous rooms
        previousRooms.forEach(roomName => {
          if (roomName && roomName !== 'Unknown') {
            updatedSiteNotesData = updateRoomMultipleWindowDimensions(roomName!, [], updatedSiteNotesData, floorNumber);
            console.log(`🪟 Cleared orphaned windows from room "${roomName}"`);
          }
        });
        break;
        
      case 'radiator':
        // Clear radiator data from previous rooms  
        previousRooms.forEach(roomName => {
          if (roomName && roomName !== 'Unknown') {
            updatedSiteNotesData = updateRoomMultipleRadiatorDimensions(roomName!, [], updatedSiteNotesData, floorNumber);
            console.log(`🔥 Cleared orphaned radiators from room "${roomName}"`);
          }
        });
        break;
        
      case 'light':
        // Reset light counts for previous rooms (this is complex, so we'll just log for now)
        // console.log(`💡 Found ${orphanedOfType.length} orphaned lights from rooms: ${previousRooms.join(', ')}`);
        // console.log(`💡 Note: Light counts may need manual adjustment in site notes`);
        break;
        
      default:
        // console.log(`🧹 Found ${orphanedOfType.length} orphaned ${elementType}s from rooms: ${previousRooms.join(', ')}`);
    }
  });
  
  return updatedSiteNotesData;
};

/**
 * Comprehensive sync that handles both room removal and orphaned element cleanup
 * This should be called when rooms are deleted from the floor plan
 */
export const syncRoomDeletionAndCleanup = (
  deletedRoomNames: string[],
  remainingElements: FloorPlanElement[],
  remainingRooms: Room[],
  siteNotesData: SiteNotesData,
  floorNumber: number
): SiteNotesData => {
  let updatedSiteNotesData = JSON.parse(JSON.stringify(siteNotesData));
  
  // Step 1: Remove deleted rooms from site notes
  deletedRoomNames.forEach(roomName => {
    updatedSiteNotesData = removeRoomFromSiteNotes(roomName, floorNumber, updatedSiteNotesData);
  });
  
  // Step 2: Clean up orphaned elements
  updatedSiteNotesData = syncOrphanedElementsCleanup(remainingElements, remainingRooms, updatedSiteNotesData, floorNumber);
  
  // Step 3: Re-sync remaining elements to ensure proper room assignments
  const windowElements = remainingElements.filter(el => el.type === 'window') as unknown as WindowElement[];
  const radiatorElements = remainingElements.filter(el => el.type === 'radiator') as unknown as RadiatorElement[];
  const lightElements = remainingElements.filter(el => el.type === 'light') as unknown as LightElement[];
  
  if (windowElements.length > 0) {
    updatedSiteNotesData = smartSyncWindowsToRoom(windowElements, remainingRooms, updatedSiteNotesData, floorNumber, true);
  }
  
  if (radiatorElements.length > 0) {
    updatedSiteNotesData = smartSyncRadiatorsToRoom(radiatorElements, remainingRooms, updatedSiteNotesData, floorNumber, true);
  }
  
  if (lightElements.length > 0) {
    updatedSiteNotesData = smartSyncLightsToRooms(lightElements, remainingRooms, updatedSiteNotesData, floorNumber, true);
  }
  
  console.log(`🧹 Completed room deletion and cleanup sync for floor ${floorNumber}:`, {
    deletedRooms: deletedRoomNames.length,
    remainingRooms: remainingRooms.length,
    remainingElements: remainingElements.length
  });
  
  return updatedSiteNotesData;
};

/**
 * Calculates the door arch (swing) position based on door properties
 * The door arch represents where the door swings open, which should be inside the room the door belongs to
 */
const calculateDoorArchPosition = (door: { position: Point; width: number; swingRight: boolean; opensIn: boolean }, wall: { start: Point; end: Point }): Point => {
  // Calculate wall properties
  const wallVector = { x: wall.end.x - wall.start.x, y: wall.end.y - wall.start.y };
  const wallLength = Math.hypot(wallVector.x, wallVector.y);
  
  if (wallLength === 0) {
    return door.position; // Fallback to door position if wall has no length
  }
  
  const wallAngle = Math.atan2(wallVector.y, wallVector.x);
  const halfWidth = door.width / 2;
  const doorWidth = door.width;
  
  // Calculate door endpoints on the wall
  const wallDir = { x: wallVector.x / wallLength, y: wallVector.y / wallLength };
  const p1 = { x: door.position.x - halfWidth * wallDir.x, y: door.position.y - halfWidth * wallDir.y };
  const p2 = { x: door.position.x + halfWidth * wallDir.x, y: door.position.y + halfWidth * wallDir.y };
  
  // Determine hinge point based on swing direction
  const hinge = door.swingRight ? p2 : p1;
  
  // Calculate the normal angle (perpendicular to wall) based on door opening direction
  const normalAngle = wallAngle + (door.opensIn ? -Math.PI / 2 : Math.PI / 2);
  
  // Calculate door leaf end position (where the door arc ends when fully open)
  const doorLeafEnd = {
    x: hinge.x + doorWidth * Math.cos(normalAngle),
    y: hinge.y + doorWidth * Math.sin(normalAngle)
  };
  
  // Calculate a point about 60% along the door arc for better room detection
  // This point should be clearly inside the room the door opens into
  const archPosition = {
    x: hinge.x + (doorWidth * 0.6) * Math.cos(normalAngle),
    y: hinge.y + (doorWidth * 0.6) * Math.sin(normalAngle)
  };
  
  return archPosition;
};

/**
 * Intelligent door allocation based purely on door arch (swing) position
 * The door arch position determines which room the door belongs to
 */
export const findBestRoomForDoor = (doorPosition: { x: number; y: number }, rooms: Room[], doorElement?: any, walls?: any[]): Room | null => {
  // If we have door element and walls, use arch-based detection
  if (doorElement && walls && walls.length > 0) {
    // Find the wall this door is on
    const doorWall = walls.find(wall => (wall as any).stableId === doorElement.wallId);
    
    if (doorWall) {
      // Calculate door arch position
      const archPosition = calculateDoorArchPosition(doorElement, doorWall);
      
      // console.log(`🚪 DOOR ARCH ALLOCATION: Calculating room for door at (${doorPosition.x.toFixed(1)}, ${doorPosition.y.toFixed(1)}) using arch position (${archPosition.x.toFixed(1)}, ${archPosition.y.toFixed(1)})`);
      
      // Find which room contains the door arch
      const roomContainingArch = rooms.find(room => isPointInsideRoom(archPosition, room.walls));
      
      if (roomContainingArch) {
        // console.log(`🚪 ✅ ARCH-BASED ALLOCATION: Assigned door to "${roomContainingArch.name}" based on door arch position`);
        return roomContainingArch;
      }
      
      // If arch position is not inside any room, try a slightly closer position
      const closerArchPosition = {
        x: doorElement.position.x + (archPosition.x - doorElement.position.x) * 0.3,
        y: doorElement.position.y + (archPosition.y - doorElement.position.y) * 0.3
      };
      
      const roomContainingCloserArch = rooms.find(room => isPointInsideRoom(closerArchPosition, room.walls));
      
      if (roomContainingCloserArch) {
        // console.log(`🚪 ✅ ARCH-BASED ALLOCATION (closer): Assigned door to "${roomContainingCloserArch.name}" based on closer arch position`);
        return roomContainingCloserArch;
      }
      
      // console.log(`🚪 ⚠️ ARCH-BASED ALLOCATION: Could not find room for door arch - falling back to proximity`);
    }
  }
  
  // Fallback to the original method if arch-based detection fails
  // Helper function to identify hallways
  const isHallway = (room: Room): boolean => {
    const roomName = room.name?.toLowerCase() || '';
    return roomName.includes('hall') || 
           roomName.includes('hallway') || 
           roomName.includes('corridor') || 
           roomName.includes('landing') || 
           roomName.includes('passage') ||
           roomName.includes('entrance') ||
           roomName.includes('lobby') ||
           roomName.includes('foyer') ||
           /^h\d*$/i.test(roomName); // Matches H1, H2, etc.
  };

  // Strategy 1: Try to find rooms that contain the door position (strict containment)
  const candidateRooms = rooms.filter(room => isPointInsideRoom(doorPosition, room.walls));
  
  if (candidateRooms.length > 1) {
    // Multiple rooms contain the door - prefer non-hallway rooms
    const actualRooms = candidateRooms.filter(room => !isHallway(room));
    const hallways = candidateRooms.filter(room => isHallway(room));
    
    console.log(`🚪 DOOR ALLOCATION (fallback): Found ${candidateRooms.length} candidate rooms for door at (${doorPosition.x.toFixed(1)}, ${doorPosition.y.toFixed(1)}):`, {
      actualRooms: actualRooms.map(r => r.name),
      hallways: hallways.map(r => r.name)
    });
    
    // Prefer actual rooms over hallways
    if (actualRooms.length > 0) {
      console.log(`🚪 ✅ SMART ALLOCATION (fallback): Assigned door to "${actualRooms[0].name}" (preferred over hallway)`);
      return actualRooms[0];
    }
    
    // If only hallways, return the first one
    return hallways[0] || candidateRooms[0];
  }
  
  if (candidateRooms.length === 1) {
    return candidateRooms[0];
  }
  
  // Strategy 2: Use proximity-based detection with hallway preference
  const DOOR_PROXIMITY_THRESHOLD = 30; // pixels
  const nearbyRooms = rooms.filter(room => {
    const roomCenter = { x: room.center.x, y: room.center.y };
    const distance = Math.sqrt(
      Math.pow(doorPosition.x - roomCenter.x, 2) + 
      Math.pow(doorPosition.y - roomCenter.y, 2)
    );
    return distance <= DOOR_PROXIMITY_THRESHOLD * 3; // Larger threshold for proximity
  });
  
  if (nearbyRooms.length > 1) {
    // Multiple nearby rooms - prefer non-hallway rooms
    const actualRooms = nearbyRooms.filter(room => !isHallway(room));
    const hallways = nearbyRooms.filter(room => isHallway(room));
    
    console.log(`🚪 DOOR ALLOCATION (proximity fallback): Found ${nearbyRooms.length} nearby rooms for door at (${doorPosition.x.toFixed(1)}, ${doorPosition.y.toFixed(1)}):`, {
      actualRooms: actualRooms.map(r => r.name),
      hallways: hallways.map(r => r.name)
    });
    
    if (actualRooms.length > 0) {
      console.log(`🚪 ✅ SMART ALLOCATION (proximity fallback): Assigned door to "${actualRooms[0].name}" (preferred over hallway)`);
      return actualRooms[0];
    }
    
    return hallways[0] || nearbyRooms[0];
  }
  
  // Strategy 3: Fall back to standard findRoomAtPoint but apply hallway preference
  const standardRoom = findRoomAtPoint(doorPosition, rooms);
  if (standardRoom && isHallway(standardRoom)) {
    // The standard method found a hallway, let's see if there's a better option nearby
    const veryNearbyRooms = rooms.filter(room => {
      const roomCenter = { x: room.center.x, y: room.center.y };
      const distance = Math.sqrt(
        Math.pow(doorPosition.x - roomCenter.x, 2) + 
        Math.pow(doorPosition.y - roomCenter.y, 2)
      );
      return distance <= DOOR_PROXIMITY_THRESHOLD * 2; // Smaller threshold for fallback
    });
    
    const nearbyActualRooms = veryNearbyRooms.filter(room => !isHallway(room));
    if (nearbyActualRooms.length > 0) {
      console.log(`🚪 ✅ SMART ALLOCATION (final fallback): Reassigned door from hallway "${standardRoom.name}" to "${nearbyActualRooms[0].name}"`);
      return nearbyActualRooms[0];
    }
  }
  
  return standardRoom;
};

const createEmptyRoom = (): any => ({
  id: '',
  name: '',
  height: '',
  sideWindows: 0,
  frontWindows: 0,
  rearWindows: 0,
  portsideWindows: 0,
  starboardWindows: 0,
  aftWindows: 0,
  upWindows: 0,
  downWindows: 0,
  windows: [],
  doors: [],
  radiators: [],
  hasWindow: false,
  hasRadiator: false,
  hasFan: false,
  isDraughty: false,
  isLivingRoom: false,
  isKitchen: false,
  isBedroom: false,
  isBathroom: false,
  isDiningRoom: false,
  isHallway: false,
  isUtilityRoom: false,
  isOtherRoom: false,
  otherRoomType: '',
  photos: [],
  orientation: '',
  description: '',
  width: '',
  length: '',
  area: 0,
  windowType: '',
  windowFrameMaterial: '',
  windowGlazing: '',
  doorType: '',
  floorType: '',
  ceilingType: '',
  wallType: '',
  insulationType: '',
  ventilationType: '',
  heatSource: '',
  heatSourceType: '',
  notes: '',
  floorSurface: '',
  wallSurface: '',
  ceilingSurface: '',
  inventory: [],
  heatingPerformance: '',
  thermalComfort: '',
  airQuality: '',
  moisture: '',
  draught: '',
  noise: '',
  inventory_backup: [],
  isHeated: false,
  hasExtractFan: false,
  hasIntermittentExtractFan: false,
  hasContinuousExtractFan: false,
  hasPermanentVents: false,
  hasSecurityVents: false,
  hasWindowVents: false,
  hasDoorVents: false,
  hasFloorVents: false,
  hasCeilingVents: false,
  hasWallVents: false,
  hasOtherVents: false,
  hasBackgroundVentilation: false,
  hasWholeHouseVentilation: false,
  hasEnergyRecoveryVentilation: false,
  hasPassiveStack: false,
  hasHumidityControlledVentilation: false,
  hasWindowOpening: false,
  hasNaturalVentilation: false,
  hasAirConditioning: false,
  hasHeating: false,
  hasCooling: false,
  hasHumidityControl: false,
  hasAirPurification: false,
  hasEnergyRecovery: false,
  hasHeatRecovery: false,
  hasMoistureSensors: false,
  hasTemperatureSensors: false,
  hasAirQualitySensors: false,
  hasMovementSensors: false,
  hasLightSensors: false,
  hasSmokeSensors: false,
  hasCarbonMonoxideSensors: false,
  hasCarbonDioxideSensors: false,
  hasRoomThermostat: false,
  hasTrvs: false,
  hasStandaloneThermostat: false,
  hasSmartThermostat: false,
  hasVentilationControls: false,
  hasLightingControls: false,
  hasSecurityControls: false,
  hasEnergyMonitoring: false,
  hasSmartMeters: false,
  hasBuildingManagementSystem: false,
  hasRenewableEnergy: false,
  hasEnergyStorage: false,
  hasElectricVehicleCharging: false,
  hasSmartAppliances: false,
  hasSmartLighting: false,
  hasSmartHeating: false,
  hasSmartSecurity: false,
  hasSmartEntertainment: false,
  hasHomeAutomation: false,
  hasVoiceControl: false,
  hasMobileControl: false,
  hasRemoteMonitoring: false,
  hasEnergySaving: false,
  hasComfortOptimization: false,
  hasHealthMonitoring: false,
  hasSafetyMonitoring: false,
  hasMaintenancePrediction: false,
  hasLeakDetection: false,
  hasFireDetection: false,
  hasIntrusionDetection: false,
  hasEmergencyResponse: false,
});

const updateRoomWindowDimensionsLocal = (
  roomName: string,
  width: number,
  height: number,
  trickleVents: boolean,
  data: SiteNotesData,
  floorNumber: number = 1,
  orientation?: string
): SiteNotesData => {
  // Clone the site notes data to avoid mutations
  const updatedSiteNotesData = JSON.parse(JSON.stringify(data));

  // Find the target floor
  const targetFloor = updatedSiteNotesData.floors.find((f: SurveyorFloor) => f.id === floorNumber);
  if (!targetFloor) {
    console.warn(`Floor ${floorNumber} not found when updating window dimensions for room "${roomName}"`);
    return data;
  }

  // Find the room by name
  const targetRoom = targetFloor.rooms.find((room: SurveyorRoom) => room.name === roomName);
  if (!targetRoom) {
    console.warn(`Room "${roomName}" not found when updating window dimensions`);
    return data;
  }

  // Update window dimensions (input is in mm from floor plan, convert to meters for site notes storage)
  // 🛡️ PROTECTION: Preserve existing dimensions if new values are 0 or empty (prevent auto-sync overwrites)
  const newWidth = width > 0 ? (width / 1000).toFixed(2) : '';
  const newHeight = height > 0 ? (height / 1000).toFixed(2) : '';
  
  // Only update dimensions if we have valid new values OR if current values are empty
  if (newWidth || !targetRoom.windowWidth) {
    const oldWidth = targetRoom.windowWidth || 'empty';
    targetRoom.windowWidth = newWidth;
    console.log(`🛡️ DIMENSION PROTECTION: windowWidth updated to "${newWidth}" (was "${oldWidth}")`);
  } else {
    console.log(`🛡️ DIMENSION PROTECTION: Preserving existing windowWidth "${targetRoom.windowWidth}" (would overwrite with empty)`);
  }
  
  if (newHeight || !targetRoom.windowHeight) {
    const oldHeight = targetRoom.windowHeight || 'empty';
    targetRoom.windowHeight = newHeight;
    console.log(`🛡️ DIMENSION PROTECTION: windowHeight updated to "${newHeight}" (was "${oldHeight}")`);
  } else {
    console.log(`🛡️ DIMENSION PROTECTION: Preserving existing windowHeight "${targetRoom.windowHeight}" (would overwrite with empty)`);
  }
  
  targetRoom.windowTrickleVents = trickleVents ? 'Yes' : 'No';
  
  // Update window orientation if provided (map to site notes format)
  if (orientation && orientation !== 'Unknown' && orientation !== '') {
    (targetRoom as any).windowOrientation = mapFloorPlanOrientationToSiteNotes(orientation);
  }
  
  // Clear windows array when using single window fields to prevent duplicates
  targetRoom.windows = [];

  // console.log(`🪟 Window dimensions updated for room "${roomName}": ${width}mm → ${(width / 1000).toFixed(2)}m × ${height}mm → ${(height / 1000).toFixed(2)}m, Trickle vents: ${trickleVents ? 'Yes' : 'No'}${orientation ? `, Orientation: ${orientation}` : ''}`);

  return updatedSiteNotesData;
};